import { cva } from 'class-variance-authority';
import * as React44 from 'react';
import { createContext, forwardRef, useId, useRef, useCallback, useState, useEffect, useImperativeHandle, useMemo, useContext, useSyncExternalStore } from 'react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { Slot } from '@radix-ui/react-slot';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Minus, Check, Circle, ChevronDown, ChevronUp, ChevronsUpDown, Search, MoreHorizontal, ChevronsRight, ChevronRight, ChevronLeft, ChevronsLeft, X, Menu, Upload, Clock, Image, Film, Music, FileText, User, Settings, LogOut, Copy, Plus } from 'lucide-react';
import * as LabelPrimitive from '@radix-ui/react-label';
import { toCanvas } from 'qrcode';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import * as SwitchPrimitives from '@radix-ui/react-switch';
import * as TogglePrimitive from '@radix-ui/react-toggle';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import * as ProgressPrimitive from '@radix-ui/react-progress';
import * as SliderPrimitive from '@radix-ui/react-slider';
import * as SelectPrimitive from '@radix-ui/react-select';
import * as PopoverPrimitive2 from '@radix-ui/react-popover';
import { Command } from 'cmdk';
import * as ContextMenuPrimitive from '@radix-ui/react-context-menu';
import { useDroppable, useDraggable, useSensors, useSensor, PointerSensor, KeyboardSensor, DndContext } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import * as ToastPrimitives from '@radix-ui/react-toast';
import * as DropdownMenuPrimitive2 from '@radix-ui/react-dropdown-menu';
import { useDropzone } from 'react-dropzone';
import Hls, { Events } from 'hls.js';

// src/components/atoms/Badge/Badge.tsx
function cn(...inputs) {
  return twMerge(clsx(inputs));
}
var badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        secondary: "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive: "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "text-foreground"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
var Badge = React44.forwardRef(
  ({ className, variant, ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        className: cn(badgeVariants({ variant }), className),
        ...props
      }
    );
  }
);
Badge.displayName = "Badge";

// src/lib/styles.ts
var focusRingStyles = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background";
var focusAlwaysRingStyles = "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ring-offset-background";
var focusRingCompactStyles = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 ring-offset-background";
var focusAlwaysRingCompactStyles = "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 ring-offset-background";
var disabledStyles = "disabled:pointer-events-none disabled:opacity-50";
var disabledWithCursorStyles = "disabled:cursor-not-allowed disabled:opacity-50";
var disabledInputStyles = "disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-muted";
var peerDisabledStyles = "peer-disabled:cursor-not-allowed peer-disabled:opacity-70";
var errorBorderStyles = "border-destructive focus-visible:ring-destructive";
var errorStyles = "border-destructive focus-visible:ring-destructive text-destructive";
var errorContainerStyles = "rounded-md border border-destructive/50 bg-destructive/10";
var errorAlwaysStyles = "border-destructive focus:border-destructive focus:ring-destructive";
var successStyles = "border-green-500 focus-visible:ring-green-500";
var interactiveStyles = "transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background active:scale-[0.98]";
var interactiveBaseStyles = "transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background";
var interactiveMutedStyles = "transition-colors hover:bg-muted hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background";
var formInputBaseStyles = "flex w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";
var fileInputStyles = "file:border-0 file:bg-transparent file:text-sm file:font-medium";
var buttonBaseStyles = "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50";
var toggleBaseStyles = "inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors hover:bg-muted hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50";
var inputStateVariants = {
  default: "border-input focus-visible:ring-ring",
  error: "border-destructive focus-visible:ring-destructive text-destructive",
  success: "border-green-500 focus-visible:ring-green-500"
};
var spinnerVariants = cva(
  "inline-block animate-spin rounded-full border-solid border-current border-r-transparent motion-reduce:animate-[spin_1.5s_linear_infinite]",
  {
    variants: {
      size: {
        sm: "h-4 w-4 border-2",
        default: "h-6 w-6 border-2",
        lg: "h-8 w-8 border-[3px]"
      }
    },
    defaultVariants: {
      size: "default"
    }
  }
);
var Spinner = React44.forwardRef(
  ({ className, size, label = "Loading", ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        role: "status",
        "aria-label": label,
        className: cn(spinnerVariants({ size, className })),
        ...props,
        children: /* @__PURE__ */ jsx("span", { className: "sr-only", children: label })
      }
    );
  }
);
Spinner.displayName = "Spinner";
var buttonVariants = cva(
  buttonBaseStyles,
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline"
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);
var Button = React44.forwardRef(
  ({ className, variant, size, asChild = false, loading = false, disabled, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    if (asChild) {
      return /* @__PURE__ */ jsx(
        Comp,
        {
          className: cn(buttonVariants({ variant, size, className })),
          ref,
          "aria-busy": loading,
          "aria-disabled": disabled || loading,
          ...props,
          children
        }
      );
    }
    return /* @__PURE__ */ jsx(
      Comp,
      {
        className: cn(buttonVariants({ variant, size, className })),
        ref,
        disabled: disabled || loading,
        "aria-busy": loading,
        "aria-disabled": disabled || loading,
        ...props,
        children: loading ? /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(
            Spinner,
            {
              size: "sm",
              className: "mr-2",
              "aria-hidden": "true"
            }
          ),
          children
        ] }) : children
      }
    );
  }
);
Button.displayName = "Button";
var Checkbox = React44.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  CheckboxPrimitive.Root,
  {
    ref,
    className: cn(
      "peer h-4 w-4 shrink-0 rounded-sm border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground",
      className
    ),
    ...props,
    children: /* @__PURE__ */ jsx(
      CheckboxPrimitive.Indicator,
      {
        className: cn("flex items-center justify-center text-current"),
        children: props.checked === "indeterminate" ? /* @__PURE__ */ jsx(Minus, { className: "h-3 w-3" }) : /* @__PURE__ */ jsx(Check, { className: "h-3 w-3" })
      }
    )
  }
));
Checkbox.displayName = CheckboxPrimitive.Root.displayName;
var inputVariants = cva(
  cn(
    "flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground",
    "placeholder:text-muted-foreground",
    focusRingStyles,
    fileInputStyles,
    disabledWithCursorStyles
  ),
  {
    variants: {
      state: inputStateVariants
    },
    defaultVariants: {
      state: "default"
    }
  }
);
var Input = React44.forwardRef(
  ({ className, type = "text", state, error, success, id, ...props }, ref) => {
    const derivedState = error ? "error" : success ? "success" : state;
    const generatedId = React44.useId();
    const inputId = id || `input-${generatedId}`;
    const errorId = error ? `${inputId}-error` : void 0;
    const successId = success ? `${inputId}-success` : void 0;
    const describedBy = errorId || successId;
    return /* @__PURE__ */ jsxs("div", { className: "w-full", children: [
      /* @__PURE__ */ jsx(
        "input",
        {
          type,
          id: inputId,
          className: cn(inputVariants({ state: derivedState, className })),
          ref,
          "aria-invalid": !!error,
          "aria-describedby": describedBy,
          ...props
        }
      ),
      error && /* @__PURE__ */ jsx(
        "p",
        {
          id: errorId,
          className: "mt-1.5 text-sm text-destructive",
          role: "alert",
          children: error
        }
      ),
      success && !error && /* @__PURE__ */ jsx(
        "p",
        {
          id: successId,
          className: "mt-1.5 text-sm text-green-600",
          children: success
        }
      )
    ] });
  }
);
Input.displayName = "Input";
var labelVariants = cva(
  "text-sm font-medium leading-none text-foreground peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
  {
    variants: {
      disabled: {
        true: "cursor-not-allowed opacity-70",
        false: ""
      }
    },
    defaultVariants: {
      disabled: false
    }
  }
);
var Label = React44.forwardRef(({ className, required, disabled, children, ...props }, ref) => /* @__PURE__ */ jsxs(
  LabelPrimitive.Root,
  {
    ref,
    className: cn(labelVariants({ disabled, className })),
    ...props,
    children: [
      children,
      required && /* @__PURE__ */ jsx("span", { className: "ml-1 text-destructive", "aria-hidden": "true", children: "*" })
    ]
  }
));
Label.displayName = "Label";
function QRCode({
  value,
  size = 200,
  errorCorrectionLevel = "M",
  className = ""
}) {
  const canvasRef = useRef(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!canvasRef.current || !value) {
      return;
    }
    const generateQRCode = async () => {
      try {
        await toCanvas(canvasRef.current, value, {
          width: size,
          errorCorrectionLevel,
          margin: 1
        });
        setError(null);
      } catch (err) {
        setError("Failed to generate QR code");
        console.error("QR Code generation error:", err);
      }
    };
    generateQRCode();
  }, [value, size, errorCorrectionLevel]);
  if (error) {
    return /* @__PURE__ */ jsx(
      "div",
      {
        className: `flex items-center justify-center bg-red-50 border border-red-200 rounded-lg ${className}`.trim(),
        style: { width: size, height: size },
        role: "alert",
        children: /* @__PURE__ */ jsx("p", { className: "text-sm text-red-600 px-4 text-center", children: error })
      }
    );
  }
  return /* @__PURE__ */ jsx(
    "canvas",
    {
      ref: canvasRef,
      className: `rounded-lg ${className}`.trim(),
      "aria-label": "QR Code",
      role: "img"
    }
  );
}
var RadioGroup = React44.forwardRef(({ className, orientation = "vertical", ...props }, ref) => {
  return /* @__PURE__ */ jsx(
    RadioGroupPrimitive.Root,
    {
      className: cn(
        "grid gap-2",
        orientation === "horizontal" && "flex flex-row space-x-4",
        className
      ),
      orientation,
      ...props,
      ref
    }
  );
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;
var RadioGroupItem = React44.forwardRef(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx(
    RadioGroupPrimitive.Item,
    {
      ref,
      className: cn(
        "aspect-square h-4 w-4 rounded-full border border-primary text-primary ring-offset-background focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx(RadioGroupPrimitive.Indicator, { className: "flex items-center justify-center", children: /* @__PURE__ */ jsx(Circle, { className: "h-2.5 w-2.5 fill-current text-current" }) })
    }
  );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;
var switchVariants = cva(
  "peer inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input",
  {
    variants: {
      size: {
        sm: "h-4 w-7",
        default: "h-6 w-11",
        lg: "h-8 w-14"
      }
    },
    defaultVariants: {
      size: "default"
    }
  }
);
var switchThumbVariants = cva(
  "pointer-events-none block rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=unchecked]:translate-x-0",
  {
    variants: {
      size: {
        sm: "h-3 w-3 data-[state=checked]:translate-x-3",
        default: "h-5 w-5 data-[state=checked]:translate-x-5",
        lg: "h-7 w-7 data-[state=checked]:translate-x-6"
      }
    },
    defaultVariants: {
      size: "default"
    }
  }
);
var Switch = React44.forwardRef(({ className, size, ...props }, ref) => /* @__PURE__ */ jsx(
  SwitchPrimitives.Root,
  {
    className: cn(switchVariants({ size, className })),
    ...props,
    ref,
    children: /* @__PURE__ */ jsx(SwitchPrimitives.Thumb, { className: cn(switchThumbVariants({ size })) })
  }
));
Switch.displayName = SwitchPrimitives.Root.displayName;
var Skeleton = React44.forwardRef(
  ({
    className,
    animation = "pulse",
    width,
    height,
    circle = false,
    style,
    ...props
  }, ref) => {
    const animationClasses = {
      pulse: "animate-pulse",
      shimmer: "animate-shimmer bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_100%]",
      none: ""
    };
    const computedStyle = {
      ...style,
      ...width !== void 0 && { width: typeof width === "number" ? `${width}px` : width },
      ...height !== void 0 && { height: typeof height === "number" ? `${height}px` : height }
    };
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        className: cn(
          "bg-gray-200",
          circle ? "rounded-full" : "rounded-md",
          animation !== "shimmer" && animationClasses[animation],
          animation === "shimmer" && animationClasses.shimmer,
          className
        ),
        style: computedStyle,
        "aria-hidden": "true",
        "data-testid": "skeleton",
        ...props
      }
    );
  }
);
Skeleton.displayName = "Skeleton";
var SkeletonText = React44.forwardRef(
  ({
    className,
    lines = 1,
    gap = "0.5rem",
    lastLineShort = true,
    animation = "pulse",
    height = "1rem",
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        className: cn("flex flex-col", className),
        style: { gap },
        role: "status",
        "aria-label": "Loading content",
        ...props,
        children: Array.from({ length: lines }).map((_, index) => /* @__PURE__ */ jsx(
          Skeleton,
          {
            animation,
            height,
            width: lastLineShort && index === lines - 1 && lines > 1 ? "75%" : "100%"
          },
          index
        ))
      }
    );
  }
);
SkeletonText.displayName = "SkeletonText";
var SkeletonCircle = React44.forwardRef(
  ({ size = 40, ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      Skeleton,
      {
        ref,
        circle: true,
        width: size,
        height: size,
        ...props
      }
    );
  }
);
SkeletonCircle.displayName = "SkeletonCircle";
var toggleVariants = cva(
  "inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors hover:bg-muted hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=on]:bg-accent data-[state=on]:text-accent-foreground",
  {
    variants: {
      variant: {
        default: "bg-transparent",
        outline: "border border-input bg-transparent hover:bg-accent hover:text-accent-foreground"
      },
      size: {
        sm: "h-9 px-2.5",
        default: "h-10 px-3",
        lg: "h-11 px-5"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);
var Toggle = React44.forwardRef(({ className, variant, size, ...props }, ref) => /* @__PURE__ */ jsx(
  TogglePrimitive.Root,
  {
    ref,
    className: cn(toggleVariants({ variant, size, className })),
    ...props
  }
));
Toggle.displayName = TogglePrimitive.Root.displayName;
var TooltipProvider = TooltipPrimitive.Provider;
var Tooltip = TooltipPrimitive.Root;
var TooltipTrigger = TooltipPrimitive.Trigger;
var TooltipContent = React44.forwardRef(({ className, sideOffset = 4, ...props }, ref) => /* @__PURE__ */ jsx(TooltipPrimitive.Portal, { children: /* @__PURE__ */ jsx(
  TooltipPrimitive.Content,
  {
    ref,
    sideOffset,
    className: cn(
      "z-50 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md",
      "animate-in fade-in-0 zoom-in-95",
      "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
      "data-[side=bottom]:slide-in-from-top-2",
      "data-[side=left]:slide-in-from-right-2",
      "data-[side=right]:slide-in-from-left-2",
      "data-[side=top]:slide-in-from-bottom-2",
      className
    ),
    ...props
  }
) }));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;
var ThemeContext = createContext(
  void 0
);
ThemeContext.displayName = "ThemeContext";
function ThemeProvider({
  children,
  defaultTheme: _defaultTheme = "light",
  storageKey: _storageKey = "yi-ui-theme",
  attribute = "class"
}) {
  const [_theme, setThemeState] = useState("light");
  const [_systemTheme, _setSystemTheme] = useState("light");
  const resolvedTheme = "light";
  const applyTheme = useCallback(
    (_newTheme) => {
      const root = document.documentElement;
      if (attribute === "class") {
        root.classList.remove("dark");
        root.classList.add("light");
      } else {
        root.setAttribute("data-theme", "light");
      }
    },
    [attribute]
  );
  const setTheme = useCallback(
    (_newTheme) => {
      setThemeState("light");
    },
    []
  );
  useEffect(() => {
    return;
  }, []);
  useEffect(() => {
    if (typeof window !== "undefined") {
      applyTheme(resolvedTheme);
    }
  }, [resolvedTheme, applyTheme]);
  const contextValue = useMemo(
    () => ({
      theme: "light",
      resolvedTheme,
      setTheme,
      systemTheme: "light"
    }),
    [resolvedTheme, setTheme]
  );
  return /* @__PURE__ */ jsx(ThemeContext.Provider, { value: contextValue, children });
}

// src/hooks/useTheme.ts
function useTheme() {
  const context = useContext(ThemeContext);
  if (context === void 0) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
function SunIcon({ className }) {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      className,
      "aria-hidden": "true",
      children: [
        /* @__PURE__ */ jsx("circle", { cx: "12", cy: "12", r: "4" }),
        /* @__PURE__ */ jsx("path", { d: "M12 2v2" }),
        /* @__PURE__ */ jsx("path", { d: "M12 20v2" }),
        /* @__PURE__ */ jsx("path", { d: "m4.93 4.93 1.41 1.41" }),
        /* @__PURE__ */ jsx("path", { d: "m17.66 17.66 1.41 1.41" }),
        /* @__PURE__ */ jsx("path", { d: "M2 12h2" }),
        /* @__PURE__ */ jsx("path", { d: "M20 12h2" }),
        /* @__PURE__ */ jsx("path", { d: "m6.34 17.66-1.41 1.41" }),
        /* @__PURE__ */ jsx("path", { d: "m19.07 4.93-1.41 1.41" })
      ]
    }
  );
}
function MoonIcon({ className }) {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      className,
      "aria-hidden": "true",
      children: /* @__PURE__ */ jsx("path", { d: "M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" })
    }
  );
}
function MonitorIcon({ className }) {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      className,
      "aria-hidden": "true",
      children: [
        /* @__PURE__ */ jsx("rect", { width: "20", height: "14", x: "2", y: "3", rx: "2" }),
        /* @__PURE__ */ jsx("line", { x1: "8", x2: "16", y1: "21", y2: "21" }),
        /* @__PURE__ */ jsx("line", { x1: "12", x2: "12", y1: "17", y2: "21" })
      ]
    }
  );
}
var themeToggleVariants = cva(
  "inline-flex items-center justify-center rounded-md font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-transparent hover:bg-accent hover:text-accent-foreground",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        ghost: "hover:bg-accent hover:text-accent-foreground"
      },
      size: {
        default: "h-10 w-10",
        sm: "h-8 w-8",
        lg: "h-12 w-12"
      }
    },
    defaultVariants: {
      variant: "ghost",
      size: "default"
    }
  }
);
var DEFAULT_LABELS = {
  light: "Light mode",
  dark: "Dark mode",
  system: "System preference"
};
var THEME_CYCLE = ["light", "dark", "system"];
function getNextTheme(current) {
  const currentIndex = THEME_CYCLE.indexOf(current);
  const nextIndex = (currentIndex + 1) % THEME_CYCLE.length;
  return THEME_CYCLE[nextIndex];
}
function getThemeIcon(theme, className) {
  switch (theme) {
    case "light":
      return /* @__PURE__ */ jsx(SunIcon, { className });
    case "dark":
      return /* @__PURE__ */ jsx(MoonIcon, { className });
    case "system":
      return /* @__PURE__ */ jsx(MonitorIcon, { className });
  }
}
var ThemeToggle = React44.forwardRef(
  ({
    className,
    variant,
    size,
    labels = DEFAULT_LABELS,
    showTitle = true,
    onClick,
    ...props
  }, ref) => {
    const { theme, setTheme } = useTheme();
    const toggleTheme = React44.useCallback(() => {
      const nextTheme = getNextTheme(theme);
      setTheme(nextTheme);
    }, [theme, setTheme]);
    const currentThemeIcon = React44.useMemo(
      () => getThemeIcon(theme, cn("h-5 w-5")),
      [theme]
    );
    const currentThemeLabel = labels[theme] || DEFAULT_LABELS[theme];
    return /* @__PURE__ */ jsxs(
      "button",
      {
        ref,
        type: "button",
        className: cn(themeToggleVariants({ variant, size }), className),
        onClick: (event) => {
          toggleTheme();
          onClick?.(event);
        },
        title: showTitle ? currentThemeLabel : void 0,
        "aria-label": currentThemeLabel,
        ...props,
        children: [
          currentThemeIcon,
          /* @__PURE__ */ jsx("span", { className: "sr-only", children: currentThemeLabel })
        ]
      }
    );
  }
);
ThemeToggle.displayName = "ThemeToggle";
var progressVariants = cva(
  "relative overflow-hidden rounded-full bg-secondary",
  {
    variants: {
      variant: {
        default: "bg-secondary",
        success: "bg-secondary",
        warning: "bg-secondary",
        error: "bg-secondary"
      },
      size: {
        sm: "h-1",
        md: "h-2",
        lg: "h-4"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "md"
    }
  }
);
var indicatorVariants = cva(
  "h-full w-full flex-1 transition-all",
  {
    variants: {
      variant: {
        default: "bg-primary",
        success: "bg-green-500",
        warning: "bg-yellow-500",
        error: "bg-red-500"
      },
      indeterminate: {
        true: "animate-progress-indeterminate origin-left",
        false: ""
      }
    },
    defaultVariants: {
      variant: "default",
      indeterminate: false
    }
  }
);
var Progress = React44.forwardRef(({ className, value, max = 100, variant, size, indeterminate, indicatorClassName, ...props }, ref) => /* @__PURE__ */ jsx(
  ProgressPrimitive.Root,
  {
    ref,
    className: cn(progressVariants({ variant, size, className })),
    value: indeterminate ? null : value,
    max,
    ...props,
    children: /* @__PURE__ */ jsx(
      ProgressPrimitive.Indicator,
      {
        className: cn(indicatorVariants({ variant, indeterminate, className: indicatorClassName })),
        style: { transform: indeterminate ? void 0 : `translateX(-${100 - Math.min(100, Math.max(0, (value || 0) / max * 100))}%)` }
      }
    )
  }
));
Progress.displayName = ProgressPrimitive.Root.displayName;
var Slider = React44.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsxs(
  SliderPrimitive.Root,
  {
    ref,
    className: cn(
      "relative flex w-full touch-none select-none items-center",
      className
    ),
    ...props,
    children: [
      /* @__PURE__ */ jsx(SliderPrimitive.Track, { className: "relative h-2 w-full grow overflow-hidden rounded-full bg-secondary", children: /* @__PURE__ */ jsx(SliderPrimitive.Range, { className: "absolute h-full bg-primary" }) }),
      props.value?.map((_, i) => /* @__PURE__ */ jsx(
        SliderPrimitive.Thumb,
        {
          className: "block h-5 w-5 rounded-full border-2 border-primary bg-background ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
        },
        i
      )) || /* @__PURE__ */ jsx(SliderPrimitive.Thumb, { className: "block h-5 w-5 rounded-full border-2 border-primary bg-background ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50" })
    ]
  }
));
Slider.displayName = SliderPrimitive.Root.displayName;
function useDebounce(value, delay = 300) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [value, delay]);
  return debouncedValue;
}
function useClickOutside(ref, callback, enabled = true) {
  useEffect(() => {
    if (!enabled) {
      return;
    }
    const handleClickOutside = (event) => {
      const refs = Array.isArray(ref) ? ref : [ref];
      const target = event.target;
      const isOutside = refs.every((r) => {
        return r.current === null || !r.current.contains(target);
      });
      if (isOutside) {
        callback(event);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [ref, callback, enabled]);
}
function useCopyToClipboard(resetTimeout = 2e3) {
  const [state, setState] = useState({
    copiedText: null,
    error: null
  });
  const reset = useCallback(() => {
    setState({ copiedText: null, error: null });
  }, []);
  const copy = useCallback(
    async (text) => {
      if (!navigator?.clipboard) {
        const error = new Error(
          "Clipboard API is not available. This may be due to an insecure context (non-HTTPS)."
        );
        setState({ copiedText: null, error });
        console.error("useCopyToClipboard:", error.message);
        return false;
      }
      try {
        await navigator.clipboard.writeText(text);
        setState({ copiedText: text, error: null });
        if (resetTimeout > 0) {
          setTimeout(() => {
            setState((prev) => {
              if (prev.copiedText === text) {
                return { copiedText: null, error: null };
              }
              return prev;
            });
          }, resetTimeout);
        }
        return true;
      } catch (err) {
        const error = err instanceof Error ? err : new Error("Failed to copy to clipboard");
        setState({ copiedText: null, error });
        console.error("useCopyToClipboard:", error.message);
        return false;
      }
    },
    [resetTimeout]
  );
  return [state.copiedText, copy, reset, state.error];
}
function parseShortcut(shortcut) {
  const parts = shortcut.toLowerCase().split("+");
  const modifiers = /* @__PURE__ */ new Set();
  let key = "";
  for (const part of parts) {
    const trimmed = part.trim();
    if (trimmed === "cmd" || trimmed === "meta") {
      modifiers.add("meta");
    } else if (trimmed === "ctrl" || trimmed === "control") {
      modifiers.add("ctrl");
    } else if (trimmed === "alt" || trimmed === "option") {
      modifiers.add("alt");
    } else if (trimmed === "shift") {
      modifiers.add("shift");
    } else {
      key = trimmed;
    }
  }
  return { modifiers, key };
}
function normalizeKey(key) {
  const keyLower = key.toLowerCase();
  const keyMap = {
    " ": "space",
    spacebar: "space",
    escape: "esc",
    arrowup: "up",
    arrowdown: "down",
    arrowleft: "left",
    arrowright: "right",
    enter: "enter",
    return: "enter",
    backspace: "backspace",
    delete: "delete",
    tab: "tab"
  };
  return keyMap[keyLower] || keyLower;
}
function useKeyboardShortcut(shortcut, callback, options = {}) {
  const {
    preventDefault = true,
    stopPropagation = false,
    enabled = true,
    targetElement = null,
    eventType = "keydown"
  } = options;
  const parsedShortcut = useMemo(() => parseShortcut(shortcut), [shortcut]);
  const handleKeyEvent = useCallback(
    (event) => {
      if (!enabled) {
        return;
      }
      const { modifiers, key } = parsedShortcut;
      const pressedKey = normalizeKey(event.key);
      if (pressedKey !== key) {
        return;
      }
      const ctrlPressed = event.ctrlKey;
      const altPressed = event.altKey;
      const shiftPressed = event.shiftKey;
      const metaPressed = event.metaKey;
      const ctrlRequired = modifiers.has("ctrl");
      const altRequired = modifiers.has("alt");
      const shiftRequired = modifiers.has("shift");
      const metaRequired = modifiers.has("meta");
      if (ctrlPressed !== ctrlRequired || altPressed !== altRequired || shiftPressed !== shiftRequired || metaPressed !== metaRequired) {
        return;
      }
      if (preventDefault) {
        event.preventDefault();
      }
      if (stopPropagation) {
        event.stopPropagation();
      }
      callback(event);
    },
    [parsedShortcut, enabled, preventDefault, stopPropagation, callback]
  );
  useEffect(() => {
    if (!enabled) {
      return;
    }
    const target = targetElement || document;
    target.addEventListener(eventType, handleKeyEvent);
    return () => {
      target.removeEventListener(eventType, handleKeyEvent);
    };
  }, [enabled, targetElement, eventType, handleKeyEvent]);
}
function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === "undefined") {
      return void 0;
    }
    return window.matchMedia(query).matches;
  });
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const mediaQuery = window.matchMedia(query);
    setMatches(mediaQuery.matches);
    const handleChange = (event) => {
      setMatches(event.matches);
    };
    mediaQuery.addEventListener("change", handleChange);
    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, [query]);
  return matches;
}
function subscribeToStorage(callback) {
  window.addEventListener("storage", callback);
  return () => window.removeEventListener("storage", callback);
}
function useLocalStorage(key, initialValue) {
  const initialValueRef = useRef(initialValue);
  const getSnapshot = useCallback(() => {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(key);
  }, [key]);
  const getServerSnapshot = useCallback(() => null, []);
  const storedString = useSyncExternalStore(
    subscribeToStorage,
    getSnapshot,
    getServerSnapshot
  );
  const storedValue = useCallback(() => {
    if (storedString === null) {
      return initialValueRef.current;
    }
    try {
      return JSON.parse(storedString);
    } catch {
      return initialValueRef.current;
    }
  }, [storedString]);
  const value = storedValue();
  const [, forceUpdate] = useState(0);
  const setValue = useCallback(
    (newValue) => {
      if (typeof window === "undefined") {
        console.warn(`Tried to set localStorage key "${key}" during SSR.`);
        return;
      }
      try {
        const currentValue = (() => {
          const item = localStorage.getItem(key);
          if (item === null) return initialValueRef.current;
          try {
            return JSON.parse(item);
          } catch {
            return initialValueRef.current;
          }
        })();
        const valueToStore = newValue instanceof Function ? newValue(currentValue) : newValue;
        localStorage.setItem(key, JSON.stringify(valueToStore));
        forceUpdate((n) => n + 1);
      } catch (error) {
        console.warn(`Error setting localStorage key "${key}":`, error);
      }
    },
    [key]
  );
  const removeValue = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }
    try {
      localStorage.removeItem(key);
      forceUpdate((n) => n + 1);
    } catch (error) {
      console.warn(`Error removing localStorage key "${key}":`, error);
    }
  }, [key]);
  return [value, setValue, removeValue];
}

// src/tokens/colors.ts
var semanticColors = {
  background: "hsl(var(--background))",
  foreground: "hsl(var(--foreground))",
  card: {
    DEFAULT: "hsl(var(--card))",
    foreground: "hsl(var(--card-foreground))"
  },
  popover: {
    DEFAULT: "hsl(var(--popover))",
    foreground: "hsl(var(--popover-foreground))"
  },
  primary: {
    DEFAULT: "hsl(var(--primary))",
    foreground: "hsl(var(--primary-foreground))"
  },
  secondary: {
    DEFAULT: "hsl(var(--secondary))",
    foreground: "hsl(var(--secondary-foreground))"
  },
  muted: {
    DEFAULT: "hsl(var(--muted))",
    foreground: "hsl(var(--muted-foreground))"
  },
  accent: {
    DEFAULT: "hsl(var(--accent))",
    foreground: "hsl(var(--accent-foreground))"
  },
  destructive: {
    DEFAULT: "hsl(var(--destructive))",
    foreground: "hsl(var(--destructive-foreground))"
  },
  border: "hsl(var(--border))",
  input: "hsl(var(--input))",
  ring: "hsl(var(--ring))"
};
var yakBrandColors = {
  /** Yak Network teal - primary brand color */
  teal: {
    50: "#E6F4F6",
    100: "#CCE9ED",
    200: "#99D3DB",
    300: "#66BDC9",
    400: "#33A7B7",
    500: "#3B7C8C",
    // Primary teal
    600: "#2F6370",
    700: "#234A54",
    800: "#183138",
    900: "#0C181C"
  },
  /** Yak Network orange - accent brand color */
  orange: {
    50: "#FFF7ED",
    100: "#FFEDD5",
    200: "#FED7AA",
    300: "#FDBA74",
    400: "#FB923C",
    500: "#F97316",
    // Primary orange
    600: "#EA580C",
    700: "#C2410C",
    800: "#9A3412",
    900: "#7C2D12"
  }
};
var lightThemeColors = {
  background: "0 0% 100%",
  foreground: "220 13% 26%",
  card: "0 0% 100%",
  cardForeground: "220 13% 26%",
  popover: "0 0% 100%",
  popoverForeground: "220 13% 26%",
  // Primary: Yak Teal
  primary: "192 41% 39%",
  primaryForeground: "0 0% 100%",
  // Secondary: Light gray
  secondary: "210 20% 96%",
  secondaryForeground: "220 13% 26%",
  // Muted: Slate gray
  muted: "210 20% 96%",
  mutedForeground: "215 16% 47%",
  // Accent: Yak Orange
  accent: "25 95% 53%",
  accentForeground: "0 0% 100%",
  destructive: "0 84% 60%",
  destructiveForeground: "0 0% 100%",
  border: "214 32% 91%",
  input: "214 32% 91%",
  ring: "192 41% 39%",
  // Yak brand specific
  yakTeal: "192 41% 39%",
  yakTealLight: "192 35% 50%",
  yakTealDark: "192 45% 30%",
  yakOrange: "25 95% 53%",
  yakOrangeLight: "25 95% 63%",
  yakOrangeDark: "25 95% 43%"
};

// src/tokens/spacing.ts
var spacing = {
  0: "0px",
  px: "1px",
  0.5: "0.125rem",
  1: "0.25rem",
  1.5: "0.375rem",
  2: "0.5rem",
  2.5: "0.625rem",
  3: "0.75rem",
  3.5: "0.875rem",
  4: "1rem",
  5: "1.25rem",
  6: "1.5rem",
  7: "1.75rem",
  8: "2rem",
  9: "2.25rem",
  10: "2.5rem",
  11: "2.75rem",
  12: "3rem",
  14: "3.5rem",
  16: "4rem",
  20: "5rem",
  24: "6rem",
  28: "7rem",
  32: "8rem",
  36: "9rem",
  40: "10rem",
  44: "11rem",
  48: "12rem",
  52: "13rem",
  56: "14rem",
  60: "15rem",
  64: "16rem",
  72: "18rem",
  80: "20rem",
  96: "24rem"
};
var spacingPresets = {
  /** No spacing */
  none: spacing[0],
  /** Extra small: 4px */
  xs: spacing[1],
  /** Small: 8px */
  sm: spacing[2],
  /** Medium: 16px */
  md: spacing[4],
  /** Large: 24px */
  lg: spacing[6],
  /** Extra large: 32px */
  xl: spacing[8],
  /** 2x Extra large: 48px */
  "2xl": spacing[12],
  /** 3x Extra large: 64px */
  "3xl": spacing[16]
};

// src/tokens/radius.ts
var radius = {
  none: "0",
  sm: "calc(var(--radius) - 4px)",
  md: "calc(var(--radius) - 2px)",
  DEFAULT: "var(--radius)",
  lg: "var(--radius)",
  xl: "calc(var(--radius) + 4px)",
  "2xl": "calc(var(--radius) + 8px)",
  "3xl": "calc(var(--radius) + 12px)",
  full: "9999px"
};
var radiusPixels = {
  none: "0px",
  sm: "4px",
  md: "6px",
  DEFAULT: "8px",
  lg: "8px",
  xl: "12px",
  "2xl": "16px",
  "3xl": "20px",
  full: "9999px"
};

// src/tokens/typography.ts
var fontFamily = {
  sans: 'Montserrat, ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
  serif: 'ui-serif, Georgia, Cambria, "Times New Roman", Times, serif',
  mono: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
};
var fontSize = {
  xs: ["0.75rem", "1rem"],
  sm: ["0.875rem", "1.25rem"],
  base: ["1rem", "1.5rem"],
  lg: ["1.125rem", "1.75rem"],
  xl: ["1.25rem", "1.75rem"],
  "2xl": ["1.5rem", "2rem"],
  "3xl": ["1.875rem", "2.25rem"],
  "4xl": ["2.25rem", "2.5rem"],
  "5xl": ["3rem", "1"],
  "6xl": ["3.75rem", "1"],
  "7xl": ["4.5rem", "1"],
  "8xl": ["6rem", "1"],
  "9xl": ["8rem", "1"]
};
var fontWeight = {
  thin: 100,
  extralight: 200,
  light: 300,
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800,
  black: 900
};
var lineHeight = {
  none: "1",
  tight: "1.25",
  snug: "1.375",
  normal: "1.5",
  relaxed: "1.625",
  loose: "2",
  3: "0.75rem",
  4: "1rem",
  5: "1.25rem",
  6: "1.5rem",
  7: "1.75rem",
  8: "2rem",
  9: "2.25rem",
  10: "2.5rem"
};
var letterSpacing = {
  tighter: "-0.05em",
  tight: "-0.025em",
  normal: "0em",
  wide: "0.025em",
  wider: "0.05em",
  widest: "0.1em"
};
function Form({ children, error, className = "", onSubmit, ...props }) {
  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit(event);
  };
  return /* @__PURE__ */ jsxs(
    "form",
    {
      className: `space-y-4 ${className}`.trim(),
      onSubmit: handleSubmit,
      noValidate: true,
      ...props,
      children: [
        error && /* @__PURE__ */ jsx(
          "div",
          {
            className: "p-4 rounded-lg bg-red-50 border border-red-200",
            role: "alert",
            "aria-live": "assertive",
            children: /* @__PURE__ */ jsxs("div", { className: "flex items-start", children: [
              /* @__PURE__ */ jsx(
                "svg",
                {
                  className: "w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0",
                  fill: "currentColor",
                  viewBox: "0 0 20 20",
                  "aria-hidden": "true",
                  children: /* @__PURE__ */ jsx(
                    "path",
                    {
                      fillRule: "evenodd",
                      d: "M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z",
                      clipRule: "evenodd"
                    }
                  )
                }
              ),
              /* @__PURE__ */ jsx("p", { className: "text-sm font-medium text-red-800", children: error })
            ] })
          }
        ),
        children
      ]
    }
  );
}
var VARIANT_STYLES = {
  success: {
    bg: "bg-green-50",
    border: "border-green-200",
    text: "text-green-800",
    icon: "text-green-600"
  },
  error: {
    bg: "bg-red-50",
    border: "border-red-200",
    text: "text-red-800",
    icon: "text-red-600"
  },
  warning: {
    bg: "bg-yellow-50",
    border: "border-yellow-200",
    text: "text-yellow-800",
    icon: "text-yellow-600"
  },
  info: {
    bg: "bg-blue-50",
    border: "border-blue-200",
    text: "text-blue-800",
    icon: "text-blue-600"
  }
};
var VARIANT_ICONS = {
  success: /* @__PURE__ */ jsx("svg", { className: "w-5 h-5", fill: "currentColor", viewBox: "0 0 20 20", "aria-hidden": "true", children: /* @__PURE__ */ jsx(
    "path",
    {
      fillRule: "evenodd",
      d: "M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z",
      clipRule: "evenodd"
    }
  ) }),
  error: /* @__PURE__ */ jsx("svg", { className: "w-5 h-5", fill: "currentColor", viewBox: "0 0 20 20", "aria-hidden": "true", children: /* @__PURE__ */ jsx(
    "path",
    {
      fillRule: "evenodd",
      d: "M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z",
      clipRule: "evenodd"
    }
  ) }),
  warning: /* @__PURE__ */ jsx("svg", { className: "w-5 h-5", fill: "currentColor", viewBox: "0 0 20 20", "aria-hidden": "true", children: /* @__PURE__ */ jsx(
    "path",
    {
      fillRule: "evenodd",
      d: "M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z",
      clipRule: "evenodd"
    }
  ) }),
  info: /* @__PURE__ */ jsx("svg", { className: "w-5 h-5", fill: "currentColor", viewBox: "0 0 20 20", "aria-hidden": "true", children: /* @__PURE__ */ jsx(
    "path",
    {
      fillRule: "evenodd",
      d: "M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z",
      clipRule: "evenodd"
    }
  ) })
};
function Toast({
  variant = "info",
  children,
  autoClose = true,
  duration = 5e3,
  onClose,
  className = ""
}) {
  const [visible, setVisible] = useState(true);
  const styles = VARIANT_STYLES[variant];
  useEffect(() => {
    if (!autoClose) return;
    const timer = setTimeout(() => {
      setVisible(false);
      onClose?.();
    }, duration);
    return () => clearTimeout(timer);
  }, [autoClose, duration, onClose]);
  if (!visible) return null;
  const handleClose = () => {
    setVisible(false);
    onClose?.();
  };
  const ariaRole = variant === "error" ? "alert" : "status";
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: `flex items-start p-4 rounded-lg border ${styles.bg} ${styles.border} ${className}`.trim(),
      role: ariaRole,
      "aria-live": variant === "error" ? "assertive" : "polite",
      children: [
        /* @__PURE__ */ jsx("div", { className: `flex-shrink-0 ${styles.icon}`, children: VARIANT_ICONS[variant] }),
        /* @__PURE__ */ jsx("div", { className: `ml-3 flex-1 ${styles.text} text-sm font-medium`, children }),
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            className: `ml-3 flex-shrink-0 inline-flex rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${styles.text} hover:opacity-75`,
            onClick: handleClose,
            "aria-label": "Close notification",
            children: /* @__PURE__ */ jsx("svg", { className: "w-5 h-5", fill: "currentColor", viewBox: "0 0 20 20", "aria-hidden": "true", children: /* @__PURE__ */ jsx(
              "path",
              {
                fillRule: "evenodd",
                d: "M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z",
                clipRule: "evenodd"
              }
            ) })
          }
        )
      ]
    }
  );
}
var VARIANT_CLASSES = {
  default: "bg-white border border-gray-200",
  bordered: "bg-white border-2 border-gray-300",
  elevated: "bg-white shadow-lg"
};
function Card({ variant = "default", className = "", children, ...props }) {
  const variantClasses = VARIANT_CLASSES[variant];
  const baseClasses = "rounded-lg overflow-hidden";
  const combinedClasses = `${baseClasses} ${variantClasses} ${className}`.trim();
  return /* @__PURE__ */ jsx("div", { className: combinedClasses, ...props, children });
}
function CardHeader({ className = "", children, ...props }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: `px-6 py-4 border-b border-gray-200 ${className}`.trim(),
      ...props,
      children
    }
  );
}
function CardBody({ className = "", children, ...props }) {
  return /* @__PURE__ */ jsx("div", { className: `px-6 py-4 ${className}`.trim(), ...props, children });
}
function CardFooter({ className = "", children, ...props }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: `px-6 py-4 border-t border-gray-200 bg-gray-50 ${className}`.trim(),
      ...props,
      children
    }
  );
}
Card.Header = CardHeader;
Card.Body = CardBody;
Card.Footer = CardFooter;
var Field = forwardRef(
  ({
    label,
    helperText,
    errorMessage,
    required,
    disabled,
    type = "text",
    id,
    className = "",
    ...props
  }, ref) => {
    const generatedId = useId();
    const fieldId = id || generatedId;
    const hasError = Boolean(errorMessage);
    const helperId = `${fieldId}-helper`;
    const errorId = `${fieldId}-error`;
    const inputBaseClasses = "w-full px-3 py-2 border rounded-lg text-foreground transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-1";
    const inputStateClasses = hasError ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "border-gray-300 focus:border-blue-500 focus:ring-blue-500";
    const inputDisabledClasses = disabled ? "bg-gray-100 cursor-not-allowed opacity-60" : "";
    const inputClasses = `${inputBaseClasses} ${inputStateClasses} ${inputDisabledClasses}`.trim();
    return /* @__PURE__ */ jsxs("div", { className: `w-full space-y-1.5 ${className}`.trim(), children: [
      /* @__PURE__ */ jsx(Label, { htmlFor: fieldId, required, children: label }),
      /* @__PURE__ */ jsx(
        "input",
        {
          ref,
          id: fieldId,
          type,
          disabled,
          required,
          className: inputClasses,
          "aria-invalid": hasError,
          "aria-describedby": hasError ? errorId : helperText ? helperId : void 0,
          ...props
        }
      ),
      hasError ? /* @__PURE__ */ jsx(
        "p",
        {
          id: errorId,
          className: "text-sm text-red-600",
          role: "alert",
          "aria-live": "assertive",
          children: errorMessage
        }
      ) : helperText ? /* @__PURE__ */ jsx("p", { id: helperId, className: "text-sm text-gray-500", children: helperText }) : null
    ] });
  }
);
Field.displayName = "Field";
var InputOTP = forwardRef(
  ({
    length = 6,
    onComplete,
    onChange,
    defaultValue = "",
    value: controlledValue,
    error = false,
    disabled = false,
    autoFocus = false,
    className = "",
    "aria-label": ariaLabel = "One-time password"
  }, ref) => {
    const inputRefs = useRef([]);
    const isControlled = controlledValue !== void 0;
    const getInitialValues = useCallback(() => {
      const initial = controlledValue ?? defaultValue;
      const arr = new Array(length).fill("");
      for (let i = 0; i < Math.min(initial.length, length); i++) {
        const char = initial[i];
        if (/^\d$/.test(char)) {
          arr[i] = char;
        }
      }
      return arr;
    }, [controlledValue, defaultValue, length]);
    const [values, setValues] = useState(getInitialValues);
    useEffect(() => {
      if (isControlled) {
        setValues(getInitialValues());
      }
    }, [controlledValue, isControlled, getInitialValues]);
    useEffect(() => {
      if (autoFocus && !disabled) {
        inputRefs.current[0]?.focus();
      }
    }, [autoFocus, disabled]);
    useImperativeHandle(ref, () => ({
      focus: () => {
        const firstEmptyIndex = values.findIndex((v) => !v);
        const targetIndex = firstEmptyIndex === -1 ? 0 : firstEmptyIndex;
        inputRefs.current[targetIndex]?.focus();
      },
      clear: () => {
        const newValues = new Array(length).fill("");
        setValues(newValues);
        onChange?.("");
        inputRefs.current[0]?.focus();
      },
      getValue: () => values.join("")
    }));
    const focusInput = (index) => {
      if (index >= 0 && index < length) {
        inputRefs.current[index]?.focus();
        inputRefs.current[index]?.select();
      }
    };
    const updateValue = useCallback(
      (newValues) => {
        if (!isControlled) {
          setValues(newValues);
        }
        const joinedValue = newValues.join("");
        onChange?.(joinedValue);
        if (joinedValue.length === length && newValues.every((v) => v !== "")) {
          onComplete?.(joinedValue);
        }
      },
      [isControlled, length, onChange, onComplete]
    );
    const handleChange = (index) => (e) => {
      const inputValue = e.target.value;
      if (inputValue.length > 1) {
        handlePasteValue(inputValue, index);
        return;
      }
      if (!/^\d?$/.test(inputValue)) {
        return;
      }
      const newValues = [...values];
      newValues[index] = inputValue;
      updateValue(newValues);
      if (inputValue && index < length - 1) {
        focusInput(index + 1);
      }
    };
    const handlePasteValue = (pastedValue, startIndex) => {
      const digits = pastedValue.replace(/\D/g, "").slice(0, length - startIndex);
      if (!digits) return;
      const newValues = [...values];
      for (let i = 0; i < digits.length; i++) {
        if (startIndex + i < length) {
          newValues[startIndex + i] = digits[i];
        }
      }
      updateValue(newValues);
      const nextEmptyIndex = newValues.findIndex((v, i) => i >= startIndex && !v);
      const targetIndex = nextEmptyIndex === -1 ? length - 1 : nextEmptyIndex;
      focusInput(targetIndex);
    };
    const handlePaste = (index) => (e) => {
      e.preventDefault();
      const pastedData = e.clipboardData.getData("text");
      handlePasteValue(pastedData, index);
    };
    const handleKeyDown = (index) => (e) => {
      switch (e.key) {
        case "Backspace":
          if (!values[index] && index > 0) {
            const newValues = [...values];
            newValues[index - 1] = "";
            updateValue(newValues);
            focusInput(index - 1);
          } else if (values[index]) {
            const newValues = [...values];
            newValues[index] = "";
            updateValue(newValues);
          }
          break;
        case "ArrowLeft":
          e.preventDefault();
          focusInput(index - 1);
          break;
        case "ArrowRight":
          e.preventDefault();
          focusInput(index + 1);
          break;
        case "Delete": {
          const newValues = [...values];
          newValues[index] = "";
          updateValue(newValues);
          break;
        }
      }
    };
    const handleFocus = (index) => () => {
      inputRefs.current[index]?.select();
    };
    const baseInputClasses = "w-12 h-14 text-center text-xl font-semibold text-foreground border rounded-lg transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-1";
    const stateClasses = error ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "border-gray-300 focus:border-blue-500 focus:ring-blue-500";
    const disabledClasses = disabled ? "bg-gray-100 cursor-not-allowed opacity-60" : "";
    const inputClasses = `${baseInputClasses} ${stateClasses} ${disabledClasses}`.trim();
    return /* @__PURE__ */ jsx(
      "div",
      {
        className: `flex gap-2 ${className}`.trim(),
        role: "group",
        "aria-label": ariaLabel,
        children: Array.from({ length }, (_, index) => /* @__PURE__ */ jsx(
          "input",
          {
            ref: (el) => {
              inputRefs.current[index] = el;
            },
            type: "text",
            inputMode: "numeric",
            pattern: "\\d*",
            maxLength: 1,
            value: values[index] || "",
            onChange: handleChange(index),
            onKeyDown: handleKeyDown(index),
            onPaste: handlePaste(index),
            onFocus: handleFocus(index),
            disabled,
            className: inputClasses,
            "aria-label": `Digit ${index + 1} of ${length}`,
            "aria-invalid": error,
            autoComplete: index === 0 ? "one-time-code" : "off"
          },
          index
        ))
      }
    );
  }
);
InputOTP.displayName = "InputOTP";
var Select = React44.forwardRef(({ label, errorMessage, required, helperText, children, ...props }, ref) => {
  const id = React44.useId();
  const hasError = Boolean(errorMessage);
  return /* @__PURE__ */ jsxs("div", { ref, className: "w-full space-y-1.5", children: [
    label && /* @__PURE__ */ jsxs(
      "label",
      {
        htmlFor: id,
        className: "inline-flex gap-1 font-semibold text-slate-900",
        children: [
          /* @__PURE__ */ jsx("span", { children: label }),
          required && /* @__PURE__ */ jsx("span", { "aria-hidden": "true", className: "text-red-500", children: "*" })
        ]
      }
    ),
    /* @__PURE__ */ jsx(SelectPrimitive.Root, { ...props, children }),
    hasError ? /* @__PURE__ */ jsx("p", { className: "text-sm text-red-600", role: "alert", "aria-live": "assertive", children: errorMessage }) : helperText ? /* @__PURE__ */ jsx("p", { className: "text-sm text-gray-500", children: helperText }) : null
  ] });
});
Select.displayName = "Select";
var SelectGroup = SelectPrimitive.Group;
var SelectValue = SelectPrimitive.Value;
var SelectTrigger = React44.forwardRef(({ className = "", error, children, ...props }, ref) => {
  const baseClasses = "flex h-10 w-full items-center justify-between rounded-lg border bg-white px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60 disabled:bg-gray-100 [&>span]:line-clamp-1";
  const stateClasses = error ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "border-gray-300 focus:border-blue-500 focus:ring-blue-500";
  const combinedClasses = `${baseClasses} ${stateClasses} ${className}`.trim();
  return /* @__PURE__ */ jsxs(SelectPrimitive.Trigger, { ref, className: combinedClasses, ...props, children: [
    children,
    /* @__PURE__ */ jsx(SelectPrimitive.Icon, { asChild: true, children: /* @__PURE__ */ jsx(ChevronDown, { className: "h-4 w-4 opacity-50 shrink-0" }) })
  ] });
});
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;
var SelectScrollUpButton = React44.forwardRef(({ className = "", ...props }, ref) => /* @__PURE__ */ jsx(
  SelectPrimitive.ScrollUpButton,
  {
    ref,
    className: `flex cursor-default items-center justify-center py-1 ${className}`.trim(),
    ...props,
    children: /* @__PURE__ */ jsx(ChevronUp, { className: "h-4 w-4" })
  }
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;
var SelectScrollDownButton = React44.forwardRef(({ className = "", ...props }, ref) => /* @__PURE__ */ jsx(
  SelectPrimitive.ScrollDownButton,
  {
    ref,
    className: `flex cursor-default items-center justify-center py-1 ${className}`.trim(),
    ...props,
    children: /* @__PURE__ */ jsx(ChevronDown, { className: "h-4 w-4" })
  }
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;
var SelectContent = React44.forwardRef(({ className = "", children, position = "popper", ...props }, ref) => /* @__PURE__ */ jsx(SelectPrimitive.Portal, { children: /* @__PURE__ */ jsxs(
  SelectPrimitive.Content,
  {
    ref,
    className: `relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-lg border border-gray-200 bg-white text-slate-900 shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 ${position === "popper" ? "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1" : ""} ${className}`.trim(),
    position,
    ...props,
    children: [
      /* @__PURE__ */ jsx(SelectScrollUpButton, {}),
      /* @__PURE__ */ jsx(
        SelectPrimitive.Viewport,
        {
          className: `p-1 ${position === "popper" ? "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]" : ""}`.trim(),
          children
        }
      ),
      /* @__PURE__ */ jsx(SelectScrollDownButton, {})
    ]
  }
) }));
SelectContent.displayName = SelectPrimitive.Content.displayName;
var SelectLabel = React44.forwardRef(({ className = "", ...props }, ref) => /* @__PURE__ */ jsx(
  SelectPrimitive.Label,
  {
    ref,
    className: `py-1.5 pl-8 pr-2 text-sm font-semibold text-gray-500 ${className}`.trim(),
    ...props
  }
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;
var SelectItem = React44.forwardRef(({ className = "", children, ...props }, ref) => /* @__PURE__ */ jsxs(
  SelectPrimitive.Item,
  {
    ref,
    className: `relative flex w-full cursor-pointer select-none items-center rounded-md py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-blue-50 focus:text-blue-900 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 ${className}`.trim(),
    ...props,
    children: [
      /* @__PURE__ */ jsx("span", { className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center", children: /* @__PURE__ */ jsx(SelectPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx(Check, { className: "h-4 w-4" }) }) }),
      /* @__PURE__ */ jsx(SelectPrimitive.ItemText, { children })
    ]
  }
));
SelectItem.displayName = SelectPrimitive.Item.displayName;
var SelectSeparator = React44.forwardRef(({ className = "", ...props }, ref) => /* @__PURE__ */ jsx(
  SelectPrimitive.Separator,
  {
    ref,
    className: `-mx-1 my-1 h-px bg-gray-200 ${className}`.trim(),
    ...props
  }
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;
var Combobox = React44.forwardRef(
  ({
    options,
    value,
    onValueChange,
    placeholder = "Select an option...",
    searchPlaceholder = "Search...",
    emptyMessage = "No results found.",
    label,
    error,
    disabled = false,
    className,
    popoverWidth = "300px"
  }, ref) => {
    const [open, setOpen] = React44.useState(false);
    const [searchQuery, setSearchQuery] = React44.useState("");
    const inputRef = React44.useRef(null);
    const selectedOption = options.find((opt) => opt.value === value);
    const filteredOptions = React44.useMemo(() => {
      if (!searchQuery) return options;
      const query = searchQuery.toLowerCase();
      return options.filter(
        (opt) => opt.label.toLowerCase().includes(query)
      );
    }, [options, searchQuery]);
    const handleSelect = React44.useCallback(
      (selectedValue) => {
        const newValue = selectedValue === value ? "" : selectedValue;
        onValueChange?.(newValue);
        setOpen(false);
        setSearchQuery("");
      },
      [value, onValueChange]
    );
    const handleKeyDown = React44.useCallback(
      (event) => {
        if (event.key === "Escape") {
          setOpen(false);
          setSearchQuery("");
        }
      },
      []
    );
    const id = React44.useId();
    const labelId = `${id}-label`;
    const errorId = `${id}-error`;
    const listboxId = `${id}-listbox`;
    const hasError = Boolean(error);
    return /* @__PURE__ */ jsxs("div", { className: cn("w-full", className), children: [
      label && /* @__PURE__ */ jsx(
        "label",
        {
          id: labelId,
          className: "block text-sm font-medium text-gray-700 mb-1",
          children: label
        }
      ),
      /* @__PURE__ */ jsxs(PopoverPrimitive2.Root, { open, onOpenChange: setOpen, children: [
        /* @__PURE__ */ jsx(PopoverPrimitive2.Trigger, { asChild: true, children: /* @__PURE__ */ jsxs(
          "button",
          {
            ref,
            type: "button",
            role: "combobox",
            "aria-expanded": open,
            "aria-controls": listboxId,
            "aria-haspopup": "listbox",
            "aria-labelledby": label ? labelId : void 0,
            "aria-describedby": hasError ? errorId : void 0,
            "aria-invalid": hasError,
            disabled,
            className: cn(
              "flex h-10 w-full items-center justify-between rounded-lg border bg-white px-3 py-2 text-sm",
              "focus:outline-none focus:ring-2 focus:ring-offset-1",
              "disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-100",
              hasError ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
              open && "ring-2 ring-offset-1",
              open && (hasError ? "ring-red-500" : "ring-blue-500")
            ),
            children: [
              /* @__PURE__ */ jsx(
                "span",
                {
                  className: cn(
                    "truncate",
                    !selectedOption && "text-gray-500"
                  ),
                  children: selectedOption?.label ?? placeholder
                }
              ),
              /* @__PURE__ */ jsx(ChevronsUpDown, { className: "ml-2 h-4 w-4 shrink-0 opacity-50" })
            ]
          }
        ) }),
        /* @__PURE__ */ jsx(PopoverPrimitive2.Portal, { children: /* @__PURE__ */ jsx(
          PopoverPrimitive2.Content,
          {
            className: cn(
              "z-50 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg",
              "data-[state=open]:animate-in data-[state=closed]:animate-out",
              "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
              "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
              "data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2"
            ),
            style: { width: popoverWidth },
            align: "start",
            sideOffset: 4,
            onKeyDown: handleKeyDown,
            children: /* @__PURE__ */ jsxs(Command, { className: "flex flex-col", shouldFilter: false, children: [
              /* @__PURE__ */ jsxs("div", { className: "flex items-center border-b border-gray-200 px-3", children: [
                /* @__PURE__ */ jsx(Search, { className: "mr-2 h-4 w-4 shrink-0 opacity-50" }),
                /* @__PURE__ */ jsx(
                  Command.Input,
                  {
                    ref: inputRef,
                    value: searchQuery,
                    onValueChange: setSearchQuery,
                    placeholder: searchPlaceholder,
                    className: cn(
                      "flex h-10 w-full bg-transparent py-3 text-sm outline-none",
                      "placeholder:text-gray-500",
                      "disabled:cursor-not-allowed disabled:opacity-50"
                    )
                  }
                )
              ] }),
              /* @__PURE__ */ jsx(Command.List, { id: listboxId, role: "listbox", className: "max-h-60 overflow-y-auto p-1", children: filteredOptions.length === 0 ? /* @__PURE__ */ jsx("div", { className: "py-6 text-center text-sm text-gray-500", children: emptyMessage }) : filteredOptions.map((option) => /* @__PURE__ */ jsxs(
                Command.Item,
                {
                  value: option.value,
                  disabled: option.disabled,
                  onSelect: () => handleSelect(option.value),
                  className: cn(
                    "relative flex cursor-pointer select-none items-center rounded-md px-2 py-2 text-sm outline-none",
                    "data-[selected=true]:bg-gray-100 data-[selected=true]:text-gray-900",
                    "data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50",
                    "hover:bg-gray-100"
                  ),
                  children: [
                    /* @__PURE__ */ jsx(
                      Check,
                      {
                        className: cn(
                          "mr-2 h-4 w-4 shrink-0",
                          value === option.value ? "opacity-100" : "opacity-0"
                        )
                      }
                    ),
                    /* @__PURE__ */ jsx("span", { className: "truncate", children: option.label })
                  ]
                },
                option.value
              )) })
            ] })
          }
        ) })
      ] }),
      error && /* @__PURE__ */ jsx(
        "p",
        {
          id: errorId,
          className: "mt-1 text-sm text-red-600",
          role: "alert",
          children: error
        }
      )
    ] });
  }
);
Combobox.displayName = "Combobox";
var Breadcrumb = React44.forwardRef(
  ({ className, separator, maxItems, onExpand, children, ...props }, ref) => {
    const [isExpanded, setIsExpanded] = React44.useState(false);
    const handleExpand = React44.useCallback(() => {
      setIsExpanded(true);
      onExpand?.();
    }, [onExpand]);
    const processedChildren = React44.useMemo(() => {
      const childArray = React44.Children.toArray(children);
      if (!maxItems || isExpanded || childArray.length <= maxItems) {
        return childArray;
      }
      const firstItem = childArray[0];
      const lastItems = childArray.slice(-(maxItems - 1));
      return [
        firstItem,
        /* @__PURE__ */ jsx(BreadcrumbItem, { children: /* @__PURE__ */ jsx(BreadcrumbEllipsis, { onClick: handleExpand }) }, "ellipsis"),
        ...lastItems
      ];
    }, [children, maxItems, isExpanded, handleExpand]);
    return /* @__PURE__ */ jsx(BreadcrumbContext.Provider, { value: { separator }, children: /* @__PURE__ */ jsx(
      "nav",
      {
        ref,
        "aria-label": "Breadcrumb",
        className: cn("flex", className),
        ...props,
        children: /* @__PURE__ */ jsx(BreadcrumbList, { children: processedChildren })
      }
    ) });
  }
);
Breadcrumb.displayName = "Breadcrumb";
var BreadcrumbContext = React44.createContext({});
function useBreadcrumbContext() {
  return React44.useContext(BreadcrumbContext);
}
var BreadcrumbList = React44.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx(
    "ol",
    {
      ref,
      className: cn(
        "flex flex-wrap items-center gap-1.5 break-words text-sm text-gray-500 sm:gap-2.5",
        className
      ),
      ...props
    }
  )
);
BreadcrumbList.displayName = "BreadcrumbList";
var BreadcrumbItem = React44.forwardRef(
  ({ className, children, ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      "li",
      {
        ref,
        className: cn("inline-flex items-center gap-1.5", className),
        ...props,
        children
      }
    );
  }
);
BreadcrumbItem.displayName = "BreadcrumbItem";
var BreadcrumbLink = React44.forwardRef(
  ({ asChild, className, ...props }, ref) => {
    const Comp = asChild ? Slot : "a";
    return /* @__PURE__ */ jsx(
      Comp,
      {
        ref,
        className: cn(
          "transition-colors hover:text-gray-900",
          "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded",
          className
        ),
        ...props
      }
    );
  }
);
BreadcrumbLink.displayName = "BreadcrumbLink";
var BreadcrumbPage = React44.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx(
    "span",
    {
      ref,
      role: "link",
      "aria-disabled": "true",
      "aria-current": "page",
      className: cn("font-medium text-gray-900", className),
      ...props
    }
  )
);
BreadcrumbPage.displayName = "BreadcrumbPage";
var BreadcrumbSeparator = ({
  children,
  className,
  ...props
}) => {
  const { separator } = useBreadcrumbContext();
  return /* @__PURE__ */ jsx(
    "li",
    {
      role: "presentation",
      "aria-hidden": "true",
      className: cn("[&>svg]:size-3.5 text-gray-400", className),
      ...props,
      children: children ?? (separator || /* @__PURE__ */ jsx(ChevronRight, {}))
    }
  );
};
BreadcrumbSeparator.displayName = "BreadcrumbSeparator";
var BreadcrumbEllipsis = React44.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsxs(
  "button",
  {
    ref,
    type: "button",
    className: cn(
      "flex h-6 w-6 items-center justify-center rounded hover:bg-gray-100",
      "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
      "transition-colors",
      className
    ),
    "aria-label": "Show more breadcrumbs",
    ...props,
    children: [
      /* @__PURE__ */ jsx(MoreHorizontal, { className: "h-4 w-4" }),
      /* @__PURE__ */ jsx("span", { className: "sr-only", children: "More" })
    ]
  }
));
BreadcrumbEllipsis.displayName = "BreadcrumbEllipsis";
function getPageRange(currentPage, totalPages, siblingCount) {
  const totalSlots = 1 + 1 + 1 + 2 * siblingCount + 2;
  if (totalPages <= totalSlots) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
  const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);
  const shouldShowLeftEllipsis = leftSiblingIndex > 2;
  const shouldShowRightEllipsis = rightSiblingIndex < totalPages - 1;
  const firstPageIndex = 1;
  const lastPageIndex = totalPages;
  if (!shouldShowLeftEllipsis && shouldShowRightEllipsis) {
    const leftItemCount = 3 + 2 * siblingCount;
    const leftRange = Array.from({ length: leftItemCount }, (_, i) => i + 1);
    return [...leftRange, "ellipsis-end", totalPages];
  }
  if (shouldShowLeftEllipsis && !shouldShowRightEllipsis) {
    const rightItemCount = 3 + 2 * siblingCount;
    const rightRange = Array.from(
      { length: rightItemCount },
      (_, i) => totalPages - rightItemCount + i + 1
    );
    return [firstPageIndex, "ellipsis-start", ...rightRange];
  }
  if (shouldShowLeftEllipsis && shouldShowRightEllipsis) {
    const middleRange = Array.from(
      { length: rightSiblingIndex - leftSiblingIndex + 1 },
      (_, i) => leftSiblingIndex + i
    );
    return [
      firstPageIndex,
      "ellipsis-start",
      ...middleRange,
      "ellipsis-end",
      lastPageIndex
    ];
  }
  return Array.from({ length: totalPages }, (_, i) => i + 1);
}
var PaginationButton = React44.forwardRef(({ className, isActive, disabled, children, ...props }, ref) => /* @__PURE__ */ jsx(
  "button",
  {
    ref,
    type: "button",
    disabled,
    className: cn(
      "inline-flex h-9 min-w-9 items-center justify-center rounded-md px-3 text-sm font-medium",
      "transition-colors duration-150",
      "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
      "disabled:pointer-events-none disabled:opacity-50",
      isActive ? "bg-blue-600 text-white hover:bg-blue-700" : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-100",
      className
    ),
    "aria-current": isActive ? "page" : void 0,
    ...props,
    children
  }
));
PaginationButton.displayName = "PaginationButton";
var PaginationNavButton = React44.forwardRef(({ className, direction, disabled, ...props }, ref) => {
  const icons = {
    first: ChevronsLeft,
    previous: ChevronLeft,
    next: ChevronRight,
    last: ChevronsRight
  };
  const labels = {
    first: "Go to first page",
    previous: "Go to previous page",
    next: "Go to next page",
    last: "Go to last page"
  };
  const Icon2 = icons[direction];
  return /* @__PURE__ */ jsx(
    "button",
    {
      ref,
      type: "button",
      disabled,
      className: cn(
        "inline-flex h-9 w-9 items-center justify-center rounded-md",
        "bg-white text-gray-700 border border-gray-300",
        "transition-colors duration-150",
        "hover:bg-gray-100",
        "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
        "disabled:pointer-events-none disabled:opacity-50",
        className
      ),
      "aria-label": labels[direction],
      ...props,
      children: /* @__PURE__ */ jsx(Icon2, { className: "h-4 w-4" })
    }
  );
});
PaginationNavButton.displayName = "PaginationNavButton";
var PaginationEllipsis = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx(
  "span",
  {
    className: cn(
      "flex h-9 w-9 items-center justify-center text-gray-500",
      className
    ),
    "aria-hidden": "true",
    ...props,
    children: /* @__PURE__ */ jsx(MoreHorizontal, { className: "h-4 w-4" })
  }
);
var Pagination = React44.forwardRef(
  ({
    currentPage,
    totalPages,
    onPageChange,
    siblingCount = 1,
    showFirstLast = false,
    disabled = false,
    className
  }, ref) => {
    const pageRange = React44.useMemo(
      () => getPageRange(currentPage, totalPages, siblingCount),
      [currentPage, totalPages, siblingCount]
    );
    const handlePageClick = React44.useCallback(
      (page) => {
        if (!disabled && page >= 1 && page <= totalPages && page !== currentPage) {
          onPageChange(page);
        }
      },
      [disabled, totalPages, currentPage, onPageChange]
    );
    const isFirstPage = currentPage === 1;
    const isLastPage = currentPage === totalPages;
    if (totalPages <= 1) {
      return null;
    }
    return /* @__PURE__ */ jsx(
      "nav",
      {
        ref,
        role: "navigation",
        "aria-label": "Pagination",
        className: cn("flex items-center justify-center", className),
        children: /* @__PURE__ */ jsxs("ul", { className: "flex items-center gap-1", children: [
          showFirstLast && /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsx(
            PaginationNavButton,
            {
              direction: "first",
              onClick: () => handlePageClick(1),
              disabled: disabled || isFirstPage
            }
          ) }),
          /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsx(
            PaginationNavButton,
            {
              direction: "previous",
              onClick: () => handlePageClick(currentPage - 1),
              disabled: disabled || isFirstPage
            }
          ) }),
          pageRange.map((item) => {
            if (item === "ellipsis-start" || item === "ellipsis-end") {
              return /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsx(PaginationEllipsis, {}) }, item);
            }
            return /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsx(
              PaginationButton,
              {
                onClick: () => handlePageClick(item),
                isActive: currentPage === item,
                disabled,
                children: item
              }
            ) }, item);
          }),
          /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsx(
            PaginationNavButton,
            {
              direction: "next",
              onClick: () => handlePageClick(currentPage + 1),
              disabled: disabled || isLastPage
            }
          ) }),
          showFirstLast && /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsx(
            PaginationNavButton,
            {
              direction: "last",
              onClick: () => handlePageClick(totalPages),
              disabled: disabled || isLastPage
            }
          ) })
        ] })
      }
    );
  }
);
Pagination.displayName = "Pagination";
var Popover = PopoverPrimitive2.Root;
var PopoverTrigger = PopoverPrimitive2.Trigger;
var PopoverContent = React44.forwardRef(({ className, align = "center", sideOffset = 4, ...props }, ref) => /* @__PURE__ */ jsx(PopoverPrimitive2.Portal, { children: /* @__PURE__ */ jsx(
  PopoverPrimitive2.Content,
  {
    ref,
    align,
    sideOffset,
    className: cn(
      "z-50 w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    ),
    ...props
  }
) }));
PopoverContent.displayName = PopoverPrimitive2.Content.displayName;
var ContextMenu = ContextMenuPrimitive.Root;
var ContextMenuTrigger = ContextMenuPrimitive.Trigger;
var ContextMenuGroup = ContextMenuPrimitive.Group;
var ContextMenuPortal = ContextMenuPrimitive.Portal;
var ContextMenuSub = ContextMenuPrimitive.Sub;
var ContextMenuRadioGroup = ContextMenuPrimitive.RadioGroup;
var ContextMenuSubTrigger = React44.forwardRef(({ className, inset, children, ...props }, ref) => /* @__PURE__ */ jsxs(
  ContextMenuPrimitive.SubTrigger,
  {
    ref,
    className: cn(
      "flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[state=open]:bg-accent data-[state=open]:text-accent-foreground",
      inset && "pl-8",
      className
    ),
    ...props,
    children: [
      children,
      /* @__PURE__ */ jsx(ChevronRight, { className: "ml-auto h-4 w-4" })
    ]
  }
));
ContextMenuSubTrigger.displayName = ContextMenuPrimitive.SubTrigger.displayName;
var ContextMenuSubContent = React44.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  ContextMenuPrimitive.SubContent,
  {
    ref,
    className: cn(
      "z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    ),
    ...props
  }
));
ContextMenuSubContent.displayName = ContextMenuPrimitive.SubContent.displayName;
var ContextMenuContent = React44.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(ContextMenuPrimitive.Portal, { children: /* @__PURE__ */ jsx(
  ContextMenuPrimitive.Content,
  {
    ref,
    className: cn(
      "z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    ),
    ...props
  }
) }));
ContextMenuContent.displayName = ContextMenuPrimitive.Content.displayName;
var ContextMenuItem = React44.forwardRef(({ className, inset, ...props }, ref) => /* @__PURE__ */ jsx(
  ContextMenuPrimitive.Item,
  {
    ref,
    className: cn(
      "relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      inset && "pl-8",
      className
    ),
    ...props
  }
));
ContextMenuItem.displayName = ContextMenuPrimitive.Item.displayName;
var ContextMenuCheckboxItem = React44.forwardRef(({ className, children, checked, ...props }, ref) => /* @__PURE__ */ jsxs(
  ContextMenuPrimitive.CheckboxItem,
  {
    ref,
    className: cn(
      "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    ),
    checked,
    ...props,
    children: [
      /* @__PURE__ */ jsx("span", { className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center", children: /* @__PURE__ */ jsx(ContextMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx(Check, { className: "h-4 w-4" }) }) }),
      children
    ]
  }
));
ContextMenuCheckboxItem.displayName = ContextMenuPrimitive.CheckboxItem.displayName;
var ContextMenuRadioItem = React44.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs(
  ContextMenuPrimitive.RadioItem,
  {
    ref,
    className: cn(
      "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    ),
    ...props,
    children: [
      /* @__PURE__ */ jsx("span", { className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center", children: /* @__PURE__ */ jsx(ContextMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx(Circle, { className: "h-2 w-2 fill-current" }) }) }),
      children
    ]
  }
));
ContextMenuRadioItem.displayName = ContextMenuPrimitive.RadioItem.displayName;
var ContextMenuLabel = React44.forwardRef(({ className, inset, ...props }, ref) => /* @__PURE__ */ jsx(
  ContextMenuPrimitive.Label,
  {
    ref,
    className: cn(
      "px-2 py-1.5 text-sm font-semibold text-foreground",
      inset && "pl-8",
      className
    ),
    ...props
  }
));
ContextMenuLabel.displayName = ContextMenuPrimitive.Label.displayName;
var ContextMenuSeparator = React44.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  ContextMenuPrimitive.Separator,
  {
    ref,
    className: cn("-mx-1 my-1 h-px bg-border", className),
    ...props
  }
));
ContextMenuSeparator.displayName = ContextMenuPrimitive.Separator.displayName;
var ContextMenuShortcut = ({
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx(
    "span",
    {
      className: cn(
        "ml-auto text-xs tracking-widest text-muted-foreground",
        className
      ),
      ...props
    }
  );
};
ContextMenuShortcut.displayName = "ContextMenuShortcut";
function formatTime(totalSeconds, forceHours = false) {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor(totalSeconds % 3600 / 60);
  const seconds = Math.floor(totalSeconds % 60);
  if (hours > 0 || forceHours) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  }
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}
var TimeRuler = React44.forwardRef(
  ({ duration, zoom, currentTime = 0, onTimeClick, onTimeScrubStart, onTimeScrub, onTimeScrubEnd, className, ...props }, ref) => {
    const [isScrubbing, setIsScrubbing] = React44.useState(false);
    const rulerRef = React44.useRef(null);
    const pixelsPerSecond = zoom / 100 * 16;
    const pixelsPerMinute = pixelsPerSecond * 60;
    let minorTickInterval = 1;
    if (pixelsPerSecond < 4) minorTickInterval = 5;
    if (pixelsPerSecond < 1) minorTickInterval = 10;
    if (pixelsPerSecond < 0.2) minorTickInterval = 30;
    let labelIntervalMinutes = 1;
    if (pixelsPerMinute < 40) labelIntervalMinutes = 2;
    if (pixelsPerMinute < 20) labelIntervalMinutes = 5;
    if (pixelsPerMinute < 10) labelIntervalMinutes = 10;
    const ticks = [];
    for (let t = 0; t <= duration; t += minorTickInterval) {
      const isMinute = t % 60 === 0;
      const isLabel = isMinute && t / 60 % labelIntervalMinutes === 0;
      ticks.push({
        time: t,
        isMajor: isMinute,
        isLabel
      });
    }
    const pixelToTime = React44.useCallback((clientX) => {
      const ruler = rulerRef.current;
      if (!ruler) return 0;
      const rect = ruler.getBoundingClientRect();
      const x = clientX - rect.left;
      const xRem = x / 16;
      const timeSeconds = xRem * 100 / zoom;
      return Math.max(0, Math.min(duration, timeSeconds));
    }, [zoom, duration]);
    const handleScrubMove = React44.useCallback((e) => {
      const time = pixelToTime(e.clientX);
      onTimeScrub?.(time);
      onTimeClick?.(time);
    }, [pixelToTime, onTimeScrub, onTimeClick]);
    const handleScrubEnd = React44.useCallback((e) => {
      setIsScrubbing(false);
      const time = pixelToTime(e.clientX);
      onTimeScrubEnd?.(time);
      document.removeEventListener("mousemove", handleScrubMove);
      document.removeEventListener("mouseup", handleScrubEnd);
    }, [pixelToTime, onTimeScrubEnd, handleScrubMove]);
    const handleMouseDown = React44.useCallback((e) => {
      e.preventDefault();
      setIsScrubbing(true);
      const time = pixelToTime(e.clientX);
      onTimeScrubStart?.(time);
      onTimeClick?.(time);
      onTimeScrub?.(time);
      document.addEventListener("mousemove", handleScrubMove);
      document.addEventListener("mouseup", handleScrubEnd);
    }, [pixelToTime, onTimeClick, onTimeScrubStart, onTimeScrub, handleScrubMove, handleScrubEnd]);
    React44.useEffect(() => {
      return () => {
        document.removeEventListener("mousemove", handleScrubMove);
        document.removeEventListener("mouseup", handleScrubEnd);
      };
    }, [handleScrubMove, handleScrubEnd]);
    const widthRem = duration * zoom / 100;
    const setRefs = React44.useCallback((node) => {
      rulerRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    }, [ref]);
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ref: setRefs,
        role: "slider",
        "aria-label": "Timeline position",
        "aria-valuemin": 0,
        "aria-valuemax": duration,
        "aria-valuenow": currentTime,
        tabIndex: 0,
        className: cn(
          "relative h-8 w-full select-none border-b bg-muted/30 text-[10px] text-muted-foreground",
          isScrubbing && "cursor-grabbing",
          !isScrubbing && "cursor-pointer",
          className
        ),
        style: { width: `${widthRem}rem` },
        onMouseDown: handleMouseDown,
        onKeyDown: (e) => {
          if (e.key === "ArrowLeft") {
            e.preventDefault();
            onTimeClick?.(Math.max(0, currentTime - 1));
          } else if (e.key === "ArrowRight") {
            e.preventDefault();
            onTimeClick?.(Math.min(duration, currentTime + 1));
          }
        },
        ...props,
        children: [
          ticks.map(({ time, isMajor, isLabel }) => /* @__PURE__ */ jsx(
            "div",
            {
              className: cn(
                "absolute bottom-0 border-l border-muted-foreground/50",
                isMajor ? "h-full border-muted-foreground" : "h-1/3 border-muted-foreground/30"
              ),
              style: { left: `${time / 100 * zoom}rem` },
              children: isLabel && /* @__PURE__ */ jsx("span", { className: "absolute top-0 left-1 whitespace-nowrap font-medium", children: formatTime(time, duration >= 3600) })
            },
            time
          )),
          /* @__PURE__ */ jsx(
            "div",
            {
              className: cn(
                "absolute top-0 bottom-0 z-10 w-px bg-primary",
                !isScrubbing && "transition-all duration-75"
              ),
              style: { left: `${currentTime / 100 * zoom}rem` },
              children: /* @__PURE__ */ jsx(
                "div",
                {
                  className: cn(
                    "absolute top-0 -left-1.5 h-3 w-3 rotate-45 border-2 border-primary bg-primary",
                    isScrubbing && "scale-125 shadow-lg"
                  )
                }
              )
            }
          )
        ]
      }
    );
  }
);
TimeRuler.displayName = "TimeRuler";
function TimelineTrack({
  id,
  label,
  height = 80,
  color,
  children,
  className,
  ...props
}) {
  const { setNodeRef, isOver } = useDroppable({
    id
  });
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ref: setNodeRef,
      className: cn(
        "group relative flex w-full border-b transition-colors",
        isOver ? "bg-accent/20" : "bg-background hover:bg-muted/10",
        className
      ),
      style: { height: `${height}px` },
      ...props,
      children: [
        /* @__PURE__ */ jsxs("div", { className: "sticky left-0 z-20 flex w-32 shrink-0 items-center border-r bg-muted/50 px-3 text-xs font-medium text-muted-foreground shadow-sm", children: [
          /* @__PURE__ */ jsx("div", { className: "absolute left-0 h-full w-1", style: { backgroundColor: color } }),
          label
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "relative flex-1 overflow-visible", children: [
          /* @__PURE__ */ jsx("div", { className: "absolute inset-0 z-0 opacity-10" }),
          /* @__PURE__ */ jsx("div", { className: "relative z-10 h-full", children })
        ] })
      ]
    }
  );
}
function TimelineItem({
  id,
  startTime,
  duration,
  zoom,
  label,
  thumbnail,
  color,
  variant = "video",
  selected,
  coSelected,
  locked,
  index,
  trackId,
  showGearIcon,
  onGearClick,
  onResize,
  className,
  style,
  linkedCuePointId: _linkedCuePointId,
  // Destructure to prevent DOM spread
  lockedToVideoId: _lockedToVideoId,
  // Destructure to prevent DOM spread
  inPoint: _inPoint,
  // Destructure to prevent DOM spread
  outPoint: _outPoint,
  // Destructure to prevent DOM spread
  sourceDuration,
  questionData,
  ...props
}) {
  const inPoint = _inPoint ?? 0;
  const outPoint = _outPoint ?? sourceDuration ?? duration;
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id,
    disabled: locked,
    data: {
      type: "timeline-item",
      id,
      index,
      trackId,
      startTime,
      duration,
      label,
      variant
    }
  });
  const attributesWithoutHydrationWarning = React44.useMemo(() => {
    if (!attributes) return attributes;
    return {
      ...attributes,
      suppressHydrationWarning: true
    };
  }, [attributes]);
  const widthRem = duration / 100 * zoom;
  const leftRem = startTime / 100 * zoom;
  const dragStyle = {
    transform: CSS.Translate.toString(transform),
    left: `${leftRem}rem`,
    width: `${widthRem}rem`,
    // Only apply inline backgroundColor if explicit color prop is provided
    // Otherwise let Tailwind variant classes handle the color
    ...color && { backgroundColor: color },
    zIndex: isDragging ? 100 : selected ? 50 : 10,
    ...style
  };
  const isTrimmed = inPoint > 0 || sourceDuration && outPoint < sourceDuration;
  const handleResizeStart = (e, direction) => {
    e.stopPropagation();
    e.preventDefault();
    if (!onResize || locked) return;
    const startX = e.clientX;
    const initialDuration = duration;
    const minDuration = variant === "question" ? 5e3 : 100;
    const onMouseMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaTime = deltaX / zoom * 1e3;
      if (direction === "left") {
        const newDeltaStart = Math.min(deltaTime, initialDuration - minDuration);
        const newDeltaDuration = -newDeltaStart;
        onResize(id, newDeltaStart, newDeltaDuration);
      } else {
        const newDeltaDuration = Math.max(deltaTime, -initialDuration + minDuration);
        onResize(id, 0, newDeltaDuration);
      }
    };
    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };
  const itemContent = /* @__PURE__ */ jsxs(
    "div",
    {
      ref: setNodeRef,
      style: dragStyle,
      className: cn(
        "absolute top-1 bottom-1 flex cursor-grab flex-col overflow-hidden rounded border text-[10px] shadow-sm transition-shadow active:cursor-grabbing",
        // Selection states: primary selected (solid ring) vs co-selected (softer ring for linked item)
        selected ? "ring-2 ring-yak-orange ring-offset-1 border-yak-orange" : coSelected ? "ring-2 ring-yak-orange/50 ring-offset-1 border-yak-orange/50 animate-pulse" : "border-black/10",
        isDragging && "opacity-50 shadow-lg",
        locked && "cursor-default opacity-80",
        variant === "video" && "bg-yak-teal text-white",
        variant === "question" && "bg-yak-orange text-white",
        // Same as Ad per requirement
        variant === "ad" && "bg-yak-orange text-white",
        variant === "interstitial" && "bg-yak-blue text-white",
        className
      ),
      ...attributesWithoutHydrationWarning,
      ...listeners,
      ...props,
      children: [
        thumbnail ? /* @__PURE__ */ jsx("div", { className: "relative h-12 w-full shrink-0 bg-black/20", children: /* @__PURE__ */ jsx("img", { src: thumbnail, alt: "", className: "h-full w-full object-cover opacity-80" }) }) : /* @__PURE__ */ jsx("div", { className: "h-1 bg-white/20" }),
        /* @__PURE__ */ jsxs("div", { className: "flex flex-1 flex-col p-1 leading-tight", children: [
          /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-1", children: [
            /* @__PURE__ */ jsx("span", { className: "truncate font-bold uppercase", children: variant }),
            locked && /* @__PURE__ */ jsx("svg", { className: "h-3 w-3 flex-shrink-0 opacity-70", viewBox: "0 0 24 24", fill: "currentColor", children: /* @__PURE__ */ jsx("path", { d: "M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" }) }),
            showGearIcon && /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                onClick: (e) => {
                  e.stopPropagation();
                  onGearClick?.();
                },
                className: "flex-shrink-0 rounded p-0.5 hover:bg-white/20 focus:outline-none focus:ring-1 focus:ring-white/50",
                "aria-label": "Configure ad",
                children: /* @__PURE__ */ jsx("svg", { className: "h-3 w-3", viewBox: "0 0 24 24", fill: "currentColor", children: /* @__PURE__ */ jsx("path", { d: "M19.14 12.94c.04-.31.06-.63.06-.94 0-.31-.02-.63-.06-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" }) })
              }
            )
          ] }),
          /* @__PURE__ */ jsx("span", { className: "line-clamp-2", children: label })
        ] }),
        isTrimmed && /* @__PURE__ */ jsx("div", { className: "absolute top-0 left-0 right-0 h-0.5 bg-white/60", title: `Trimmed: ${inPoint.toFixed(1)}s - ${outPoint.toFixed(1)}s` }),
        !locked && onResize && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(
            "div",
            {
              role: "presentation",
              className: "absolute top-0 left-0 bottom-0 w-1.5 cursor-ew-resize hover:bg-white/30",
              onMouseDown: (e) => handleResizeStart(e, "left")
            }
          ),
          /* @__PURE__ */ jsx(
            "div",
            {
              role: "presentation",
              className: "absolute top-0 right-0 bottom-0 w-1.5 cursor-ew-resize hover:bg-white/30",
              onMouseDown: (e) => handleResizeStart(e, "right")
            }
          )
        ] })
      ]
    }
  );
  if (variant === "question" && questionData?.questionText) {
    return /* @__PURE__ */ jsx(TooltipProvider, { children: /* @__PURE__ */ jsxs(Tooltip, { children: [
      /* @__PURE__ */ jsx(TooltipTrigger, { asChild: true, children: itemContent }),
      /* @__PURE__ */ jsxs(TooltipContent, { side: "top", className: "max-w-xs", children: [
        /* @__PURE__ */ jsx("p", { className: "font-medium text-sm", children: questionData.questionText }),
        questionData.answers && questionData.answers.length > 0 && /* @__PURE__ */ jsx("ul", { className: "mt-1 text-xs text-muted-foreground list-disc pl-4", children: questionData.answers.slice(0, 4).map((answer, i) => /* @__PURE__ */ jsx("li", { children: answer }, i)) })
      ] })
    ] }) });
  }
  return itemContent;
}
function DragDropContext({ children, ...props }) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );
  return /* @__PURE__ */ jsx(DndContext, { sensors, ...props, children });
}
var Accordion = AccordionPrimitive.Root;
var AccordionItem = React44.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  AccordionPrimitive.Item,
  {
    ref,
    className: cn("border-b", className),
    ...props
  }
));
AccordionItem.displayName = "AccordionItem";
var AccordionTrigger = React44.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsx(AccordionPrimitive.Header, { className: "flex", children: /* @__PURE__ */ jsxs(
  AccordionPrimitive.Trigger,
  {
    ref,
    className: cn(
      "flex flex-1 items-center justify-between py-4 font-medium transition-all hover:underline [&[data-state=open]>svg]:rotate-180",
      className
    ),
    ...props,
    children: [
      children,
      /* @__PURE__ */ jsx(ChevronDown, { className: "h-4 w-4 shrink-0 transition-transform duration-200" })
    ]
  }
) }));
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;
var AccordionContent = React44.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsx(
  AccordionPrimitive.Content,
  {
    ref,
    className: "overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down",
    ...props,
    children: /* @__PURE__ */ jsx("div", { className: cn("pb-4 pt-0", className), children })
  }
));
AccordionContent.displayName = AccordionPrimitive.Content.displayName;
var Tabs = TabsPrimitive.Root;
var tabsListVariants = cva(
  "inline-flex items-center justify-center text-muted-foreground",
  {
    variants: {
      variant: {
        underline: "h-10 w-full border-b border-border bg-transparent p-0",
        pills: "h-10 gap-1 rounded-md bg-muted p-1"
      }
    },
    defaultVariants: {
      variant: "underline"
    }
  }
);
var TabsList = React44.forwardRef(({ className, variant, ...props }, ref) => /* @__PURE__ */ jsx(
  TabsPrimitive.List,
  {
    ref,
    className: cn(tabsListVariants({ variant, className })),
    ...props
  }
));
TabsList.displayName = TabsPrimitive.List.displayName;
var tabsTriggerVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        underline: "relative h-10 rounded-none border-b-2 border-transparent bg-transparent pb-3 pt-2 data-[state=active]:border-primary data-[state=active]:text-foreground",
        pills: "rounded-sm data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
      }
    },
    defaultVariants: {
      variant: "underline"
    }
  }
);
var TabsTrigger = React44.forwardRef(({ className, variant, ...props }, ref) => /* @__PURE__ */ jsx(
  TabsPrimitive.Trigger,
  {
    ref,
    className: cn(tabsTriggerVariants({ variant, className })),
    ...props
  }
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;
var TabsContent = React44.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  TabsPrimitive.Content,
  {
    ref,
    className: cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    ),
    ...props
  }
));
TabsContent.displayName = TabsPrimitive.Content.displayName;
var Card2 = React44.forwardRef(({ className, hoverable = false, padding = "md", ...props }, ref) => {
  const paddingStyles = {
    none: "",
    sm: "p-4",
    md: "p-6",
    lg: "p-8"
  };
  return /* @__PURE__ */ jsx(
    "div",
    {
      ref,
      className: cn(
        "rounded-lg border bg-card text-card-foreground shadow-sm",
        hoverable && "transition-shadow hover:shadow-md focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
        paddingStyles[padding],
        className
      ),
      ...props
    }
  );
});
Card2.displayName = "Card";
var CardHeader2 = React44.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "div",
  {
    ref,
    className: cn("flex flex-col space-y-1.5 p-6", className),
    ...props
  }
));
CardHeader2.displayName = "CardHeader";
var CardTitle = React44.forwardRef(({ className, as: Component = "h3", ...props }, ref) => /* @__PURE__ */ jsx(
  Component,
  {
    ref,
    className: cn(
      "text-2xl font-semibold leading-none tracking-tight",
      className
    ),
    ...props
  }
));
CardTitle.displayName = "CardTitle";
var CardDescription = React44.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "p",
  {
    ref,
    className: cn("text-sm text-muted-foreground", className),
    ...props
  }
));
CardDescription.displayName = "CardDescription";
var CardContent = React44.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx("div", { ref, className: cn("p-6 pt-0", className), ...props }));
CardContent.displayName = "CardContent";
var CardFooter2 = React44.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "div",
  {
    ref,
    className: cn("flex items-center p-6 pt-0", className),
    ...props
  }
));
CardFooter2.displayName = "CardFooter";
var FormFieldContext = React44.createContext(null);
var FormItemContext = React44.createContext(null);
function useFormField() {
  const fieldContext = React44.useContext(FormFieldContext);
  const itemContext = React44.useContext(FormItemContext);
  if (!itemContext) {
    throw new Error("useFormField must be used within a FormItem");
  }
  const { id } = itemContext;
  return {
    id,
    name: fieldContext?.name ?? "",
    formItemId: fieldContext?.formItemId ?? `${id}-form-item`,
    formDescriptionId: fieldContext?.formDescriptionId ?? `${id}-form-description`,
    formMessageId: fieldContext?.formMessageId ?? `${id}-form-message`,
    error: fieldContext?.error
  };
}
var Form2 = React44.forwardRef(
  ({ className, error, onSubmit, children, ...props }, ref) => {
    const handleSubmit = (event) => {
      event.preventDefault();
      onSubmit?.(event);
    };
    return /* @__PURE__ */ jsxs(
      "form",
      {
        ref,
        className: cn("space-y-6", className),
        onSubmit: handleSubmit,
        noValidate: true,
        ...props,
        children: [
          error && /* @__PURE__ */ jsx(
            "div",
            {
              className: "rounded-md bg-destructive/15 p-4 text-sm text-destructive",
              role: "alert",
              "aria-live": "assertive",
              children: /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
                /* @__PURE__ */ jsx(
                  "svg",
                  {
                    className: "h-4 w-4 flex-shrink-0",
                    fill: "currentColor",
                    viewBox: "0 0 20 20",
                    "aria-hidden": "true",
                    children: /* @__PURE__ */ jsx(
                      "path",
                      {
                        fillRule: "evenodd",
                        d: "M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z",
                        clipRule: "evenodd"
                      }
                    )
                  }
                ),
                error
              ] })
            }
          ),
          children
        ]
      }
    );
  }
);
Form2.displayName = "Form";
var FormField = React44.forwardRef(
  ({ name, error, children }, _ref) => {
    const id = React44.useId();
    const contextValue = React44.useMemo(
      () => ({
        id,
        name,
        error,
        formItemId: `${id}-form-item`,
        formDescriptionId: `${id}-form-description`,
        formMessageId: `${id}-form-message`
      }),
      [id, name, error]
    );
    return /* @__PURE__ */ jsx(FormFieldContext.Provider, { value: contextValue, children });
  }
);
FormField.displayName = "FormField";
var FormItem = React44.forwardRef(
  ({ className, ...props }, ref) => {
    const id = React44.useId();
    return /* @__PURE__ */ jsx(FormItemContext.Provider, { value: { id }, children: /* @__PURE__ */ jsx("div", { ref, className: cn("space-y-2", className), ...props }) });
  }
);
FormItem.displayName = "FormItem";
var FormLabel = React44.forwardRef(({ className, required, ...props }, ref) => {
  const { error, formItemId } = useFormField();
  return /* @__PURE__ */ jsx(
    Label,
    {
      ref,
      className: cn(error && "text-destructive", className),
      htmlFor: formItemId,
      required,
      ...props
    }
  );
});
FormLabel.displayName = "FormLabel";
var FormControl = React44.forwardRef(
  ({ asChild = true, ...props }, ref) => {
    const { error, formItemId, formDescriptionId, formMessageId } = useFormField();
    const Comp = asChild ? Slot : "div";
    return /* @__PURE__ */ jsx(
      Comp,
      {
        ref,
        id: formItemId,
        "aria-describedby": !error ? formDescriptionId : `${formDescriptionId} ${formMessageId}`,
        "aria-invalid": !!error,
        ...props
      }
    );
  }
);
FormControl.displayName = "FormControl";
var FormDescription = React44.forwardRef(({ className, ...props }, ref) => {
  const { formDescriptionId } = useFormField();
  return /* @__PURE__ */ jsx(
    "p",
    {
      ref,
      id: formDescriptionId,
      className: cn("text-sm text-muted-foreground", className),
      ...props
    }
  );
});
FormDescription.displayName = "FormDescription";
var FormMessage = React44.forwardRef(
  ({ className, children, ...props }, ref) => {
    const { error, formMessageId } = useFormField();
    const message = children ?? error;
    if (!message) {
      return null;
    }
    return /* @__PURE__ */ jsx(
      "p",
      {
        ref,
        id: formMessageId,
        className: cn("text-sm font-medium text-destructive", className),
        role: "alert",
        "aria-live": "polite",
        ...props,
        children: message
      }
    );
  }
);
FormMessage.displayName = "FormMessage";
var Dialog = DialogPrimitive.Root;
var DialogTrigger = DialogPrimitive.Trigger;
var DialogPortal = DialogPrimitive.Portal;
var DialogClose = DialogPrimitive.Close;
var DialogOverlay = React44.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DialogPrimitive.Overlay,
  {
    ref,
    className: cn(
      "fixed inset-0 z-50 bg-black/80 backdrop-blur-sm",
      "data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out",
      className
    ),
    ...props
  }
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;
var DialogContent = React44.forwardRef(({ className, children, showCloseButton = true, ...props }, ref) => /* @__PURE__ */ jsxs(DialogPortal, { children: [
  /* @__PURE__ */ jsx(DialogOverlay, {}),
  /* @__PURE__ */ jsxs(
    DialogPrimitive.Content,
    {
      ref,
      className: cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200",
        "data-[state=open]:animate-zoom-in data-[state=closed]:animate-zoom-out",
        "sm:rounded-lg",
        className
      ),
      ...props,
      children: [
        children,
        showCloseButton && /* @__PURE__ */ jsxs(
          DialogPrimitive.Close,
          {
            className: cn(
              "absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity",
              "hover:opacity-100",
              "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
              "disabled:pointer-events-none",
              "data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
            ),
            "aria-label": "Close dialog",
            children: [
              /* @__PURE__ */ jsx(X, { className: "h-4 w-4" }),
              /* @__PURE__ */ jsx("span", { className: "sr-only", children: "Close" })
            ]
          }
        )
      ]
    }
  )
] }));
DialogContent.displayName = DialogPrimitive.Content.displayName;
var DialogHeader = ({ className, ...props }) => /* @__PURE__ */ jsx(
  "div",
  {
    className: cn("flex flex-col space-y-1.5 text-center sm:text-left", className),
    ...props
  }
);
DialogHeader.displayName = "DialogHeader";
var DialogFooter = ({ className, ...props }) => /* @__PURE__ */ jsx(
  "div",
  {
    className: cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    ),
    ...props
  }
);
DialogFooter.displayName = "DialogFooter";
var DialogTitle = React44.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DialogPrimitive.Title,
  {
    ref,
    className: cn("text-lg font-semibold leading-none tracking-tight", className),
    ...props
  }
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;
var DialogDescription = React44.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DialogPrimitive.Description,
  {
    ref,
    className: cn("text-sm text-muted-foreground", className),
    ...props
  }
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;
var AlertDialog = AlertDialogPrimitive.Root;
var AlertDialogTrigger = AlertDialogPrimitive.Trigger;
var AlertDialogPortal = AlertDialogPrimitive.Portal;
var AlertDialogOverlay = React44.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  AlertDialogPrimitive.Overlay,
  {
    className: cn(
      "fixed inset-0 z-50 bg-black/80 backdrop-blur-sm",
      "data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out",
      className
    ),
    ...props,
    ref
  }
));
AlertDialogOverlay.displayName = AlertDialogPrimitive.Overlay.displayName;
var AlertDialogContent = React44.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsxs(AlertDialogPortal, { children: [
  /* @__PURE__ */ jsx(AlertDialogOverlay, {}),
  /* @__PURE__ */ jsx(
    AlertDialogPrimitive.Content,
    {
      ref,
      className: cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200",
        "data-[state=open]:animate-zoom-in data-[state=closed]:animate-zoom-out",
        "sm:rounded-lg",
        className
      ),
      ...props
    }
  )
] }));
AlertDialogContent.displayName = AlertDialogPrimitive.Content.displayName;
var AlertDialogHeader = ({ className, ...props }) => /* @__PURE__ */ jsx(
  "div",
  {
    className: cn("flex flex-col space-y-2 text-center sm:text-left", className),
    ...props
  }
);
AlertDialogHeader.displayName = "AlertDialogHeader";
var AlertDialogFooter = ({ className, ...props }) => /* @__PURE__ */ jsx(
  "div",
  {
    className: cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    ),
    ...props
  }
);
AlertDialogFooter.displayName = "AlertDialogFooter";
var AlertDialogTitle = React44.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  AlertDialogPrimitive.Title,
  {
    ref,
    className: cn("text-lg font-semibold", className),
    ...props
  }
));
AlertDialogTitle.displayName = AlertDialogPrimitive.Title.displayName;
var AlertDialogDescription = React44.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  AlertDialogPrimitive.Description,
  {
    ref,
    className: cn("text-sm text-muted-foreground", className),
    ...props
  }
));
AlertDialogDescription.displayName = AlertDialogPrimitive.Description.displayName;
var AlertDialogAction = React44.forwardRef(({ className, destructive = false, ...props }, ref) => /* @__PURE__ */ jsx(
  AlertDialogPrimitive.Action,
  {
    ref,
    className: cn(
      buttonVariants({ variant: destructive ? "destructive" : "default" }),
      className
    ),
    ...props
  }
));
AlertDialogAction.displayName = AlertDialogPrimitive.Action.displayName;
var AlertDialogCancel = React44.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  AlertDialogPrimitive.Cancel,
  {
    ref,
    className: cn(
      buttonVariants({ variant: "outline" }),
      "mt-2 sm:mt-0",
      className
    ),
    ...props
  }
));
AlertDialogCancel.displayName = AlertDialogPrimitive.Cancel.displayName;
var ToastProvider = ToastPrimitives.Provider;
var ToastViewport = React44.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  ToastPrimitives.Viewport,
  {
    ref,
    className: cn(
      "fixed top-0 z-[100] flex max-h-screen w-full flex-col-reverse p-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[420px]",
      className
    ),
    ...props
  }
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;
var toastVariants = cva(
  "group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-md border p-6 pr-8 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-slide-in-from-top data-[state=closed]:animate-fade-out sm:data-[state=open]:animate-slide-in-from-bottom",
  {
    variants: {
      variant: {
        default: "border bg-background text-foreground",
        success: "border-green-200 bg-green-50 text-green-900   ",
        error: "destructive group border-destructive bg-destructive text-destructive-foreground",
        warning: "border-yellow-200 bg-yellow-50 text-yellow-900   "
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
var Toast2 = React44.forwardRef(({ className, variant, ...props }, ref) => {
  return /* @__PURE__ */ jsx(
    ToastPrimitives.Root,
    {
      ref,
      className: cn(toastVariants({ variant }), className),
      ...props
    }
  );
});
Toast2.displayName = ToastPrimitives.Root.displayName;
var ToastAction = React44.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  ToastPrimitives.Action,
  {
    ref,
    className: cn(
      "inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium ring-offset-background transition-colors",
      "hover:bg-secondary",
      "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
      "disabled:pointer-events-none disabled:opacity-50",
      "group-[.destructive]:border-muted/40 group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.destructive]:hover:text-destructive-foreground group-[.destructive]:focus:ring-destructive",
      className
    ),
    ...props
  }
));
ToastAction.displayName = ToastPrimitives.Action.displayName;
var ToastClose = React44.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  ToastPrimitives.Close,
  {
    ref,
    className: cn(
      "absolute right-2 top-2 rounded-md p-1 text-foreground/50 opacity-0 transition-opacity",
      "hover:text-foreground",
      "focus:opacity-100 focus:outline-none focus:ring-2",
      "group-hover:opacity-100",
      "group-[.destructive]:text-red-300 group-[.destructive]:hover:text-red-50 group-[.destructive]:focus:ring-red-400 group-[.destructive]:focus:ring-offset-red-600",
      className
    ),
    "toast-close": "",
    "aria-label": "Close notification",
    ...props,
    children: /* @__PURE__ */ jsx(X, { className: "h-4 w-4" })
  }
));
ToastClose.displayName = ToastPrimitives.Close.displayName;
var ToastTitle = React44.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  ToastPrimitives.Title,
  {
    ref,
    className: cn("text-sm font-semibold", className),
    ...props
  }
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;
var ToastDescription = React44.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  ToastPrimitives.Description,
  {
    ref,
    className: cn("text-sm opacity-90", className),
    ...props
  }
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;
var LoginForm = React44.forwardRef(
  ({
    onSubmit,
    defaultEmail = "",
    error,
    loading = false,
    onForgotPassword,
    forgotPasswordHref,
    onSignUp,
    signUpHref,
    className,
    action,
    method = "post",
    children
  }, ref) => {
    const [email, setEmail] = React44.useState(defaultEmail);
    const [password, setPassword] = React44.useState("");
    const [rememberMe, setRememberMe] = React44.useState(false);
    const handleSubmit = async (e) => {
      if (onSubmit) {
        e.preventDefault();
        await onSubmit({ email, password, rememberMe });
      }
    };
    const handleForgotPasswordClick = (e) => {
      if (onForgotPassword) {
        e.preventDefault();
        onForgotPassword();
      }
    };
    const handleSignUpClick = (e) => {
      if (onSignUp) {
        e.preventDefault();
        onSignUp();
      }
    };
    return /* @__PURE__ */ jsxs(
      "form",
      {
        ref,
        className: cn("space-y-6", className),
        onSubmit: handleSubmit,
        action,
        method,
        children: [
          children,
          error && /* @__PURE__ */ jsx(
            "div",
            {
              className: "rounded-md border border-destructive/50 bg-destructive/10 p-4 text-center",
              role: "alert",
              "aria-live": "polite",
              children: /* @__PURE__ */ jsx("p", { className: "text-sm text-destructive", children: error })
            }
          ),
          /* @__PURE__ */ jsx(
            Field,
            {
              label: "Email",
              name: "email",
              type: "email",
              autoComplete: "email",
              value: email,
              onChange: (e) => setEmail(e.target.value),
              required: true,
              disabled: loading
            }
          ),
          /* @__PURE__ */ jsx(
            Field,
            {
              label: "Password",
              name: "password",
              type: "password",
              autoComplete: "current-password",
              value: password,
              onChange: (e) => setPassword(e.target.value),
              required: true,
              disabled: loading
            }
          ),
          /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
              /* @__PURE__ */ jsx(
                Checkbox,
                {
                  id: "rememberMe",
                  name: "rememberMe",
                  checked: rememberMe,
                  onCheckedChange: (checked) => setRememberMe(checked === true),
                  disabled: loading
                }
              ),
              /* @__PURE__ */ jsx(
                Label,
                {
                  htmlFor: "rememberMe",
                  className: "text-sm font-normal text-muted-foreground cursor-pointer",
                  children: "Remember me"
                }
              )
            ] }),
            /* @__PURE__ */ jsx(
              "a",
              {
                href: forgotPasswordHref || "#",
                onClick: handleForgotPasswordClick,
                className: "text-sm font-medium text-primary hover:underline underline-offset-4",
                children: "Forgot password?"
              }
            )
          ] }),
          rememberMe && /* @__PURE__ */ jsx("input", { type: "hidden", name: "rememberMe", value: "on" }),
          /* @__PURE__ */ jsx(
            Button,
            {
              type: "submit",
              className: "w-full",
              size: "lg",
              loading,
              disabled: loading,
              children: "Sign in"
            }
          ),
          /* @__PURE__ */ jsxs("p", { className: "text-center text-sm text-muted-foreground", children: [
            "Don't have an account?",
            " ",
            /* @__PURE__ */ jsx(
              "a",
              {
                href: signUpHref || "#",
                onClick: handleSignUpClick,
                className: "font-medium text-primary hover:underline underline-offset-4",
                children: "Sign up"
              }
            )
          ] })
        ]
      }
    );
  }
);
LoginForm.displayName = "LoginForm";
var MFAForm = React44.forwardRef(
  ({
    onSubmit,
    error,
    loading = false,
    otpLength = 6,
    title = "OTP Verification",
    description = "Enter the 6-digit code from your authenticator app.",
    onResendCode,
    canResend = true,
    resendCountdown,
    className,
    action,
    method = "post",
    autoFocus = true,
    showUserId = false,
    userIdLabel = "User ID (recovery code only)",
    showTrustDevice = false,
    trustDeviceLabel = "Trust this device",
    children
  }, ref) => {
    const [code, setCode] = React44.useState("");
    const [userId, setUserId] = React44.useState("");
    const [trustDevice, setTrustDevice] = React44.useState(false);
    const otpRef = React44.useRef(null);
    const handleSubmit = async (e) => {
      if (onSubmit) {
        e.preventDefault();
        await onSubmit({
          code,
          userId: showUserId ? userId : void 0,
          trustDevice: showTrustDevice ? trustDevice : void 0
        });
      }
    };
    const handleComplete = (completedCode) => {
      setCode(completedCode);
    };
    const handleChange = (value) => {
      setCode(value);
    };
    const handleResendClick = (e) => {
      e.preventDefault();
      onResendCode?.();
    };
    const isCodeComplete = code.length === otpLength;
    return /* @__PURE__ */ jsxs(
      "form",
      {
        ref,
        className: cn("space-y-6", className),
        onSubmit: handleSubmit,
        action,
        method,
        children: [
          children,
          /* @__PURE__ */ jsxs("div", { className: "text-center space-y-2", children: [
            /* @__PURE__ */ jsx("h2", { className: "text-xl font-semibold text-foreground", children: title }),
            /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: description })
          ] }),
          error && /* @__PURE__ */ jsx(
            "div",
            {
              className: "rounded-md border border-destructive/50 bg-destructive/10 p-4 text-center",
              role: "alert",
              "aria-live": "polite",
              children: /* @__PURE__ */ jsx("p", { className: "text-sm text-destructive", children: error })
            }
          ),
          /* @__PURE__ */ jsx("div", { className: "flex justify-center", children: /* @__PURE__ */ jsx(
            InputOTP,
            {
              ref: otpRef,
              length: otpLength,
              onComplete: handleComplete,
              onChange: handleChange,
              value: code,
              error: Boolean(error),
              disabled: loading,
              autoFocus,
              "aria-label": "One-time password"
            }
          ) }),
          /* @__PURE__ */ jsx("input", { type: "hidden", name: "code", value: code }),
          showUserId && /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
            /* @__PURE__ */ jsx(Label, { htmlFor: "userId", children: userIdLabel }),
            /* @__PURE__ */ jsx(
              "input",
              {
                id: "userId",
                name: "userId",
                type: "text",
                placeholder: "Optional",
                value: userId,
                onChange: (e) => setUserId(e.target.value),
                disabled: loading,
                className: "w-full px-3 py-2 border border-input rounded-md bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
              }
            )
          ] }),
          showTrustDevice && /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
            /* @__PURE__ */ jsx(
              Checkbox,
              {
                id: "trustDevice",
                name: "trustDevice",
                checked: trustDevice,
                onCheckedChange: (checked) => setTrustDevice(checked === true),
                disabled: loading
              }
            ),
            /* @__PURE__ */ jsx(
              Label,
              {
                htmlFor: "trustDevice",
                className: "text-sm font-normal text-muted-foreground cursor-pointer",
                children: trustDeviceLabel
              }
            )
          ] }),
          showTrustDevice && trustDevice && /* @__PURE__ */ jsx("input", { type: "hidden", name: "trustDevice", value: "on" }),
          /* @__PURE__ */ jsx(
            Button,
            {
              type: "submit",
              className: "w-full",
              size: "lg",
              loading,
              disabled: loading || !isCodeComplete,
              children: "Verify"
            }
          ),
          onResendCode && /* @__PURE__ */ jsx("div", { className: "text-center", children: resendCountdown && resendCountdown > 0 ? /* @__PURE__ */ jsxs("p", { className: "text-sm text-muted-foreground", children: [
            "Resend code in ",
            resendCountdown,
            "s"
          ] }) : /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              onClick: handleResendClick,
              disabled: !canResend || loading,
              className: "text-sm font-medium text-primary hover:underline underline-offset-4 disabled:opacity-50 disabled:cursor-not-allowed",
              children: "Resend code"
            }
          ) })
        ]
      }
    );
  }
);
MFAForm.displayName = "MFAForm";
var SignUpForm = React44.forwardRef(
  ({
    onSubmit,
    error,
    fieldErrors = {},
    loading = false,
    onSignIn,
    signInHref,
    termsHref = "#",
    privacyHref = "#",
    className,
    action,
    method = "post"
  }, ref) => {
    const [firstName, setFirstName] = React44.useState("");
    const [lastName, setLastName] = React44.useState("");
    const [email, setEmail] = React44.useState("");
    const [password, setPassword] = React44.useState("");
    const [confirmPassword, setConfirmPassword] = React44.useState("");
    const [acceptTerms, setAcceptTerms] = React44.useState(false);
    const [localErrors, setLocalErrors] = React44.useState({});
    const validateForm = () => {
      const errors = {};
      if (password !== confirmPassword) {
        errors.confirmPassword = "Passwords do not match";
      }
      if (password.length < 8) {
        errors.password = "Password must be at least 8 characters";
      }
      if (!acceptTerms) {
        errors.acceptTerms = "You must accept the terms of service";
      }
      setLocalErrors(errors);
      return Object.keys(errors).length === 0;
    };
    const handleSubmit = async (e) => {
      if (onSubmit) {
        e.preventDefault();
        if (!validateForm()) {
          return;
        }
        await onSubmit({
          firstName,
          lastName,
          email,
          password,
          confirmPassword,
          acceptTerms
        });
      }
    };
    const handleSignInClick = (e) => {
      if (onSignIn) {
        e.preventDefault();
        onSignIn();
      }
    };
    const allErrors = { ...localErrors, ...fieldErrors };
    return /* @__PURE__ */ jsxs(
      "form",
      {
        ref,
        className: cn("space-y-6", className),
        onSubmit: handleSubmit,
        action,
        method,
        children: [
          error && /* @__PURE__ */ jsx(
            "div",
            {
              className: "rounded-md border border-destructive/50 bg-destructive/10 p-4 text-center",
              role: "alert",
              "aria-live": "polite",
              children: /* @__PURE__ */ jsx("p", { className: "text-sm text-destructive", children: error })
            }
          ),
          /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-2 gap-4", children: [
            /* @__PURE__ */ jsx(
              Field,
              {
                label: "First name",
                name: "firstName",
                type: "text",
                autoComplete: "given-name",
                value: firstName,
                onChange: (e) => setFirstName(e.target.value),
                errorMessage: allErrors.firstName,
                required: true,
                disabled: loading
              }
            ),
            /* @__PURE__ */ jsx(
              Field,
              {
                label: "Last name",
                name: "lastName",
                type: "text",
                autoComplete: "family-name",
                value: lastName,
                onChange: (e) => setLastName(e.target.value),
                errorMessage: allErrors.lastName,
                required: true,
                disabled: loading
              }
            )
          ] }),
          /* @__PURE__ */ jsx(
            Field,
            {
              label: "Email",
              name: "email",
              type: "email",
              autoComplete: "email",
              value: email,
              onChange: (e) => setEmail(e.target.value),
              errorMessage: allErrors.email,
              required: true,
              disabled: loading
            }
          ),
          /* @__PURE__ */ jsx(
            Field,
            {
              label: "Password",
              name: "password",
              type: "password",
              autoComplete: "new-password",
              value: password,
              onChange: (e) => {
                setPassword(e.target.value);
                setLocalErrors((prev) => ({ ...prev, password: void 0 }));
              },
              errorMessage: allErrors.password,
              helperText: "Must be at least 8 characters",
              required: true,
              disabled: loading
            }
          ),
          /* @__PURE__ */ jsx(
            Field,
            {
              label: "Confirm password",
              name: "confirmPassword",
              type: "password",
              autoComplete: "new-password",
              value: confirmPassword,
              onChange: (e) => {
                setConfirmPassword(e.target.value);
                setLocalErrors((prev) => ({ ...prev, confirmPassword: void 0 }));
              },
              errorMessage: allErrors.confirmPassword,
              required: true,
              disabled: loading
            }
          ),
          /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
            /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-2", children: [
              /* @__PURE__ */ jsx(
                Checkbox,
                {
                  id: "acceptTerms",
                  name: "acceptTerms",
                  checked: acceptTerms,
                  onCheckedChange: (checked) => {
                    setAcceptTerms(checked === true);
                    setLocalErrors((prev) => ({ ...prev, acceptTerms: void 0 }));
                  },
                  disabled: loading,
                  className: "mt-0.5"
                }
              ),
              /* @__PURE__ */ jsxs(
                Label,
                {
                  htmlFor: "acceptTerms",
                  className: "text-sm font-normal text-muted-foreground cursor-pointer leading-snug",
                  children: [
                    "I agree to the",
                    " ",
                    /* @__PURE__ */ jsx(
                      "a",
                      {
                        href: termsHref,
                        className: "font-medium text-primary hover:underline underline-offset-4",
                        target: "_blank",
                        rel: "noopener noreferrer",
                        children: "Terms of Service"
                      }
                    ),
                    " ",
                    "and",
                    " ",
                    /* @__PURE__ */ jsx(
                      "a",
                      {
                        href: privacyHref,
                        className: "font-medium text-primary hover:underline underline-offset-4",
                        target: "_blank",
                        rel: "noopener noreferrer",
                        children: "Privacy Policy"
                      }
                    )
                  ]
                }
              )
            ] }),
            allErrors.acceptTerms && /* @__PURE__ */ jsx("p", { className: "text-sm text-destructive", role: "alert", children: allErrors.acceptTerms })
          ] }),
          acceptTerms && /* @__PURE__ */ jsx("input", { type: "hidden", name: "acceptTerms", value: "on" }),
          /* @__PURE__ */ jsx(
            Button,
            {
              type: "submit",
              className: "w-full",
              size: "lg",
              loading,
              disabled: loading,
              children: "Sign up"
            }
          ),
          /* @__PURE__ */ jsxs("p", { className: "text-center text-sm text-muted-foreground", children: [
            "Already have an account?",
            " ",
            /* @__PURE__ */ jsx(
              "a",
              {
                href: signInHref || "#",
                onClick: handleSignInClick,
                className: "font-medium text-primary hover:underline underline-offset-4",
                children: "Sign in"
              }
            )
          ] })
        ]
      }
    );
  }
);
SignUpForm.displayName = "SignUpForm";
function MFASetup({
  open,
  onOpenChange,
  secret,
  qrCodeValue,
  onSubmit,
  onCancel,
  error,
  loading = false,
  qrCodeSize = 180,
  title = "Enable multi-factor authentication",
  description = "Each time you log in, you will use an authenticator app to generate a one-off code.",
  className
}) {
  const [password, setPassword] = React44.useState("");
  const [authCode, setAuthCode] = React44.useState("");
  const [copiedText, copy] = useCopyToClipboard();
  const handleSubmit = async (e) => {
    e.preventDefault();
    await onSubmit?.({ password, authCode });
  };
  const handleCancel = () => {
    onCancel?.();
    onOpenChange(false);
  };
  const handleCopySecret = async () => {
    await copy(secret);
  };
  const isCopied = copiedText === secret;
  const isFormValid = password.length > 0 && authCode.length >= 6;
  React44.useEffect(() => {
    if (!open) {
      setPassword("");
      setAuthCode("");
    }
  }, [open]);
  return /* @__PURE__ */ jsx(Dialog, { open, onOpenChange, children: /* @__PURE__ */ jsx(DialogContent, { className: cn("sm:max-w-md", className), showCloseButton: false, children: /* @__PURE__ */ jsxs("form", { onSubmit: handleSubmit, children: [
    /* @__PURE__ */ jsxs(DialogHeader, { children: [
      /* @__PURE__ */ jsx(DialogTitle, { children: title }),
      /* @__PURE__ */ jsx(DialogDescription, { children: description })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "space-y-6 py-4", children: [
      error && /* @__PURE__ */ jsx(
        "div",
        {
          className: "rounded-md border border-destructive/50 bg-destructive/10 p-3 text-center",
          role: "alert",
          "aria-live": "polite",
          children: /* @__PURE__ */ jsx("p", { className: "text-sm text-destructive", children: error })
        }
      ),
      /* @__PURE__ */ jsxs("div", { className: "space-y-3", children: [
        /* @__PURE__ */ jsx(Label, { className: "text-base font-semibold", children: "Step 1: Scan the QR code with your authenticator app" }),
        /* @__PURE__ */ jsx("div", { className: "flex justify-center", children: /* @__PURE__ */ jsx("div", { className: "rounded-lg border border-border bg-white p-2", children: /* @__PURE__ */ jsx(QRCode, { value: qrCodeValue, size: qrCodeSize }) }) }),
        /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
          /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: "If you can't scan the QR code enter this code in your authenticator app:" }),
          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
            /* @__PURE__ */ jsx("code", { className: "flex-1 rounded-md bg-muted px-3 py-2 text-sm font-mono break-all", children: secret }),
            /* @__PURE__ */ jsx(
              Button,
              {
                type: "button",
                variant: "outline",
                size: "icon",
                onClick: handleCopySecret,
                "aria-label": isCopied ? "Copied" : "Copy secret key",
                children: isCopied ? /* @__PURE__ */ jsx(Check, { className: "h-4 w-4 text-green-600" }) : /* @__PURE__ */ jsx(Copy, { className: "h-4 w-4" })
              }
            )
          ] })
        ] })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
        /* @__PURE__ */ jsx(Label, { htmlFor: "mfa-password", className: "text-base font-semibold", children: "Step 2: Enter password" }),
        /* @__PURE__ */ jsx(
          "input",
          {
            id: "mfa-password",
            name: "password",
            type: "password",
            autoComplete: "current-password",
            value: password,
            onChange: (e) => setPassword(e.target.value),
            disabled: loading,
            required: true,
            className: "w-full px-3 py-2 border border-input rounded-md bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
          }
        )
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
        /* @__PURE__ */ jsx(Label, { htmlFor: "mfa-code", className: "text-base font-semibold", children: "Step 3: Enter authentication code" }),
        /* @__PURE__ */ jsx(
          "input",
          {
            id: "mfa-code",
            name: "authCode",
            type: "text",
            inputMode: "numeric",
            pattern: "[0-9]*",
            autoComplete: "one-time-code",
            value: authCode,
            onChange: (e) => {
              const value = e.target.value.replace(/\D/g, "");
              setAuthCode(value);
            },
            maxLength: 6,
            disabled: loading,
            required: true,
            className: "w-full px-3 py-2 border border-input rounded-md bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50",
            placeholder: "000000"
          }
        )
      ] })
    ] }),
    /* @__PURE__ */ jsxs(DialogFooter, { className: "gap-2 sm:gap-0", children: [
      /* @__PURE__ */ jsx(
        Button,
        {
          type: "button",
          variant: "outline",
          onClick: handleCancel,
          disabled: loading,
          children: "Cancel"
        }
      ),
      /* @__PURE__ */ jsx(
        Button,
        {
          type: "submit",
          loading,
          disabled: loading || !isFormValid,
          children: "Enable"
        }
      )
    ] })
  ] }) }) });
}
MFASetup.displayName = "MFASetup";
function AuthLayout({
  logo,
  title,
  subtitle,
  children,
  footer,
  imageUrl,
  imageAlt = "Authentication",
  imageOverlay,
  imagePanelClassName,
  imagePosition = "left",
  className,
  formPanelClassName
}) {
  const imagePanel = /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "hidden lg:flex lg:w-1/2 relative overflow-hidden",
        !imageUrl && "bg-gradient-to-br from-blue-600 to-blue-800",
        imagePanelClassName
      ),
      children: [
        imageUrl && /* @__PURE__ */ jsx(
          "img",
          {
            src: imageUrl,
            alt: imageAlt,
            className: "absolute inset-0 w-full h-full object-cover"
          }
        ),
        /* @__PURE__ */ jsx("div", { className: "absolute inset-0 bg-black/30" }),
        imageOverlay && /* @__PURE__ */ jsx("div", { className: "relative z-10 flex items-center justify-center p-12 text-white", children: imageOverlay })
      ]
    }
  );
  const formPanel = /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(
        "flex flex-1 lg:w-1/2 items-center justify-center p-4 sm:p-6 lg:p-8 bg-gray-50",
        formPanelClassName
      ),
      children: /* @__PURE__ */ jsxs("div", { className: "w-full max-w-md space-y-8", children: [
        /* @__PURE__ */ jsxs("div", { className: "text-center space-y-4", children: [
          logo && /* @__PURE__ */ jsx("div", { className: "flex justify-center", children: logo }),
          (title || subtitle) && /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
            title && /* @__PURE__ */ jsx("h1", { className: "text-2xl font-bold tracking-tight text-gray-900", children: title }),
            subtitle && /* @__PURE__ */ jsx("p", { className: "text-sm text-gray-600", children: subtitle })
          ] })
        ] }),
        /* @__PURE__ */ jsx("div", { className: "bg-white rounded-lg shadow-lg p-6 sm:p-8", children }),
        footer && /* @__PURE__ */ jsx("div", { className: "text-center text-sm text-gray-600", children: footer })
      ] })
    }
  );
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(
        "min-h-screen w-full flex flex-col lg:flex-row",
        className
      ),
      children: imagePosition === "left" ? /* @__PURE__ */ jsxs(Fragment, { children: [
        imagePanel,
        formPanel
      ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
        formPanel,
        imagePanel
      ] })
    }
  );
}
AuthLayout.displayName = "AuthLayout";
var SOCIAL_ICONS = {
  google: /* @__PURE__ */ jsxs("svg", { className: "h-5 w-5", viewBox: "0 0 24 24", "aria-hidden": "true", children: [
    /* @__PURE__ */ jsx(
      "path",
      {
        fill: "currentColor",
        d: "M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        fill: "currentColor",
        d: "M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        fill: "currentColor",
        d: "M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        fill: "currentColor",
        d: "M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      }
    )
  ] }),
  github: /* @__PURE__ */ jsx("svg", { className: "h-5 w-5", viewBox: "0 0 24 24", fill: "currentColor", "aria-hidden": "true", children: /* @__PURE__ */ jsx("path", { d: "M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" }) }),
  microsoft: /* @__PURE__ */ jsxs("svg", { className: "h-5 w-5", viewBox: "0 0 24 24", "aria-hidden": "true", children: [
    /* @__PURE__ */ jsx("path", { fill: "#F25022", d: "M1 1h10v10H1z" }),
    /* @__PURE__ */ jsx("path", { fill: "#00A4EF", d: "M1 13h10v10H1z" }),
    /* @__PURE__ */ jsx("path", { fill: "#7FBA00", d: "M13 1h10v10H13z" }),
    /* @__PURE__ */ jsx("path", { fill: "#FFB900", d: "M13 13h10v10H13z" })
  ] }),
  apple: /* @__PURE__ */ jsx("svg", { className: "h-5 w-5", viewBox: "0 0 24 24", fill: "currentColor", "aria-hidden": "true", children: /* @__PURE__ */ jsx("path", { d: "M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" }) })
};
var SOCIAL_LABELS = {
  google: "Continue with Google",
  github: "Continue with GitHub",
  microsoft: "Continue with Microsoft",
  apple: "Continue with Apple"
};
function AuthTemplate({
  logo,
  title = "Sign in to your account",
  subtitle,
  defaultEmail = "",
  defaultPassword = "",
  defaultRememberMe = false,
  errorMessage,
  emailError,
  passwordError,
  loading = false,
  disabled = false,
  submitText = "Sign in",
  onSubmit,
  onForgotPassword,
  forgotPasswordText = "Forgot password?",
  showRememberMe = true,
  rememberMeText = "Remember me",
  socialProviders,
  socialDividerText = "Or continue with",
  footer,
  className,
  imageUrl,
  imageAlt = "Sign in",
  imageOverlay,
  imagePanelClassName,
  imagePosition = "left"
}) {
  const [email, setEmail] = useState(defaultEmail);
  const [password, setPassword] = useState(defaultPassword);
  const [rememberMe, setRememberMe] = useState(defaultRememberMe);
  const formId = useId();
  const rememberMeId = `${formId}-remember-me`;
  const errorId = `${formId}-error`;
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit?.({ email, password, rememberMe });
  };
  const isDisabled = disabled || loading;
  const formContent = /* @__PURE__ */ jsxs("div", { className: "space-y-6", children: [
    errorMessage && /* @__PURE__ */ jsx(
      "div",
      {
        id: errorId,
        role: "alert",
        "aria-live": "assertive",
        className: "rounded-md bg-red-50 p-4",
        children: /* @__PURE__ */ jsxs("div", { className: "flex", children: [
          /* @__PURE__ */ jsx("div", { className: "flex-shrink-0", children: /* @__PURE__ */ jsx(
            "svg",
            {
              className: "h-5 w-5 text-red-400",
              viewBox: "0 0 20 20",
              fill: "currentColor",
              "aria-hidden": "true",
              children: /* @__PURE__ */ jsx(
                "path",
                {
                  fillRule: "evenodd",
                  d: "M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z",
                  clipRule: "evenodd"
                }
              )
            }
          ) }),
          /* @__PURE__ */ jsx("div", { className: "ml-3", children: /* @__PURE__ */ jsx("p", { className: "text-sm font-medium text-red-800", children: errorMessage }) })
        ] })
      }
    ),
    /* @__PURE__ */ jsxs("form", { onSubmit: handleSubmit, className: "space-y-5", children: [
      /* @__PURE__ */ jsx(
        Field,
        {
          label: "Email address",
          type: "email",
          placeholder: "name@example.com",
          value: email,
          onChange: (e) => setEmail(e.target.value),
          errorMessage: emailError,
          disabled: isDisabled,
          required: true,
          autoComplete: "email"
        }
      ),
      /* @__PURE__ */ jsx(
        Field,
        {
          label: "Password",
          type: "password",
          placeholder: "Enter your password",
          value: password,
          onChange: (e) => setPassword(e.target.value),
          errorMessage: passwordError,
          disabled: isDisabled,
          required: true,
          autoComplete: "current-password"
        }
      ),
      /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
        showRememberMe && /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx(
            Checkbox,
            {
              id: rememberMeId,
              checked: rememberMe,
              onCheckedChange: (checked) => setRememberMe(checked === true),
              disabled: isDisabled
            }
          ),
          /* @__PURE__ */ jsx(
            Label,
            {
              htmlFor: rememberMeId,
              className: "text-sm font-normal text-gray-600 cursor-pointer",
              children: rememberMeText
            }
          )
        ] }),
        onForgotPassword && /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            onClick: onForgotPassword,
            disabled: isDisabled,
            className: "text-sm font-medium text-blue-600 hover:text-blue-500 focus:outline-none focus:underline disabled:opacity-50 disabled:cursor-not-allowed",
            children: forgotPasswordText
          }
        )
      ] }),
      /* @__PURE__ */ jsx(
        Button,
        {
          type: "submit",
          className: "w-full",
          loading,
          disabled: isDisabled,
          children: submitText
        }
      )
    ] }),
    socialProviders && socialProviders.length > 0 && /* @__PURE__ */ jsxs("div", { className: "space-y-4", children: [
      /* @__PURE__ */ jsxs("div", { className: "relative", children: [
        /* @__PURE__ */ jsx("div", { className: "absolute inset-0 flex items-center", children: /* @__PURE__ */ jsx("div", { className: "w-full border-t border-gray-300" }) }),
        /* @__PURE__ */ jsx("div", { className: "relative flex justify-center text-sm", children: /* @__PURE__ */ jsx("span", { className: "bg-white px-2 text-gray-500", children: socialDividerText }) })
      ] }),
      /* @__PURE__ */ jsx("div", { className: "grid grid-cols-1 gap-3", children: socialProviders.map(
        ({
          provider,
          onClick,
          loading: providerLoading,
          disabled: providerDisabled
        }) => /* @__PURE__ */ jsxs(
          Button,
          {
            type: "button",
            variant: "outline",
            className: "w-full",
            onClick,
            loading: providerLoading,
            disabled: isDisabled || providerDisabled,
            children: [
              /* @__PURE__ */ jsx("span", { className: "mr-2", children: SOCIAL_ICONS[provider] }),
              SOCIAL_LABELS[provider]
            ]
          },
          provider
        )
      ) })
    ] })
  ] });
  return /* @__PURE__ */ jsx(
    AuthLayout,
    {
      logo,
      title,
      subtitle,
      footer,
      imageUrl,
      imageAlt,
      imageOverlay,
      imagePanelClassName,
      imagePosition,
      className,
      children: formContent
    }
  );
}
AuthTemplate.displayName = "AuthTemplate";
function SignupTemplate({
  logo,
  title = "Create your account",
  subtitle,
  defaultName = "",
  defaultEmail = "",
  defaultPassword = "",
  defaultConfirmPassword = "",
  defaultAcceptTerms = false,
  errorMessage,
  nameError,
  emailError,
  passwordError,
  confirmPasswordError,
  loading = false,
  disabled = false,
  submitText = "Create account",
  onSubmit,
  showTerms = true,
  termsContent,
  onTermsClick,
  onPrivacyClick,
  passwordHelperText = "Must be at least 8 characters with one number and one special character",
  footer,
  className,
  showNameField = true,
  nameLabel = "Full name",
  namePlaceholder = "Enter your full name",
  imageUrl,
  imageAlt = "Create account",
  imageOverlay,
  imagePanelClassName,
  imagePosition = "left"
}) {
  const [name, setName] = useState(defaultName);
  const [email, setEmail] = useState(defaultEmail);
  const [password, setPassword] = useState(defaultPassword);
  const [confirmPassword, setConfirmPassword] = useState(defaultConfirmPassword);
  const [acceptTerms, setAcceptTerms] = useState(defaultAcceptTerms);
  const formId = useId();
  const termsId = `${formId}-terms`;
  const errorId = `${formId}-error`;
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit?.({ name, email, password, confirmPassword, acceptTerms });
  };
  const isDisabled = disabled || loading;
  const defaultTermsContent = /* @__PURE__ */ jsxs("span", { className: "text-sm text-gray-600", children: [
    "I agree to the",
    " ",
    /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        onClick: onTermsClick,
        className: "font-medium text-blue-600 hover:text-blue-500 focus:outline-none focus:underline",
        children: "Terms of Service"
      }
    ),
    " ",
    "and",
    " ",
    /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        onClick: onPrivacyClick,
        className: "font-medium text-blue-600 hover:text-blue-500 focus:outline-none focus:underline",
        children: "Privacy Policy"
      }
    )
  ] });
  const formContent = /* @__PURE__ */ jsxs("div", { className: "space-y-6", children: [
    errorMessage && /* @__PURE__ */ jsx(
      "div",
      {
        id: errorId,
        role: "alert",
        "aria-live": "assertive",
        className: "rounded-md bg-red-50 p-4",
        children: /* @__PURE__ */ jsxs("div", { className: "flex", children: [
          /* @__PURE__ */ jsx("div", { className: "flex-shrink-0", children: /* @__PURE__ */ jsx(
            "svg",
            {
              className: "h-5 w-5 text-red-400",
              viewBox: "0 0 20 20",
              fill: "currentColor",
              "aria-hidden": "true",
              children: /* @__PURE__ */ jsx(
                "path",
                {
                  fillRule: "evenodd",
                  d: "M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z",
                  clipRule: "evenodd"
                }
              )
            }
          ) }),
          /* @__PURE__ */ jsx("div", { className: "ml-3", children: /* @__PURE__ */ jsx("p", { className: "text-sm font-medium text-red-800", children: errorMessage }) })
        ] })
      }
    ),
    /* @__PURE__ */ jsxs("form", { onSubmit: handleSubmit, className: "space-y-5", children: [
      showNameField && /* @__PURE__ */ jsx(
        Field,
        {
          label: nameLabel,
          type: "text",
          placeholder: namePlaceholder,
          value: name,
          onChange: (e) => setName(e.target.value),
          errorMessage: nameError,
          disabled: isDisabled,
          required: true,
          autoComplete: "name"
        }
      ),
      /* @__PURE__ */ jsx(
        Field,
        {
          label: "Email address",
          type: "email",
          placeholder: "name@example.com",
          value: email,
          onChange: (e) => setEmail(e.target.value),
          errorMessage: emailError,
          disabled: isDisabled,
          required: true,
          autoComplete: "email"
        }
      ),
      /* @__PURE__ */ jsx(
        Field,
        {
          label: "Password",
          type: "password",
          placeholder: "Create a password",
          value: password,
          onChange: (e) => setPassword(e.target.value),
          errorMessage: passwordError,
          helperText: !passwordError ? passwordHelperText : void 0,
          disabled: isDisabled,
          required: true,
          autoComplete: "new-password"
        }
      ),
      /* @__PURE__ */ jsx(
        Field,
        {
          label: "Confirm password",
          type: "password",
          placeholder: "Confirm your password",
          value: confirmPassword,
          onChange: (e) => setConfirmPassword(e.target.value),
          errorMessage: confirmPasswordError,
          disabled: isDisabled,
          required: true,
          autoComplete: "new-password"
        }
      ),
      showTerms && /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-2", children: [
        /* @__PURE__ */ jsx(
          Checkbox,
          {
            id: termsId,
            checked: acceptTerms,
            onCheckedChange: (checked) => setAcceptTerms(checked === true),
            disabled: isDisabled,
            className: "mt-0.5"
          }
        ),
        /* @__PURE__ */ jsx(
          Label,
          {
            htmlFor: termsId,
            className: "text-sm font-normal leading-relaxed cursor-pointer",
            children: termsContent || defaultTermsContent
          }
        )
      ] }),
      /* @__PURE__ */ jsx(
        Button,
        {
          type: "submit",
          className: "w-full",
          loading,
          disabled: isDisabled,
          children: submitText
        }
      )
    ] })
  ] });
  return /* @__PURE__ */ jsx(
    AuthLayout,
    {
      logo,
      title,
      subtitle,
      footer,
      imageUrl,
      imageAlt,
      imageOverlay,
      imagePanelClassName,
      imagePosition,
      className,
      children: formContent
    }
  );
}
SignupTemplate.displayName = "SignupTemplate";
function OtpTemplate({
  logo,
  title = "Verify your identity",
  subtitle,
  destination,
  otpLength = 6,
  defaultValue = "",
  errorMessage,
  loading = false,
  disabled = false,
  verifyText = "Verify",
  onSubmit,
  onChange,
  onComplete,
  onResendCode,
  resendLoading = false,
  resendCooldown = 60,
  resendText = "Resend code",
  resendCountdownText = (seconds) => `Resend code in ${seconds}s`,
  onBackToLogin,
  backToLoginText = "Back to login",
  autoSubmit = false,
  footer,
  className,
  autoFocus = true,
  imageUrl,
  imageAlt = "Verify identity",
  imageOverlay,
  imagePanelClassName,
  imagePosition = "left"
}) {
  const [otpValue, setOtpValue] = useState(defaultValue);
  const [countdown, setCountdown] = useState(resendCooldown);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const otpRef = useRef(null);
  const countdownRef = useRef(null);
  useEffect(() => {
    startCountdown();
    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, []);
  const startCountdown = useCallback(() => {
    setCountdown(resendCooldown);
    setIsResendDisabled(true);
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (countdownRef.current) {
            clearInterval(countdownRef.current);
          }
          setIsResendDisabled(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1e3);
  }, [resendCooldown]);
  const handleSubmit = (e) => {
    e.preventDefault();
    if (otpValue.length === otpLength) {
      onSubmit?.(otpValue);
    }
  };
  const handleOtpChange = (value) => {
    setOtpValue(value);
    onChange?.(value);
  };
  const handleOtpComplete = (code) => {
    onComplete?.(code);
    if (autoSubmit) {
      onSubmit?.(code);
    }
  };
  const handleResendCode = () => {
    onResendCode?.();
    startCountdown();
    otpRef.current?.clear();
  };
  const isDisabled = disabled || loading;
  const canSubmit = otpValue.length === otpLength && !isDisabled;
  const defaultSubtitle = destination ? `We've sent a ${otpLength}-digit verification code to ${destination}` : `Enter the ${otpLength}-digit code we sent you`;
  const formContent = /* @__PURE__ */ jsxs("div", { className: "space-y-6", children: [
    errorMessage && /* @__PURE__ */ jsx(
      "div",
      {
        role: "alert",
        "aria-live": "assertive",
        className: "rounded-md bg-red-50 p-4",
        children: /* @__PURE__ */ jsxs("div", { className: "flex", children: [
          /* @__PURE__ */ jsx("div", { className: "flex-shrink-0", children: /* @__PURE__ */ jsx(
            "svg",
            {
              className: "h-5 w-5 text-red-400",
              viewBox: "0 0 20 20",
              fill: "currentColor",
              "aria-hidden": "true",
              children: /* @__PURE__ */ jsx(
                "path",
                {
                  fillRule: "evenodd",
                  d: "M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z",
                  clipRule: "evenodd"
                }
              )
            }
          ) }),
          /* @__PURE__ */ jsx("div", { className: "ml-3", children: /* @__PURE__ */ jsx("p", { className: "text-sm font-medium text-red-800", children: errorMessage }) })
        ] })
      }
    ),
    /* @__PURE__ */ jsxs("form", { onSubmit: handleSubmit, className: "space-y-6", children: [
      /* @__PURE__ */ jsx("div", { className: "flex justify-center", children: /* @__PURE__ */ jsx(
        InputOTP,
        {
          ref: otpRef,
          length: otpLength,
          value: otpValue,
          onChange: handleOtpChange,
          onComplete: handleOtpComplete,
          error: Boolean(errorMessage),
          disabled: isDisabled,
          autoFocus,
          "aria-label": "Verification code"
        }
      ) }),
      /* @__PURE__ */ jsx(
        Button,
        {
          type: "submit",
          className: "w-full",
          loading,
          disabled: !canSubmit,
          children: verifyText
        }
      )
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "text-center space-y-3", children: [
      /* @__PURE__ */ jsxs("div", { className: "text-sm text-gray-600", children: [
        "Didn't receive the code?",
        " ",
        isResendDisabled ? /* @__PURE__ */ jsx("span", { className: "font-medium text-gray-500", children: resendCountdownText(countdown) }) : /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            onClick: handleResendCode,
            disabled: isResendDisabled || resendLoading,
            className: "font-medium text-blue-600 hover:text-blue-500 focus:outline-none focus:underline disabled:opacity-50 disabled:cursor-not-allowed",
            children: resendLoading ? "Sending..." : resendText
          }
        )
      ] }),
      onBackToLogin && /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          onClick: onBackToLogin,
          disabled: isDisabled,
          className: "text-sm font-medium text-gray-600 hover:text-gray-900 focus:outline-none focus:underline disabled:opacity-50 disabled:cursor-not-allowed",
          children: backToLoginText
        }
      )
    ] })
  ] });
  return /* @__PURE__ */ jsx(
    AuthLayout,
    {
      logo,
      title,
      subtitle: subtitle || defaultSubtitle,
      footer,
      imageUrl,
      imageAlt,
      imageOverlay,
      imagePanelClassName,
      imagePosition,
      className,
      children: formContent
    }
  );
}
OtpTemplate.displayName = "OtpTemplate";
function isNavigationGroup(item) {
  return "items" in item && Array.isArray(item.items);
}
var sidebarVariants = cva(
  "flex flex-col bg-cyan-900 border-r border-cyan-800 transition-all duration-300 ease-in-out",
  {
    variants: {
      collapsed: {
        true: "w-16",
        false: "w-64"
      }
    },
    defaultVariants: {
      collapsed: false
    }
  }
);
var navItemVariants = cva(
  "relative group flex items-center gap-3 rounded-l-lg rounded-r-none px-3 py-2 text-sm font-medium transition-colors w-full",
  {
    variants: {
      active: {
        true: "bg-[#EEEEF0] text-cyan-900",
        false: "text-slate-300 hover:bg-[#EEEEF0] hover:text-cyan-900"
      },
      disabled: {
        true: "pointer-events-none opacity-50",
        false: ""
      },
      collapsed: {
        true: "justify-center px-2",
        false: ""
      }
    },
    defaultVariants: {
      active: false,
      disabled: false,
      collapsed: false
    }
  }
);
var SidebarNavItem = React44.forwardRef(
  ({ item, collapsed = false, depth = 0 }, ref) => {
    const { label, icon, href, onClick, isActive, disabled, badge, children } = item;
    const [isExpanded, setIsExpanded] = React44.useState(false);
    const hasChildren = children && children.length > 0;
    const content = /* @__PURE__ */ jsxs(Fragment, { children: [
      isActive && !collapsed && /* @__PURE__ */ jsx("div", { className: "absolute right-0 top-0 bottom-0 w-[5px] rounded-l-sm bg-orange-500" }),
      icon && /* @__PURE__ */ jsx("span", { className: "flex h-5 w-5 shrink-0 items-center justify-center", children: icon }),
      !collapsed && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx("span", { className: "flex-1 truncate", children: label }),
        badge !== void 0 && /* @__PURE__ */ jsx("span", { className: "ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-cyan-700 px-1.5 text-xs font-medium text-cyan-50", children: badge }),
        hasChildren && /* @__PURE__ */ jsx(
          ChevronRight,
          {
            className: cn(
              "h-4 w-4 shrink-0 transition-transform",
              isExpanded && "rotate-90"
            )
          }
        )
      ] })
    ] });
    const itemClasses = navItemVariants({
      active: isActive,
      disabled,
      collapsed
    });
    const handleClick = () => {
      if (hasChildren) {
        setIsExpanded(!isExpanded);
      }
      onClick?.();
    };
    const itemElement = href ? /* @__PURE__ */ jsx(
      "a",
      {
        ref,
        href,
        className: cn(itemClasses, depth > 0 && !collapsed && "pl-10"),
        "aria-current": isActive ? "page" : void 0,
        "aria-disabled": disabled,
        onClick,
        children: content
      }
    ) : /* @__PURE__ */ jsx(
      "button",
      {
        ref,
        type: "button",
        className: cn(itemClasses, "w-full", depth > 0 && !collapsed && "pl-10"),
        "aria-current": isActive ? "page" : void 0,
        "aria-disabled": disabled,
        "aria-expanded": hasChildren ? isExpanded : void 0,
        disabled,
        onClick: handleClick,
        children: content
      }
    );
    if (collapsed && !hasChildren) {
      return /* @__PURE__ */ jsx(TooltipProvider, { children: /* @__PURE__ */ jsxs(Tooltip, { children: [
        /* @__PURE__ */ jsx(TooltipTrigger, { asChild: true, children: itemElement }),
        /* @__PURE__ */ jsx(TooltipContent, { side: "right", sideOffset: 8, children: label })
      ] }) });
    }
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      itemElement,
      hasChildren && isExpanded && !collapsed && /* @__PURE__ */ jsx("div", { className: "mt-1 space-y-1", children: children.map((child) => /* @__PURE__ */ jsx(
        SidebarNavItem,
        {
          item: child,
          collapsed,
          depth: depth + 1
        },
        child.id
      )) })
    ] });
  }
);
SidebarNavItem.displayName = "SidebarNavItem";
var SidebarNavGroup = ({ group, collapsed = false }) => {
  return /* @__PURE__ */ jsxs("div", { className: "space-y-1", children: [
    group.label && !collapsed && /* @__PURE__ */ jsx("h3", { className: "mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-slate-400", children: group.label }),
    collapsed && group.label && /* @__PURE__ */ jsx("div", { className: "mx-auto my-2 h-px w-8 bg-cyan-800", "aria-hidden": "true" }),
    group.items.map((item) => /* @__PURE__ */ jsx(SidebarNavItem, { item, collapsed }, item.id))
  ] });
};
var Sidebar = React44.forwardRef(
  ({
    navigation,
    collapsed = false,
    onCollapsedChange,
    showCollapseToggle = true,
    header,
    footer,
    className,
    navigationClassName,
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsxs(
      "aside",
      {
        ref,
        className: cn(sidebarVariants({ collapsed }), className),
        "aria-label": "Sidebar navigation",
        ...props,
        children: [
          header && /* @__PURE__ */ jsx(
            "div",
            {
              className: cn(
                "flex shrink-0 items-center border-b border-cyan-800 px-4 pt-4 pb-3",
                collapsed && "justify-center px-2"
              ),
              children: header
            }
          ),
          /* @__PURE__ */ jsx(
            "nav",
            {
              className: cn(
                "flex-1 space-y-4 overflow-y-auto py-4 pl-4 pr-0",
                collapsed && "px-2",
                navigationClassName
              ),
              children: navigation.map((item, index) => {
                if (isNavigationGroup(item)) {
                  return /* @__PURE__ */ jsx(
                    SidebarNavGroup,
                    {
                      group: item,
                      collapsed
                    },
                    item.label || `group-${index}`
                  );
                }
                return /* @__PURE__ */ jsx(SidebarNavItem, { item, collapsed }, item.id);
              })
            }
          ),
          footer && /* @__PURE__ */ jsx(
            "div",
            {
              className: cn(
                "shrink-0 border-t border-cyan-800 p-4",
                collapsed && "px-2"
              ),
              children: footer
            }
          ),
          showCollapseToggle && onCollapsedChange && /* @__PURE__ */ jsx(
            "div",
            {
              className: cn(
                "shrink-0 border-t border-cyan-800 p-2",
                collapsed && "flex justify-center"
              ),
              children: /* @__PURE__ */ jsx(
                Button,
                {
                  variant: "ghost",
                  size: "sm",
                  onClick: () => onCollapsedChange(!collapsed),
                  className: cn("w-full text-slate-300 hover:bg-cyan-800/50 hover:text-white", collapsed && "w-auto"),
                  "aria-label": collapsed ? "Expand sidebar" : "Collapse sidebar",
                  children: collapsed ? /* @__PURE__ */ jsx(ChevronRight, { className: "h-4 w-4" }) : /* @__PURE__ */ jsxs(Fragment, { children: [
                    /* @__PURE__ */ jsx(ChevronLeft, { className: "mr-2 h-4 w-4" }),
                    /* @__PURE__ */ jsx("span", { children: "Collapse" })
                  ] })
                }
              )
            }
          )
        ]
      }
    );
  }
);
Sidebar.displayName = "Sidebar";
var MobileSidebar = React44.forwardRef(
  ({
    navigation,
    open,
    onOpenChange,
    trigger,
    header,
    footer,
    className,
    navigationClassName,
    ...props
  }, ref) => {
    const defaultTrigger = /* @__PURE__ */ jsx(Button, { variant: "ghost", size: "icon", "aria-label": "Open menu", children: /* @__PURE__ */ jsx(Menu, { className: "h-5 w-5" }) });
    return /* @__PURE__ */ jsxs(DialogPrimitive.Root, { open, onOpenChange, children: [
      /* @__PURE__ */ jsx(DialogPrimitive.Trigger, { asChild: true, children: trigger || defaultTrigger }),
      /* @__PURE__ */ jsxs(DialogPrimitive.Portal, { children: [
        /* @__PURE__ */ jsx(DialogPrimitive.Overlay, { className: "fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" }),
        /* @__PURE__ */ jsxs(
          DialogPrimitive.Content,
          {
            ref,
            className: cn(
              "fixed inset-y-0 left-0 z-50 flex h-full w-72 flex-col bg-cyan-900 shadow-xl",
              "data-[state=open]:animate-in data-[state=closed]:animate-out",
              "data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left",
              "duration-300",
              className
            ),
            ...props,
            children: [
              /* @__PURE__ */ jsxs("div", { className: "flex shrink-0 items-center justify-between border-b border-cyan-800 px-4 pt-4 pb-3", children: [
                header || /* @__PURE__ */ jsx("div", {}),
                /* @__PURE__ */ jsx(DialogPrimitive.Close, { asChild: true, children: /* @__PURE__ */ jsx(Button, { variant: "ghost", size: "icon", "aria-label": "Close menu", className: "text-slate-300 hover:bg-cyan-800/50 hover:text-white", children: /* @__PURE__ */ jsx(X, { className: "h-5 w-5" }) }) })
              ] }),
              /* @__PURE__ */ jsx(
                "nav",
                {
                  className: cn(
                    "flex-1 space-y-4 overflow-y-auto p-4",
                    navigationClassName
                  ),
                  children: navigation.map((item, index) => {
                    if (isNavigationGroup(item)) {
                      return /* @__PURE__ */ jsx(
                        SidebarNavGroup,
                        {
                          group: item,
                          collapsed: false
                        },
                        item.label || `group-${index}`
                      );
                    }
                    return /* @__PURE__ */ jsx(SidebarNavItem, { item, collapsed: false }, item.id);
                  })
                }
              ),
              footer && /* @__PURE__ */ jsx("div", { className: "shrink-0 border-t border-cyan-800 p-4", children: footer })
            ]
          }
        )
      ] })
    ] });
  }
);
MobileSidebar.displayName = "MobileSidebar";
var SidebarTrigger = React44.forwardRef(
  ({ icon, className, ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      Button,
      {
        ref,
        variant: "ghost",
        size: "icon",
        className,
        "aria-label": "Toggle sidebar",
        ...props,
        children: icon || /* @__PURE__ */ jsx(Menu, { className: "h-5 w-5" })
      }
    );
  }
);
SidebarTrigger.displayName = "SidebarTrigger";
var UserMenu = ({ user, onSignOut, onProfileClick, onSettingsClick }) => {
  return /* @__PURE__ */ jsxs(DropdownMenuPrimitive2.Root, { children: [
    /* @__PURE__ */ jsx(DropdownMenuPrimitive2.Trigger, { asChild: true, children: /* @__PURE__ */ jsxs(
      "button",
      {
        className: "flex items-center gap-3 rounded-lg px-2 py-1.5 transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1",
        "aria-label": "User menu",
        children: [
          user.avatarUrl ? /* @__PURE__ */ jsx("img", { src: user.avatarUrl, alt: "", className: "h-8 w-8 rounded-full object-cover" }) : /* @__PURE__ */ jsx("div", { className: "flex h-8 w-8 items-center justify-center rounded-full bg-gray-200 text-sm font-medium text-gray-600", children: user.initials || user.name.charAt(0).toUpperCase() }),
          /* @__PURE__ */ jsxs("div", { className: "hidden sm:block text-left", children: [
            /* @__PURE__ */ jsx("p", { className: "text-sm font-medium text-gray-900", children: user.name }),
            /* @__PURE__ */ jsx("p", { className: "text-xs text-gray-500", children: user.email })
          ] }),
          /* @__PURE__ */ jsx(ChevronDown, { className: "h-4 w-4 text-gray-500" })
        ]
      }
    ) }),
    /* @__PURE__ */ jsx(DropdownMenuPrimitive2.Portal, { children: /* @__PURE__ */ jsxs(
      DropdownMenuPrimitive2.Content,
      {
        align: "end",
        sideOffset: 8,
        className: "z-50 min-w-[200px] overflow-hidden rounded-lg border border-gray-200 bg-white p-1 shadow-lg animate-in fade-in-0 zoom-in-95 data-[side=bottom]:slide-in-from-top-2",
        children: [
          /* @__PURE__ */ jsxs("div", { className: "px-3 py-2", children: [
            /* @__PURE__ */ jsx("p", { className: "text-sm font-medium text-gray-900", children: user.name }),
            /* @__PURE__ */ jsx("p", { className: "text-xs text-gray-500", children: user.email })
          ] }),
          /* @__PURE__ */ jsx(DropdownMenuPrimitive2.Separator, { className: "-mx-1 my-1 h-px bg-gray-200" }),
          /* @__PURE__ */ jsxs(
            DropdownMenuPrimitive2.Item,
            {
              className: "flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm text-gray-700 outline-none transition-colors hover:bg-gray-100 focus:bg-gray-100",
              onSelect: onProfileClick,
              children: [
                /* @__PURE__ */ jsx(User, { className: "h-4 w-4" }),
                "Profile"
              ]
            }
          ),
          /* @__PURE__ */ jsxs(
            DropdownMenuPrimitive2.Item,
            {
              className: "flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm text-gray-700 outline-none transition-colors hover:bg-gray-100 focus:bg-gray-100",
              onSelect: onSettingsClick,
              children: [
                /* @__PURE__ */ jsx(Settings, { className: "h-4 w-4" }),
                "Settings"
              ]
            }
          ),
          /* @__PURE__ */ jsx(DropdownMenuPrimitive2.Separator, { className: "-mx-1 my-1 h-px bg-gray-200" }),
          /* @__PURE__ */ jsxs(
            DropdownMenuPrimitive2.Item,
            {
              className: "flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm text-red-600 outline-none transition-colors hover:bg-red-50 focus:bg-red-50",
              onSelect: onSignOut,
              children: [
                /* @__PURE__ */ jsx(LogOut, { className: "h-4 w-4" }),
                "Sign out"
              ]
            }
          )
        ]
      }
    ) })
  ] });
};
var WorkspaceSwitcher = ({
  workspaces,
  currentWorkspace,
  onWorkspaceChange,
  collapsed = false
}) => {
  const [isOpen, setIsOpen] = React44.useState(false);
  return /* @__PURE__ */ jsxs(DropdownMenuPrimitive2.Root, { open: isOpen, onOpenChange: setIsOpen, children: [
    /* @__PURE__ */ jsx(DropdownMenuPrimitive2.Trigger, { asChild: true, children: /* @__PURE__ */ jsxs(
      "button",
      {
        className: cn(
          "flex items-center gap-2 rounded-lg transition-colors hover:bg-cyan-800/50 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-1",
          collapsed ? "p-2" : "px-2 py-1.5"
        ),
        "aria-label": `Switch workspace, current: ${currentWorkspace.name}`,
        "aria-haspopup": "listbox",
        "aria-expanded": isOpen,
        children: [
          currentWorkspace.logo ? /* @__PURE__ */ jsx("img", { src: currentWorkspace.logo, alt: "", className: "h-8 w-8 rounded-lg object-cover" }) : /* @__PURE__ */ jsx("div", { className: "flex h-8 w-8 items-center justify-center rounded-lg bg-cyan-700 text-sm font-bold text-white", children: currentWorkspace.shortName || currentWorkspace.name.charAt(0).toUpperCase() }),
          !collapsed && /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx("span", { className: "flex-1 truncate text-left text-sm font-semibold text-slate-100", children: currentWorkspace.name }),
            /* @__PURE__ */ jsx(ChevronDown, { className: "h-4 w-4 shrink-0 text-slate-400" })
          ] })
        ]
      }
    ) }),
    /* @__PURE__ */ jsx(DropdownMenuPrimitive2.Portal, { children: /* @__PURE__ */ jsxs(
      DropdownMenuPrimitive2.Content,
      {
        align: collapsed ? "start" : "start",
        side: collapsed ? "right" : "bottom",
        sideOffset: 8,
        className: "z-50 min-w-[200px] overflow-hidden rounded-lg border border-gray-200 bg-white p-1 shadow-lg animate-in fade-in-0 zoom-in-95",
        role: "listbox",
        "aria-label": "Available workspaces",
        children: [
          /* @__PURE__ */ jsx("div", { className: "px-3 py-2", children: /* @__PURE__ */ jsx(
            "p",
            {
              className: "text-xs font-medium uppercase tracking-wider text-gray-500",
              id: "workspace-list-label",
              children: "Workspaces"
            }
          ) }),
          workspaces.map((workspace) => /* @__PURE__ */ jsxs(
            DropdownMenuPrimitive2.Item,
            {
              className: "flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm text-gray-700 outline-none transition-colors hover:bg-gray-100 focus:bg-gray-100",
              onSelect: () => onWorkspaceChange(workspace),
              children: [
                workspace.logo ? /* @__PURE__ */ jsx("img", { src: workspace.logo, alt: "", className: "h-6 w-6 rounded object-cover" }) : /* @__PURE__ */ jsx("div", { className: "flex h-6 w-6 items-center justify-center rounded bg-gray-200 text-xs font-medium text-gray-600", children: workspace.shortName || workspace.name.charAt(0).toUpperCase() }),
                /* @__PURE__ */ jsx("span", { className: "flex-1 truncate", children: workspace.name }),
                workspace.id === currentWorkspace.id && /* @__PURE__ */ jsx(Check, { className: "h-4 w-4 text-blue-600" })
              ]
            },
            workspace.id
          )),
          /* @__PURE__ */ jsx(DropdownMenuPrimitive2.Separator, { className: "-mx-1 my-1 h-px bg-gray-200" }),
          /* @__PURE__ */ jsxs(DropdownMenuPrimitive2.Item, { className: "flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm text-gray-700 outline-none transition-colors hover:bg-gray-100 focus:bg-gray-100", children: [
            /* @__PURE__ */ jsx(Plus, { className: "h-4 w-4" }),
            "Create workspace"
          ] })
        ]
      }
    ) })
  ] });
};
var DashboardHeader = ({
  breadcrumbs,
  pageTitle,
  headerContent,
  headerActions,
  user,
  onSignOut,
  onProfileClick,
  onSettingsClick,
  showMobileMenu,
  mobileMenuTrigger
}) => {
  return /* @__PURE__ */ jsxs(
    "header",
    {
      className: "sticky top-0 z-40 flex h-16 shrink-0 items-center gap-4 border-b border-gray-200 bg-white px-4 md:px-6",
      role: "banner",
      "aria-label": "Page header",
      children: [
        showMobileMenu && mobileMenuTrigger,
        /* @__PURE__ */ jsxs("div", { className: "flex flex-1 items-center gap-4", children: [
          breadcrumbs && breadcrumbs.length > 0 ? /* @__PURE__ */ jsx(Breadcrumb, { "aria-label": "Breadcrumb navigation", children: /* @__PURE__ */ jsx(BreadcrumbList, { children: breadcrumbs.map((item, index) => /* @__PURE__ */ jsxs(React44.Fragment, { children: [
            /* @__PURE__ */ jsx(BreadcrumbItem, { children: item.isCurrent ? /* @__PURE__ */ jsx(BreadcrumbPage, { children: item.label }) : /* @__PURE__ */ jsx(BreadcrumbLink, { href: item.href, children: item.label }) }),
            index < breadcrumbs.length - 1 && /* @__PURE__ */ jsx(BreadcrumbSeparator, {})
          ] }, index)) }) }) : pageTitle ? /* @__PURE__ */ jsx("h1", { className: "text-lg font-semibold text-gray-900", children: pageTitle }) : null,
          headerContent
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", role: "group", "aria-label": "Header actions", children: [
          headerActions,
          user && /* @__PURE__ */ jsx(UserMenu, { user, onSignOut, onProfileClick, onSettingsClick })
        ] })
      ]
    }
  );
};
var DashboardLayout = React44.forwardRef(
  ({
    navigation,
    user,
    onSignOut,
    onProfileClick,
    onSettingsClick,
    breadcrumbs,
    headerContent,
    headerActions,
    logo,
    logoCollapsed,
    pageTitle,
    showCollapseToggle = true,
    defaultCollapsed = false,
    collapsed: controlledCollapsed,
    onCollapsedChange,
    sidebarFooter,
    children,
    className,
    // Destructure workspace-related props to prevent DOM spreading
    workspaces: _workspaces,
    currentWorkspace: _currentWorkspace,
    onWorkspaceChange: _onWorkspaceChange,
    ...props
  }, ref) => {
    const [internalCollapsed, setInternalCollapsed] = React44.useState(defaultCollapsed);
    const [mobileOpen, setMobileOpen] = React44.useState(false);
    const collapsed = controlledCollapsed ?? internalCollapsed;
    const setCollapsed = onCollapsedChange ?? setInternalCollapsed;
    const sidebarHeader = React44.useMemo(() => {
      const currentLogo = collapsed ? logoCollapsed : logo;
      return currentLogo;
    }, [collapsed, logo, logoCollapsed]);
    const sidebarFooterContent = React44.useMemo(() => {
      if (sidebarFooter) {
        return sidebarFooter;
      }
      return null;
    }, [sidebarFooter]);
    const mobileSidebarHeader = React44.useMemo(() => {
      const currentLogo = collapsed ? logoCollapsed : logo;
      return currentLogo;
    }, [collapsed, logo, logoCollapsed]);
    const sidebarWidth = collapsed ? "64px" : "256px";
    const cssVars = {
      "--sidebar-width": sidebarWidth
    };
    return /* @__PURE__ */ jsxs("div", { ref, className: cn("flex h-screen bg-slate-50", className), style: cssVars, ...props, children: [
      /* @__PURE__ */ jsx("div", { className: "hidden md:block", children: /* @__PURE__ */ jsx(
        Sidebar,
        {
          navigation,
          collapsed,
          onCollapsedChange: setCollapsed,
          showCollapseToggle,
          header: sidebarHeader,
          footer: sidebarFooterContent,
          className: "h-full"
        }
      ) }),
      /* @__PURE__ */ jsxs("div", { className: "flex flex-1 flex-col overflow-hidden", children: [
        /* @__PURE__ */ jsx(
          DashboardHeader,
          {
            breadcrumbs,
            pageTitle,
            headerContent,
            headerActions,
            user,
            onSignOut,
            onProfileClick,
            onSettingsClick,
            showMobileMenu: true,
            mobileMenuTrigger: /* @__PURE__ */ jsx("div", { className: "md:hidden", children: /* @__PURE__ */ jsx(
              MobileSidebar,
              {
                navigation,
                open: mobileOpen,
                onOpenChange: setMobileOpen,
                header: mobileSidebarHeader
              }
            ) })
          }
        ),
        /* @__PURE__ */ jsx(
          "main",
          {
            id: "main-content",
            className: "flex-1 overflow-auto p-4 md:p-6",
            role: "main",
            "aria-label": "Main content",
            tabIndex: -1,
            children
          }
        )
      ] })
    ] });
  }
);
DashboardLayout.displayName = "DashboardLayout";
var UploadProgress = React44.forwardRef(
  ({ progress, status, fileName, error, className }, ref) => {
    const getStatusColor = () => {
      switch (status) {
        case "success":
          return "bg-green-500";
        case "error":
          return "bg-destructive";
        case "uploading":
          return "bg-primary";
        default:
          return "bg-muted-foreground";
      }
    };
    const getStatusText = () => {
      switch (status) {
        case "pending":
          return "Waiting...";
        case "uploading":
          return `${Math.round(progress)}%`;
        case "success":
          return "Complete";
        case "error":
          return error || "Failed";
      }
    };
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ref,
        className: cn("w-full space-y-1", className),
        role: "progressbar",
        "aria-valuenow": progress,
        "aria-valuemin": 0,
        "aria-valuemax": 100,
        "aria-label": fileName ? `Uploading ${fileName}` : "Upload progress",
        children: [
          /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between text-sm", children: [
            fileName && /* @__PURE__ */ jsx("span", { className: "truncate text-foreground max-w-[200px]", children: fileName }),
            /* @__PURE__ */ jsx(
              "span",
              {
                className: cn(
                  "text-xs",
                  status === "error" && "text-destructive",
                  status === "success" && "text-green-600 "
                ),
                children: getStatusText()
              }
            )
          ] }),
          /* @__PURE__ */ jsx("div", { className: "h-2 w-full overflow-hidden rounded-full bg-muted", children: /* @__PURE__ */ jsx(
            "div",
            {
              className: cn(
                "h-full transition-all duration-300 ease-out",
                getStatusColor()
              ),
              style: { width: `${Math.min(100, Math.max(0, progress))}%` }
            }
          ) })
        ]
      }
    );
  }
);
UploadProgress.displayName = "UploadProgress";
var getFileIcon = (file) => {
  const type = file.type;
  if (type.startsWith("image/")) {
    return Image;
  }
  if (type.startsWith("video/")) {
    return Film;
  }
  if (type.startsWith("audio/")) {
    return Music;
  }
  return FileText;
};
var formatFileSize = (bytes) => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};
var FilePreview = React44.forwardRef(
  ({ file, onRemove, compact = false, className }, ref) => {
    const { file: rawFile, status, progress, error, previewUrl } = file;
    const FileIcon = getFileIcon(rawFile);
    const isImage = rawFile.type.startsWith("image/");
    const isUploading = status === "uploading";
    const hasError = status === "error";
    if (compact) {
      return /* @__PURE__ */ jsxs(
        "div",
        {
          ref,
          className: cn(
            "flex items-center gap-3 rounded-lg border p-2",
            hasError && "border-destructive bg-destructive/5",
            className
          ),
          children: [
            /* @__PURE__ */ jsx("div", { className: "flex-shrink-0", children: isImage && previewUrl ? /* @__PURE__ */ jsx(
              "img",
              {
                src: previewUrl,
                alt: rawFile.name,
                className: "h-8 w-8 rounded object-cover"
              }
            ) : /* @__PURE__ */ jsx(FileIcon, { className: "h-8 w-8 text-muted-foreground", "aria-hidden": "true" }) }),
            /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1", children: [
              /* @__PURE__ */ jsx("p", { className: "truncate text-sm font-medium text-foreground", children: rawFile.name }),
              /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 text-xs text-muted-foreground", children: [
                /* @__PURE__ */ jsx("span", { children: formatFileSize(rawFile.size) }),
                hasError && error && /* @__PURE__ */ jsx("span", { className: "text-destructive", children: error })
              ] }),
              isUploading && /* @__PURE__ */ jsx(
                UploadProgress,
                {
                  progress,
                  status,
                  className: "mt-1"
                }
              )
            ] }),
            /* @__PURE__ */ jsx("div", { className: "flex-shrink-0", children: isUploading ? /* @__PURE__ */ jsx(Spinner, { size: "sm", "aria-label": "Uploading" }) : /* @__PURE__ */ jsx(
              Button,
              {
                variant: "ghost",
                size: "icon",
                onClick: onRemove,
                "aria-label": `Remove ${rawFile.name}`,
                className: "h-8 w-8 hover:bg-destructive/10 hover:text-destructive",
                children: /* @__PURE__ */ jsx(X, { className: "h-4 w-4" })
              }
            ) })
          ]
        }
      );
    }
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ref,
        className: cn(
          "relative flex flex-col items-center rounded-lg border p-4",
          hasError && "border-destructive bg-destructive/5",
          className
        ),
        children: [
          !isUploading && /* @__PURE__ */ jsx(
            Button,
            {
              variant: "ghost",
              size: "icon",
              onClick: onRemove,
              "aria-label": `Remove ${rawFile.name}`,
              className: "absolute right-1 top-1 h-6 w-6 hover:bg-destructive/10 hover:text-destructive",
              children: /* @__PURE__ */ jsx(X, { className: "h-3 w-3" })
            }
          ),
          /* @__PURE__ */ jsxs("div", { className: "mb-2 flex h-20 w-20 items-center justify-center overflow-hidden rounded-lg bg-muted", children: [
            isImage && previewUrl ? /* @__PURE__ */ jsx(
              "img",
              {
                src: previewUrl,
                alt: rawFile.name,
                className: "h-full w-full object-cover"
              }
            ) : /* @__PURE__ */ jsx(FileIcon, { className: "h-10 w-10 text-muted-foreground", "aria-hidden": "true" }),
            isUploading && /* @__PURE__ */ jsx("div", { className: "absolute inset-0 flex items-center justify-center bg-background/80", children: /* @__PURE__ */ jsx(Spinner, { "aria-label": "Uploading" }) })
          ] }),
          /* @__PURE__ */ jsx("p", { className: "max-w-full truncate text-center text-sm font-medium text-foreground", children: rawFile.name }),
          /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: formatFileSize(rawFile.size) }),
          isUploading && /* @__PURE__ */ jsx(
            UploadProgress,
            {
              progress,
              status,
              className: "mt-2 w-full"
            }
          ),
          hasError && error && /* @__PURE__ */ jsx("p", { className: "mt-1 text-center text-xs text-destructive", children: error })
        ]
      }
    );
  }
);
FilePreview.displayName = "FilePreview";

// src/components/organisms/UploadDropzone/UploadDropzone.types.ts
var acceptedFileTypeMimeMap = {
  image: ["image/jpeg", "image/png", "image/gif", "image/webp", "image/svg+xml"],
  video: ["video/mp4", "video/webm", "video/ogg", "video/quicktime"],
  audio: ["audio/mpeg", "audio/wav", "audio/ogg", "audio/webm"],
  document: [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "text/plain",
    "text/markdown"
  ],
  spreadsheet: [
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "text/csv"
  ],
  archive: [
    "application/zip",
    "application/x-rar-compressed",
    "application/x-7z-compressed",
    "application/gzip"
  ]
};
var generateFileId = () => {
  return `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};
var parseAccept = (accept) => {
  if (!accept || accept.length === 0) return void 0;
  const result = {};
  accept.forEach((item) => {
    if (item in acceptedFileTypeMimeMap) {
      const mimes = acceptedFileTypeMimeMap[item];
      mimes.forEach((mime) => {
        result[mime] = [];
      });
    } else if (item.startsWith(".")) {
      const extension = item;
      const extensionMap = {
        ".jpg": "image/jpeg",
        ".jpeg": "image/jpeg",
        ".png": "image/png",
        ".gif": "image/gif",
        ".webp": "image/webp",
        ".svg": "image/svg+xml",
        ".pdf": "application/pdf",
        ".doc": "application/msword",
        ".docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        ".xls": "application/vnd.ms-excel",
        ".xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        ".csv": "text/csv",
        ".txt": "text/plain",
        ".mp4": "video/mp4",
        ".mp3": "audio/mpeg",
        ".wav": "audio/wav",
        ".zip": "application/zip"
      };
      const mime = extensionMap[extension.toLowerCase()];
      if (mime) {
        result[mime] = [extension];
      }
    } else if (item.includes("/")) {
      result[item] = [];
    }
  });
  return Object.keys(result).length > 0 ? result : void 0;
};
var formatMaxSize = (bytes) => {
  if (bytes >= 1073741824) {
    return `${(bytes / 1073741824).toFixed(1)} GB`;
  }
  if (bytes >= 1048576) {
    return `${(bytes / 1048576).toFixed(0)} MB`;
  }
  if (bytes >= 1024) {
    return `${(bytes / 1024).toFixed(0)} KB`;
  }
  return `${bytes} B`;
};
var UploadDropzone = React44.forwardRef(
  ({
    onUpload,
    onError,
    onRemove,
    accept,
    maxSize = 10 * 1024 * 1024,
    // 10MB default
    maxFiles,
    multiple = true,
    disabled = false,
    label,
    helperText,
    className,
    files: controlledFiles,
    onFilesChange
  }, ref) => {
    const [internalFiles, setInternalFiles] = useState([]);
    const files = controlledFiles ?? internalFiles;
    const isControlled = controlledFiles !== void 0;
    const updateFiles = useCallback(
      (updater) => {
        if (isControlled && onFilesChange) {
          onFilesChange(updater(files));
        } else {
          setInternalFiles(updater);
        }
      },
      [isControlled, onFilesChange, files]
    );
    const acceptConfig = useMemo(() => parseAccept(accept), [accept]);
    const onDrop = useCallback(
      (acceptedFiles, rejectedFiles) => {
        if (rejectedFiles.length > 0) {
          const errors = rejectedFiles.map((rejection) => {
            const error = rejection.errors[0];
            let code = "file-invalid-type";
            let message = "Invalid file type";
            if (error.code === "file-too-large") {
              code = "file-too-large";
              message = `File is too large. Maximum size is ${formatMaxSize(maxSize)}`;
            } else if (error.code === "too-many-files") {
              code = "too-many-files";
              message = `Too many files. Maximum is ${maxFiles}`;
            } else if (error.code === "file-invalid-type") {
              message = "File type not accepted";
            }
            return {
              file: rejection.file,
              code,
              message
            };
          });
          onError?.(errors);
        }
        if (acceptedFiles.length > 0) {
          const newFiles = acceptedFiles.map((file) => {
            const uploadFile = {
              id: generateFileId(),
              file,
              status: "pending",
              progress: 0
            };
            if (file.type.startsWith("image/")) {
              uploadFile.previewUrl = URL.createObjectURL(file);
            }
            return uploadFile;
          });
          updateFiles((prev) => [...prev, ...newFiles]);
          onUpload?.(newFiles);
        }
      },
      [maxSize, maxFiles, onError, onUpload, updateFiles]
    );
    const handleRemove = useCallback(
      (fileToRemove) => {
        if (fileToRemove.previewUrl) {
          URL.revokeObjectURL(fileToRemove.previewUrl);
        }
        updateFiles((prev) => prev.filter((f) => f.id !== fileToRemove.id));
        onRemove?.(fileToRemove);
      },
      [updateFiles, onRemove]
    );
    const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
      onDrop,
      accept: acceptConfig,
      maxSize,
      maxFiles,
      multiple,
      disabled
    });
    const defaultHelperText = useMemo(() => {
      const parts = [];
      if (accept && accept.length > 0) {
        const isCategory = accept.some((a) => a in acceptedFileTypeMimeMap);
        if (isCategory) {
          parts.push(`Accepted: ${accept.join(", ")}`);
        } else {
          parts.push(`Accepted: ${accept.join(", ")}`);
        }
      }
      parts.push(`Max size: ${formatMaxSize(maxSize)}`);
      if (maxFiles) {
        parts.push(`Max files: ${maxFiles}`);
      }
      return parts.join(" | ");
    }, [accept, maxSize, maxFiles]);
    React44.useEffect(() => {
      return () => {
        files.forEach((file) => {
          if (file.previewUrl) {
            URL.revokeObjectURL(file.previewUrl);
          }
        });
      };
    }, []);
    return /* @__PURE__ */ jsxs("div", { ref, className: cn("w-full space-y-4", className), children: [
      /* @__PURE__ */ jsxs(
        "div",
        {
          ...getRootProps(),
          className: cn(
            "flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors",
            "border-muted-foreground/25 bg-muted/25",
            "hover:border-primary/50 hover:bg-muted/50",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            isDragActive && !isDragReject && "border-primary bg-primary/10",
            isDragReject && "border-destructive bg-destructive/10",
            disabled && "cursor-not-allowed opacity-50"
          ),
          children: [
            /* @__PURE__ */ jsx("input", { ...getInputProps(), "aria-label": "File upload" }),
            /* @__PURE__ */ jsx(
              Upload,
              {
                className: cn(
                  "mb-4 h-10 w-10",
                  isDragActive && !isDragReject && "text-primary",
                  isDragReject && "text-destructive",
                  !isDragActive && "text-muted-foreground"
                ),
                "aria-hidden": "true"
              }
            ),
            /* @__PURE__ */ jsx("p", { className: "mb-1 text-center text-sm font-medium text-foreground", children: label || (isDragActive ? "Drop files here" : "Drag & drop files here") }),
            /* @__PURE__ */ jsx("p", { className: "mb-4 text-center text-xs text-muted-foreground", children: "or" }),
            /* @__PURE__ */ jsx(
              Button,
              {
                type: "button",
                variant: "outline",
                size: "sm",
                disabled,
                tabIndex: -1,
                "aria-hidden": "true",
                children: "Browse Files"
              }
            ),
            /* @__PURE__ */ jsx("p", { className: "mt-4 text-center text-xs text-muted-foreground", children: helperText || defaultHelperText })
          ]
        }
      ),
      files.length > 0 && /* @__PURE__ */ jsx(
        "div",
        {
          className: "space-y-2",
          role: "list",
          "aria-label": "Uploaded files",
          children: files.map((file) => /* @__PURE__ */ jsx(
            FilePreview,
            {
              file,
              onRemove: () => handleRemove(file),
              compact: true
            },
            file.id
          ))
        }
      )
    ] });
  }
);
UploadDropzone.displayName = "UploadDropzone";
var RecentSearches = React44.forwardRef(
  ({ searches, onSelect, onClear, highlightedIndex = -1, className }, ref) => {
    if (searches.length === 0) {
      return null;
    }
    return /* @__PURE__ */ jsxs("div", { ref, className: cn("py-2", className), children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between px-3 mb-1", children: [
        /* @__PURE__ */ jsx("span", { className: "text-xs font-medium text-muted-foreground uppercase tracking-wide", children: "Recent Searches" }),
        /* @__PURE__ */ jsxs(
          Button,
          {
            variant: "ghost",
            size: "sm",
            className: "h-6 px-2 text-xs text-muted-foreground hover:text-foreground",
            onClick: onClear,
            "aria-label": "Clear recent searches",
            children: [
              "Clear",
              /* @__PURE__ */ jsx(X, { className: "h-3 w-3 ml-1" })
            ]
          }
        )
      ] }),
      /* @__PURE__ */ jsx("ul", { role: "listbox", "aria-label": "Recent searches", children: searches.map((query, index) => /* @__PURE__ */ jsxs(
        "li",
        {
          role: "option",
          "aria-selected": index === highlightedIndex,
          className: cn(
            "flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors",
            "hover:bg-accent",
            index === highlightedIndex && "bg-accent"
          ),
          onClick: () => onSelect(query),
          onKeyDown: (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onSelect(query);
            }
          },
          tabIndex: -1,
          children: [
            /* @__PURE__ */ jsx(
              Clock,
              {
                className: "h-4 w-4 text-muted-foreground flex-shrink-0",
                "aria-hidden": "true"
              }
            ),
            /* @__PURE__ */ jsx("span", { className: "text-sm text-foreground truncate", children: query })
          ]
        },
        query
      )) })
    ] });
  }
);
RecentSearches.displayName = "RecentSearches";
function HighlightedText({
  html,
  fallback
}) {
  if (!html) {
    return /* @__PURE__ */ jsx("span", { children: fallback || "" });
  }
  const sanitized = html.replace(/<(?!\/?(mark)(?:\s|>))[^>]*>/gi, "").replace(/&lt;/g, "<").replace(/&gt;/g, ">");
  return /* @__PURE__ */ jsx(
    "span",
    {
      dangerouslySetInnerHTML: { __html: sanitized },
      className: "[&>mark]:bg-yellow-200 [&>mark]: [&>mark]:rounded-sm [&>mark]:px-0.5"
    }
  );
}
function DefaultHitContent({ hit }) {
  const highlightedTitle = hit._highlightResult?.title?.value || hit.title;
  const highlightedDescription = hit._highlightResult?.description?.value || hit.description;
  return /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-3", children: [
    hit.imageUrl ? /* @__PURE__ */ jsx("div", { className: "flex-shrink-0 w-10 h-10 rounded-md overflow-hidden bg-muted", children: /* @__PURE__ */ jsx(
      "img",
      {
        src: hit.imageUrl,
        alt: "",
        className: "w-full h-full object-cover",
        loading: "lazy"
      }
    ) }) : /* @__PURE__ */ jsx("div", { className: "flex-shrink-0 w-10 h-10 rounded-md bg-muted flex items-center justify-center", children: hit.category === "image" ? /* @__PURE__ */ jsx(Image, { className: "h-5 w-5 text-muted-foreground" }) : /* @__PURE__ */ jsx(FileText, { className: "h-5 w-5 text-muted-foreground" }) }),
    /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
      /* @__PURE__ */ jsx("div", { className: "font-medium text-sm text-foreground truncate", children: /* @__PURE__ */ jsx(HighlightedText, { html: highlightedTitle, fallback: hit.title }) }),
      (highlightedDescription || hit.description) && /* @__PURE__ */ jsx("div", { className: "text-xs text-muted-foreground line-clamp-2 mt-0.5", children: /* @__PURE__ */ jsx(
        HighlightedText,
        {
          html: highlightedDescription,
          fallback: hit.description
        }
      ) }),
      hit.category && /* @__PURE__ */ jsx("div", { className: "mt-1", children: /* @__PURE__ */ jsx("span", { className: "inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground", children: hit.category }) })
    ] })
  ] });
}
var SearchHit = React44.forwardRef(
  ({ hit, isHighlighted, onSelect, renderHit, className }, ref) => {
    const handleClick = React44.useCallback(() => {
      onSelect(hit);
    }, [hit, onSelect]);
    const handleKeyDown = React44.useCallback(
      (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect(hit);
        }
      },
      [hit, onSelect]
    );
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        role: "option",
        "aria-selected": isHighlighted,
        tabIndex: -1,
        className: cn(
          "px-3 py-2 cursor-pointer transition-colors",
          "hover:bg-accent",
          isHighlighted && "bg-accent",
          className
        ),
        onClick: handleClick,
        onKeyDown: handleKeyDown,
        children: renderHit ? renderHit(hit) : /* @__PURE__ */ jsx(DefaultHitContent, { hit })
      }
    );
  }
);
SearchHit.displayName = "SearchHit";
var DEFAULT_PLACEHOLDER = "Search...";
var DEFAULT_RECENT_SEARCHES_KEY = "yi-recent-searches";
var DEFAULT_MAX_RECENT_SEARCHES = 5;
var DEFAULT_DEBOUNCE_MS = 300;
var DEFAULT_HITS_PER_PAGE = 10;
var DEFAULT_ARIA_LABEL = "Search";
function EmptyState({ query }) {
  return /* @__PURE__ */ jsxs("div", { className: "py-8 px-4 text-center", children: [
    /* @__PURE__ */ jsxs("p", { className: "text-sm text-muted-foreground", children: [
      "No results found for ",
      /* @__PURE__ */ jsxs("span", { className: "font-medium", children: [
        "\u201C",
        query,
        "\u201D"
      ] })
    ] }),
    /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground mt-1", children: "Try searching with different keywords" })
  ] });
}
function LoadingState() {
  return /* @__PURE__ */ jsx("div", { className: "py-8 flex items-center justify-center", role: "status", children: /* @__PURE__ */ jsx(Spinner, { size: "default", label: "Searching..." }) });
}
function ErrorState({ message }) {
  return /* @__PURE__ */ jsx("div", { className: "py-8 px-4 text-center", children: /* @__PURE__ */ jsx("p", { className: "text-sm text-destructive", children: message }) });
}
var SiteSearch = React44.forwardRef(
  ({
    appId,
    apiKey,
    indexName,
    onResultSelect,
    placeholder = DEFAULT_PLACEHOLDER,
    recentSearchesKey = DEFAULT_RECENT_SEARCHES_KEY,
    maxRecentSearches = DEFAULT_MAX_RECENT_SEARCHES,
    debounceMs = DEFAULT_DEBOUNCE_MS,
    filters,
    hitsPerPage = DEFAULT_HITS_PER_PAGE,
    renderHit,
    showRecentSearches = true,
    className,
    inputClassName,
    dropdownClassName,
    ariaLabel = DEFAULT_ARIA_LABEL,
    disabled = false,
    autoFocus = false,
    _mockMode = false,
    _mockResults,
    _mockLoading = false
  }, ref) => {
    const containerRef = React44.useRef(null);
    const inputRef = React44.useRef(null);
    const listRef = React44.useRef(null);
    const itemRefs = React44.useRef(/* @__PURE__ */ new Map());
    const algoliaClientRef = React44.useRef(null);
    const mockModeRef = React44.useRef(_mockMode);
    const mockResultsRef = React44.useRef(_mockResults);
    const mockLoadingRef = React44.useRef(_mockLoading);
    const filtersRef = React44.useRef(filters);
    const hitsPerPageRef = React44.useRef(hitsPerPage);
    mockModeRef.current = _mockMode;
    mockLoadingRef.current = _mockLoading;
    filtersRef.current = filters;
    hitsPerPageRef.current = hitsPerPage;
    if (JSON.stringify(mockResultsRef.current) !== JSON.stringify(_mockResults)) {
      mockResultsRef.current = _mockResults;
    }
    const [recentSearches, setRecentSearches, clearRecentSearches] = useLocalStorage(recentSearchesKey, []);
    const [state, setState] = React44.useState({
      query: "",
      isOpen: false,
      isLoading: false,
      results: [],
      error: void 0,
      highlightedIndex: -1
    });
    const debouncedQuery = useDebounce(state.query, debounceMs);
    const isOpenRef = React44.useRef(state.isOpen);
    isOpenRef.current = state.isOpen;
    const handleClickOutside = React44.useCallback(() => {
      if (isOpenRef.current) {
        setState((prev) => ({ ...prev, isOpen: false, highlightedIndex: -1 }));
      }
    }, []);
    useClickOutside(containerRef, handleClickOutside);
    const initializeAlgolia = React44.useCallback(async () => {
      if (mockModeRef.current || algoliaClientRef.current) return;
      try {
        const algoliasearch = (await import('algoliasearch')).default;
        const client = algoliasearch(appId, apiKey);
        const index = client.initIndex(indexName);
        algoliaClientRef.current = {
          search: async (query) => {
            const filterStr = filtersRef.current?.map((f) => `${f.attribute}:${f.value}`).join(" AND ");
            const response = await index.search(query, {
              hitsPerPage: hitsPerPageRef.current,
              filters: filterStr
            });
            return { hits: response.hits };
          }
        };
      } catch (error) {
        console.error("Failed to initialize Algolia client:", error);
        setState((prev) => ({
          ...prev,
          error: "Search is currently unavailable"
        }));
      }
    }, [appId, apiKey, indexName]);
    const performSearch = React44.useCallback(
      async (query) => {
        if (!query.trim()) {
          setState((prev) => ({
            ...prev,
            results: [],
            isLoading: false,
            error: void 0
          }));
          return;
        }
        if (mockModeRef.current) {
          setState((prev) => ({
            ...prev,
            results: mockResultsRef.current || [],
            isLoading: false
          }));
          return;
        }
        setState((prev) => ({ ...prev, isLoading: true, error: void 0 }));
        try {
          await initializeAlgolia();
          if (!algoliaClientRef.current) {
            throw new Error("Search client not initialized");
          }
          const { hits } = await algoliaClientRef.current.search(query);
          setState((prev) => ({
            ...prev,
            results: hits,
            isLoading: false,
            highlightedIndex: hits.length > 0 ? 0 : -1
          }));
        } catch (error) {
          console.error("Search error:", error);
          setState((prev) => ({
            ...prev,
            results: [],
            isLoading: false,
            error: "Search failed. Please try again."
          }));
        }
      },
      [initializeAlgolia]
    );
    React44.useEffect(() => {
      if (state.isOpen) {
        performSearch(debouncedQuery);
      }
    }, [debouncedQuery, state.isOpen, performSearch]);
    const prevMockLoadingRef = React44.useRef(_mockLoading);
    React44.useEffect(() => {
      if (!mockModeRef.current) return;
      if (prevMockLoadingRef.current !== _mockLoading) {
        prevMockLoadingRef.current = _mockLoading;
        setState((prev) => ({
          ...prev,
          isLoading: _mockLoading,
          results: _mockLoading ? [] : mockResultsRef.current || []
        }));
      }
    }, [_mockLoading]);
    const addToRecentSearches = React44.useCallback(
      (query) => {
        if (!query.trim()) return;
        setRecentSearches((prev) => {
          const filtered = prev.filter((q) => q !== query);
          return [query, ...filtered].slice(0, maxRecentSearches);
        });
      },
      [setRecentSearches, maxRecentSearches]
    );
    const handleInputChange = React44.useCallback(
      (event) => {
        const value = event.target.value;
        setState((prev) => ({
          ...prev,
          query: value,
          isOpen: true,
          highlightedIndex: -1
        }));
      },
      []
    );
    const handleInputFocus = React44.useCallback(() => {
      setState((prev) => ({ ...prev, isOpen: true }));
    }, []);
    const handleResultSelect = React44.useCallback(
      (hit) => {
        addToRecentSearches(state.query);
        setState((prev) => ({
          ...prev,
          isOpen: false,
          query: "",
          highlightedIndex: -1
        }));
        onResultSelect(hit);
      },
      [state.query, addToRecentSearches, onResultSelect]
    );
    const handleRecentSearchSelect = React44.useCallback((query) => {
      setState((prev) => ({
        ...prev,
        query,
        highlightedIndex: -1
      }));
      inputRef.current?.focus();
    }, []);
    const handleClearRecentSearches = React44.useCallback(() => {
      clearRecentSearches();
    }, [clearRecentSearches]);
    const handleClearInput = React44.useCallback(() => {
      setState((prev) => ({
        ...prev,
        query: "",
        results: [],
        highlightedIndex: -1
      }));
      inputRef.current?.focus();
    }, []);
    const showRecent = showRecentSearches && !state.query && recentSearches.length > 0;
    const totalItems = showRecent ? recentSearches.length : state.results.length;
    const handleKeyDown = React44.useCallback(
      (event) => {
        if (!state.isOpen) {
          if (event.key === "ArrowDown" || event.key === "Enter") {
            setState((prev) => ({ ...prev, isOpen: true }));
          }
          return;
        }
        switch (event.key) {
          case "ArrowDown":
            event.preventDefault();
            setState((prev) => ({
              ...prev,
              highlightedIndex: prev.highlightedIndex < totalItems - 1 ? prev.highlightedIndex + 1 : 0
            }));
            break;
          case "ArrowUp":
            event.preventDefault();
            setState((prev) => ({
              ...prev,
              highlightedIndex: prev.highlightedIndex > 0 ? prev.highlightedIndex - 1 : totalItems - 1
            }));
            break;
          case "Enter":
            event.preventDefault();
            if (state.highlightedIndex >= 0) {
              if (showRecent) {
                handleRecentSearchSelect(recentSearches[state.highlightedIndex]);
              } else if (state.results[state.highlightedIndex]) {
                handleResultSelect(state.results[state.highlightedIndex]);
              }
            } else if (state.query.trim()) {
              addToRecentSearches(state.query);
            }
            break;
          case "Escape":
            event.preventDefault();
            setState((prev) => ({
              ...prev,
              isOpen: false,
              highlightedIndex: -1
            }));
            inputRef.current?.blur();
            break;
        }
      },
      [
        state.isOpen,
        state.highlightedIndex,
        state.results,
        state.query,
        totalItems,
        showRecent,
        recentSearches,
        handleRecentSearchSelect,
        handleResultSelect,
        addToRecentSearches
      ]
    );
    React44.useEffect(() => {
      if (state.highlightedIndex >= 0 && listRef.current) {
        const highlightedItem = itemRefs.current.get(state.highlightedIndex);
        if (highlightedItem) {
          highlightedItem.scrollIntoView({
            block: "nearest",
            behavior: "smooth"
          });
        }
      }
    }, [state.highlightedIndex]);
    const inputId = React44.useId();
    const listboxId = `${inputId}-listbox`;
    const shouldShowDropdown = state.isOpen && (state.isLoading || state.error || state.results.length > 0 || showRecent || state.query.trim() && state.results.length === 0 && !state.isLoading);
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ref: (node) => {
          containerRef.current = node;
          if (typeof ref === "function") {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        },
        className: cn("relative w-full max-w-md", className),
        children: [
          /* @__PURE__ */ jsxs("div", { className: "relative", children: [
            /* @__PURE__ */ jsx(
              Search,
              {
                className: "absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none",
                "aria-hidden": "true"
              }
            ),
            /* @__PURE__ */ jsx(
              Input,
              {
                ref: inputRef,
                id: inputId,
                type: "search",
                value: state.query,
                onChange: handleInputChange,
                onFocus: handleInputFocus,
                onKeyDown: handleKeyDown,
                placeholder,
                disabled,
                autoFocus,
                className: cn(
                  "pl-9 pr-9",
                  inputClassName
                ),
                "aria-label": ariaLabel,
                "aria-expanded": shouldShowDropdown ? true : void 0,
                "aria-controls": listboxId,
                "aria-activedescendant": state.highlightedIndex >= 0 ? `${listboxId}-option-${state.highlightedIndex}` : void 0,
                "aria-autocomplete": "list",
                role: "combobox"
              }
            ),
            state.query && /* @__PURE__ */ jsx(
              Button,
              {
                variant: "ghost",
                size: "icon",
                className: "absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 text-muted-foreground hover:text-foreground",
                onClick: handleClearInput,
                "aria-label": "Clear search",
                type: "button",
                children: /* @__PURE__ */ jsx(X, { className: "h-4 w-4" })
              }
            )
          ] }),
          shouldShowDropdown && /* @__PURE__ */ jsxs(
            "div",
            {
              ref: listRef,
              id: listboxId,
              role: "listbox",
              "aria-label": "Search results",
              className: cn(
                "absolute top-full left-0 right-0 z-50 mt-1",
                "max-h-80 overflow-auto",
                "rounded-md border bg-popover shadow-lg",
                dropdownClassName
              ),
              children: [
                state.isLoading && /* @__PURE__ */ jsx(LoadingState, {}),
                state.error && !state.isLoading && /* @__PURE__ */ jsx(ErrorState, { message: state.error }),
                showRecent && !state.isLoading && !state.error && /* @__PURE__ */ jsx(
                  RecentSearches,
                  {
                    searches: recentSearches,
                    onSelect: handleRecentSearchSelect,
                    onClear: handleClearRecentSearches,
                    highlightedIndex: state.highlightedIndex
                  }
                ),
                !showRecent && !state.isLoading && !state.error && state.results.length > 0 && /* @__PURE__ */ jsxs("div", { className: "py-2", "aria-live": "polite", children: [
                  /* @__PURE__ */ jsx("div", { className: "px-3 mb-1", children: /* @__PURE__ */ jsxs("span", { className: "text-xs text-muted-foreground", children: [
                    state.results.length,
                    " result",
                    state.results.length !== 1 ? "s" : ""
                  ] }) }),
                  state.results.map((hit, index) => /* @__PURE__ */ jsx(
                    SearchHit,
                    {
                      ref: (node) => {
                        if (node) {
                          itemRefs.current.set(index, node);
                        } else {
                          itemRefs.current.delete(index);
                        }
                      },
                      hit,
                      isHighlighted: index === state.highlightedIndex,
                      onSelect: handleResultSelect,
                      renderHit,
                      "aria-selected": index === state.highlightedIndex
                    },
                    hit.objectID
                  ))
                ] }),
                !showRecent && !state.isLoading && !state.error && state.query.trim() && state.results.length === 0 && /* @__PURE__ */ jsx(EmptyState, { query: state.query })
              ]
            }
          ),
          /* @__PURE__ */ jsxs("div", { className: "sr-only", role: "status", "aria-live": "polite", children: [
            state.isLoading && "Searching...",
            !state.isLoading && state.results.length > 0 && `${state.results.length} results found`,
            !state.isLoading && state.query.trim() && state.results.length === 0 && `No results found for ${state.query}`
          ] })
        ]
      }
    );
  }
);
SiteSearch.displayName = "SiteSearch";
function Timeline({
  duration,
  zoom,
  currentTime,
  videoAssets,
  cuePoints,
  selectedId,
  coSelectedId,
  insertionIndicator,
  onTimeClick,
  onTimeScrubStart,
  onTimeScrub,
  onTimeScrubEnd,
  onItemSelect,
  onItemMove: _onItemMove,
  onItemResize,
  onCuePointConfigure,
  className
}) {
  const rulerContainerRef = React44.useRef(null);
  const tracksContainerRef = React44.useRef(null);
  const handleTracksScroll = React44.useCallback((e) => {
    if (rulerContainerRef.current) {
      rulerContainerRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  }, []);
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col overflow-hidden bg-background border rounded-lg", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex h-8 w-full border-b", children: [
      /* @__PURE__ */ jsx("div", { className: "w-32 shrink-0 border-r bg-muted/50" }),
      /* @__PURE__ */ jsx("div", { ref: rulerContainerRef, className: "flex-1 overflow-x-auto overflow-y-hidden scrollbar-hide no-scrollbar", children: /* @__PURE__ */ jsx(
        TimeRuler,
        {
          duration,
          zoom,
          currentTime,
          onTimeClick,
          onTimeScrubStart,
          onTimeScrub,
          onTimeScrubEnd
        }
      ) })
    ] }),
    /* @__PURE__ */ jsx(
      "div",
      {
        ref: tracksContainerRef,
        role: "region",
        "aria-label": "Timeline tracks",
        className: "flex-1 overflow-auto scrollbar-hide no-scrollbar",
        onScroll: handleTracksScroll,
        onClick: (e) => {
          if (e.target === e.currentTarget) {
            onItemSelect?.(null);
          }
        },
        children: /* @__PURE__ */ jsxs(
          "div",
          {
            role: "list",
            "aria-label": "Timeline items",
            className: "relative min-w-full",
            onClick: (e) => {
              if (e.target === e.currentTarget) {
                onItemSelect?.(null);
              }
            },
            children: [
              /* @__PURE__ */ jsxs(TimelineTrack, { id: "video-track", label: "Video", color: "var(--color-primary)", children: [
                videoAssets.map((item, index) => /* @__PURE__ */ jsx(
                  TimelineItem,
                  {
                    ...item,
                    index,
                    trackId: "video-track",
                    zoom,
                    selected: selectedId === item.id || coSelectedId === item.id,
                    onClick: () => onItemSelect?.(item.id),
                    onResize: onItemResize
                  },
                  item.id
                )),
                insertionIndicator && insertionIndicator.trackId === "video-track" && /* @__PURE__ */ jsx(
                  "div",
                  {
                    className: "absolute top-0 bottom-0 z-50 w-1 bg-yak-orange animate-pulse shadow-[0_0_8px_rgba(255,153,0,0.8)]",
                    style: {
                      left: `${insertionIndicator.time / 100 * zoom}rem`
                    }
                  }
                )
              ] }),
              /* @__PURE__ */ jsxs(TimelineTrack, { id: "cue-track", label: "Interactions", color: "var(--color-yak-orange)", children: [
                cuePoints.map((item, index) => /* @__PURE__ */ jsx(
                  TimelineItem,
                  {
                    ...item,
                    index,
                    trackId: "cue-track",
                    zoom,
                    selected: selectedId === item.id || coSelectedId === item.id,
                    locked: !!item.lockedToVideoId,
                    showGearIcon: item.variant === "ad" && !!item.lockedToVideoId,
                    onGearClick: () => onCuePointConfigure?.(item.id),
                    onClick: () => onItemSelect?.(item.id),
                    onResize: item.lockedToVideoId ? void 0 : onItemResize
                  },
                  item.id
                )),
                insertionIndicator && insertionIndicator.trackId === "cue-track" && /* @__PURE__ */ jsx(
                  "div",
                  {
                    className: "absolute top-0 bottom-0 z-50 w-1 bg-yak-orange animate-pulse shadow-[0_0_8px_rgba(255,153,0,0.8)]",
                    style: {
                      left: `${insertionIndicator.time / 100 * zoom}rem`
                    }
                  }
                )
              ] }),
              /* @__PURE__ */ jsx(
                "div",
                {
                  className: "pointer-events-none absolute top-0 bottom-0 z-40 w-px bg-primary shadow-[0_0_8px_rgba(var(--primary),0.5)]",
                  style: {
                    left: `calc(${currentTime / 100 * zoom}rem + 8rem)`,
                    // +8rem for the track label width
                    transition: "left 75ms linear"
                  }
                }
              )
            ]
          }
        )
      }
    )
  ] });
}
function calculateDrawParams(canvasWidth, canvasHeight, frameWidth, frameHeight) {
  const canvasAspect = canvasWidth / canvasHeight;
  const frameAspect = frameWidth / frameHeight;
  let drawWidth;
  let drawHeight;
  let drawX;
  let drawY;
  if (frameAspect > canvasAspect) {
    drawHeight = canvasHeight;
    drawWidth = canvasHeight * frameAspect;
    drawX = (canvasWidth - drawWidth) / 2;
    drawY = 0;
  } else {
    drawWidth = canvasWidth;
    drawHeight = canvasWidth / frameAspect;
    drawX = 0;
    drawY = (canvasHeight - drawHeight) / 2;
  }
  return {
    x: Math.round(drawX),
    y: Math.round(drawY),
    width: Math.round(drawWidth),
    height: Math.round(drawHeight),
    sourceWidth: frameWidth,
    sourceHeight: frameHeight
  };
}
var ScrubPreview = React44.forwardRef(
  function ScrubPreview2({
    frame,
    isActive = false,
    width,
    height,
    opacity = 1,
    className = "",
    onReady
  }, ref) {
    const canvasRef = React44.useRef(null);
    const bufferCanvasRef = React44.useRef(null);
    const ctxRef = React44.useRef(null);
    const bufferCtxRef = React44.useRef(null);
    const hasFrameRef = React44.useRef(false);
    const readyCalledRef = React44.useRef(false);
    const isActiveRef = React44.useRef(isActive);
    const drawParamsRef = React44.useRef(null);
    const rafIdRef = React44.useRef(null);
    const lastDrawTimeRef = React44.useRef(0);
    const pendingFrameRef = React44.useRef(null);
    const MIN_DRAW_INTERVAL_MS = 16;
    const getBufferCanvas = React44.useCallback(() => {
      if (!bufferCanvasRef.current) {
        bufferCanvasRef.current = document.createElement("canvas");
        bufferCtxRef.current = bufferCanvasRef.current.getContext("2d", {
          alpha: false,
          desynchronized: true
        });
      }
      return bufferCanvasRef.current;
    }, []);
    const getDrawParams = React44.useCallback(
      (frameWidth, frameHeight) => {
        const cached = drawParamsRef.current;
        if (cached && cached.sourceWidth === frameWidth && cached.sourceHeight === frameHeight) {
          return cached;
        }
        const params = calculateDrawParams(width, height, frameWidth, frameHeight);
        drawParamsRef.current = params;
        return params;
      },
      [width, height]
    );
    const storeFrameInBuffer = React44.useCallback(
      (bitmap) => {
        const buffer = getBufferCanvas();
        const ctx = bufferCtxRef.current;
        if (!ctx) return false;
        if (buffer.width !== bitmap.width || buffer.height !== bitmap.height) {
          buffer.width = bitmap.width;
          buffer.height = bitmap.height;
        }
        try {
          ctx.drawImage(bitmap, 0, 0);
          hasFrameRef.current = true;
          return true;
        } catch (e) {
          console.warn("[ScrubPreview] Failed to store frame in buffer:", e);
          return false;
        }
      },
      [getBufferCanvas]
    );
    const drawFromBuffer = React44.useCallback(() => {
      const canvas = canvasRef.current;
      const buffer = bufferCanvasRef.current;
      const ctx = ctxRef.current;
      if (!canvas || !buffer || !ctx || !hasFrameRef.current) return;
      const params = getDrawParams(buffer.width, buffer.height);
      ctx.clearRect(0, 0, width, height);
      ctx.drawImage(buffer, params.x, params.y, params.width, params.height);
    }, [width, height, getDrawParams]);
    const drawFrame = React44.useCallback(
      (bitmap) => {
        const ctx = ctxRef.current;
        if (!ctx) return false;
        const params = getDrawParams(bitmap.width, bitmap.height);
        try {
          ctx.clearRect(0, 0, width, height);
          ctx.drawImage(bitmap, params.x, params.y, params.width, params.height);
          return true;
        } catch (e) {
          console.warn("[ScrubPreview] Failed to draw frame:", e);
          return false;
        }
      },
      [width, height, getDrawParams]
    );
    const clearCanvas = React44.useCallback(() => {
      const ctx = ctxRef.current;
      if (!ctx) return;
      ctx.clearRect(0, 0, width, height);
    }, [width, height]);
    const performDraw = React44.useCallback(() => {
      rafIdRef.current = null;
      lastDrawTimeRef.current = performance.now();
      const pendingFrame = pendingFrameRef.current;
      if (pendingFrame) {
        pendingFrameRef.current = null;
        const drawn = drawFrame(pendingFrame);
        if (drawn) {
          storeFrameInBuffer(pendingFrame);
          if (!readyCalledRef.current) {
            readyCalledRef.current = true;
            onReady?.();
          }
          return;
        } else if (hasFrameRef.current) {
          drawFromBuffer();
          return;
        }
      }
      if (hasFrameRef.current) {
        drawFromBuffer();
      } else {
        clearCanvas();
      }
    }, [drawFrame, drawFromBuffer, storeFrameInBuffer, clearCanvas, onReady]);
    const scheduleDraw = React44.useCallback(
      (newFrame) => {
        if (newFrame) {
          pendingFrameRef.current = newFrame;
        }
        if (rafIdRef.current !== null) {
          return;
        }
        const now = performance.now();
        const timeSinceLastDraw = now - lastDrawTimeRef.current;
        if (timeSinceLastDraw >= MIN_DRAW_INTERVAL_MS) {
          rafIdRef.current = requestAnimationFrame(performDraw);
        } else {
          const delay = MIN_DRAW_INTERVAL_MS - timeSinceLastDraw;
          setTimeout(() => {
            if (rafIdRef.current === null) {
              rafIdRef.current = requestAnimationFrame(performDraw);
            }
          }, delay);
        }
      },
      [performDraw]
    );
    const updateOpacity = React44.useCallback(
      (active) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.style.opacity = active ? String(opacity) : "0";
        canvas.style.transition = active ? "none" : "opacity 150ms ease-out";
        canvas.style.willChange = active ? "contents" : "auto";
      },
      [opacity]
    );
    React44.useImperativeHandle(
      ref,
      () => ({
        setFrame: (newFrame) => {
          if (!isActiveRef.current) return;
          scheduleDraw(newFrame);
        },
        setActive: (active) => {
          const wasActive = isActiveRef.current;
          isActiveRef.current = active;
          updateOpacity(active);
          if (!active && wasActive) {
            if (rafIdRef.current !== null) {
              cancelAnimationFrame(rafIdRef.current);
              rafIdRef.current = null;
            }
            if (hasFrameRef.current) {
              drawFromBuffer();
            }
          }
        }
      }),
      [scheduleDraw, updateOpacity, drawFromBuffer]
    );
    React44.useEffect(() => {
      isActiveRef.current = isActive;
    }, [isActive]);
    React44.useEffect(() => {
      if (!isActive) {
        if (rafIdRef.current !== null) {
          cancelAnimationFrame(rafIdRef.current);
          rafIdRef.current = null;
        }
        if (hasFrameRef.current) {
          drawFromBuffer();
        }
        return;
      }
      if (frame !== void 0) {
        scheduleDraw(frame ?? null);
      }
    }, [frame, isActive, scheduleDraw, drawFromBuffer]);
    React44.useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      const ctx = canvas.getContext("2d", {
        alpha: true,
        desynchronized: true
        // Reduces latency on supported browsers
      });
      if (ctx) {
        ctx.scale(dpr, dpr);
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "low";
        ctxRef.current = ctx;
      }
      drawParamsRef.current = null;
      if (hasFrameRef.current) {
        drawFromBuffer();
      }
    }, [width, height, drawFromBuffer]);
    React44.useEffect(() => {
      return () => {
        if (rafIdRef.current !== null) {
          cancelAnimationFrame(rafIdRef.current);
          rafIdRef.current = null;
        }
        bufferCanvasRef.current = null;
        bufferCtxRef.current = null;
        ctxRef.current = null;
        hasFrameRef.current = false;
        pendingFrameRef.current = null;
      };
    }, []);
    return /* @__PURE__ */ jsx(
      "canvas",
      {
        ref: canvasRef,
        className: `scrub-preview ${className}`,
        style: {
          position: "absolute",
          top: 0,
          left: 0,
          width: `${width}px`,
          height: `${height}px`,
          opacity: isActive ? opacity : 0,
          transition: isActive ? "none" : "opacity 150ms ease-out",
          pointerEvents: "none",
          zIndex: 10,
          // GPU acceleration hints
          willChange: isActive ? "contents" : "auto",
          // Force GPU layer
          transform: "translateZ(0)"
        },
        "aria-hidden": "true"
      }
    );
  }
);

// src/lib/FrameCache.ts
var DEFAULT_CONFIG = {
  maxEntries: 500,
  maxMemoryMB: 200,
  extractionFps: 2
};
function createCacheKey(videoUrl, timestamp, fps) {
  const interval = 1 / fps;
  const roundedTimestamp = Math.round(timestamp / interval) * interval;
  return `${videoUrl}::${roundedTimestamp.toFixed(3)}`;
}
function estimateImageBitmapSizeMB(width, height) {
  return width * height * 4 / (1024 * 1024);
}
var FrameCache = class {
  constructor(config) {
    this.cache = /* @__PURE__ */ new Map();
    this.totalMemoryMB = 0;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }
  /**
   * Get a frame from the cache
   */
  get(videoUrl, timestamp) {
    const key = createCacheKey(videoUrl, timestamp, this.config.extractionFps);
    const entry = this.cache.get(key);
    if (entry) {
      entry.lastAccess = Date.now();
      return entry.bitmap;
    }
    return null;
  }
  /**
   * Store a frame in the cache
   */
  set(videoUrl, timestamp, bitmap) {
    const key = createCacheKey(videoUrl, timestamp, this.config.extractionFps);
    if (this.cache.has(key)) {
      const existing = this.cache.get(key);
      this.totalMemoryMB -= existing.estimatedSizeMB;
      existing.bitmap.close();
    }
    const estimatedSizeMB = estimateImageBitmapSizeMB(bitmap.width, bitmap.height);
    while ((this.cache.size >= this.config.maxEntries || this.totalMemoryMB + estimatedSizeMB > this.config.maxMemoryMB) && this.cache.size > 0) {
      this.evictLRU(1);
    }
    const entry = {
      bitmap,
      width: bitmap.width,
      height: bitmap.height,
      videoUrl,
      timestamp,
      lastAccess: Date.now(),
      estimatedSizeMB
    };
    this.cache.set(key, entry);
    this.totalMemoryMB += estimatedSizeMB;
  }
  /**
   * Check if a frame exists in the cache
   */
  has(videoUrl, timestamp) {
    const key = createCacheKey(videoUrl, timestamp, this.config.extractionFps);
    return this.cache.has(key);
  }
  /**
   * Get the closest cached frame to the requested timestamp
   * Returns null if no frame is within maxDelta seconds
   */
  getClosestFrame(videoUrl, timestamp, maxDelta = 1) {
    const exact = this.get(videoUrl, timestamp);
    if (exact) {
      return { bitmap: exact, timestamp };
    }
    let closestEntry = null;
    let closestDelta = Infinity;
    for (const entry of this.cache.values()) {
      if (entry.videoUrl !== videoUrl) continue;
      const delta = Math.abs(entry.timestamp - timestamp);
      if (delta < closestDelta && delta <= maxDelta) {
        closestDelta = delta;
        closestEntry = entry;
      }
    }
    if (closestEntry) {
      closestEntry.lastAccess = Date.now();
      return { bitmap: closestEntry.bitmap, timestamp: closestEntry.timestamp };
    }
    return null;
  }
  /**
   * Get all cached frames within a time range for a video
   */
  getFrameRange(videoUrl, startTime, endTime) {
    const frames = [];
    for (const entry of this.cache.values()) {
      if (entry.videoUrl === videoUrl && entry.timestamp >= startTime && entry.timestamp <= endTime) {
        entry.lastAccess = Date.now();
        frames.push({ bitmap: entry.bitmap, timestamp: entry.timestamp });
      }
    }
    return frames.sort((a, b) => a.timestamp - b.timestamp);
  }
  /**
   * Evict least recently used frames
   */
  evictLRU(count = 1) {
    const sortedEntries = Array.from(this.cache.entries()).sort(
      ([, a], [, b]) => a.lastAccess - b.lastAccess
    );
    for (let i = 0; i < Math.min(count, sortedEntries.length); i++) {
      const [key, entry] = sortedEntries[i];
      entry.bitmap.close();
      this.totalMemoryMB -= entry.estimatedSizeMB;
      this.cache.delete(key);
    }
  }
  /**
   * Evict frames for a specific video, keeping frames near a specific time
   */
  evictVideoExcept(videoUrl, keepNearTime, keepCount = 10) {
    const videoEntries = Array.from(this.cache.entries()).filter(([, entry]) => entry.videoUrl === videoUrl).sort(([, a], [, b]) => {
      const deltaA = Math.abs(a.timestamp - keepNearTime);
      const deltaB = Math.abs(b.timestamp - keepNearTime);
      return deltaA - deltaB;
    });
    for (let i = keepCount; i < videoEntries.length; i++) {
      const [key, entry] = videoEntries[i];
      entry.bitmap.close();
      this.totalMemoryMB -= entry.estimatedSizeMB;
      this.cache.delete(key);
    }
  }
  /**
   * Get current memory usage statistics
   */
  getMemoryUsage() {
    return {
      entries: this.cache.size,
      estimatedMB: Math.round(this.totalMemoryMB * 100) / 100,
      maxMB: this.config.maxMemoryMB
    };
  }
  /**
   * Get extraction coverage for a video (0-1 percentage)
   */
  getCoverage(videoUrl, duration) {
    const interval = 1 / this.config.extractionFps;
    const expectedFrames = Math.ceil(duration / interval);
    let cachedFrames = 0;
    for (const entry of this.cache.values()) {
      if (entry.videoUrl === videoUrl) {
        cachedFrames++;
      }
    }
    return Math.min(1, cachedFrames / expectedFrames);
  }
  /**
   * Get list of timestamps that need extraction for a video
   */
  getMissingTimestamps(videoUrl, duration) {
    const interval = 1 / this.config.extractionFps;
    const missing = [];
    for (let t = 0; t <= duration; t += interval) {
      if (!this.has(videoUrl, t)) {
        missing.push(t);
      }
    }
    return missing;
  }
  /**
   * Clear all frames for a specific video
   */
  clearVideo(videoUrl) {
    const keysToDelete = [];
    for (const [key, entry] of this.cache.entries()) {
      if (entry.videoUrl === videoUrl) {
        entry.bitmap.close();
        this.totalMemoryMB -= entry.estimatedSizeMB;
        keysToDelete.push(key);
      }
    }
    for (const key of keysToDelete) {
      this.cache.delete(key);
    }
  }
  /**
   * Clear all cached frames
   */
  clear() {
    for (const entry of this.cache.values()) {
      entry.bitmap.close();
    }
    this.cache.clear();
    this.totalMemoryMB = 0;
  }
  /**
   * Get the extraction FPS setting
   */
  getExtractionFps() {
    return this.config.extractionFps;
  }
  /**
   * Get all unique video URLs in the cache
   */
  getVideos() {
    const videos = /* @__PURE__ */ new Set();
    for (const entry of this.cache.values()) {
      videos.add(entry.videoUrl);
    }
    return Array.from(videos);
  }
};
var globalCacheInstance = null;
function getGlobalFrameCache(config) {
  if (!globalCacheInstance) {
    globalCacheInstance = new FrameCache(config);
  }
  return globalCacheInstance;
}
function resetGlobalFrameCache() {
  if (globalCacheInstance) {
    globalCacheInstance.clear();
    globalCacheInstance = null;
  }
}

// src/components/organisms/VideoPreview/useFrameExtraction.ts
var DEFAULT_SCRUB_FRAME_WIDTH = 320;
var supportsFastSeek = typeof HTMLVideoElement !== "undefined" && "fastSeek" in HTMLVideoElement.prototype;
var supportsWebCodecs = typeof VideoDecoder !== "undefined";
var videoPool = /* @__PURE__ */ new Map();
var videoLoadPromises = /* @__PURE__ */ new Map();
var webCodecsWorker = null;
var webCodecsWorkerUrl = null;
var webCodecsCallbacks = /* @__PURE__ */ new Map();
function getWebCodecsWorker(workerUrl) {
  if (!supportsWebCodecs) {
    return null;
  }
  if (webCodecsWorker && webCodecsWorkerUrl?.href === workerUrl.href) {
    return webCodecsWorker;
  }
  try {
    webCodecsWorker = new Worker(workerUrl, { type: "module" });
    webCodecsWorker.onmessage = (event) => {
      const message = event.data;
      const callbacks = webCodecsCallbacks.get(message.requestId);
      if (!callbacks) return;
      switch (message.type) {
        case "frame": {
          const frameMsg = message;
          callbacks.onFrame(frameMsg.timestamp, frameMsg.bitmap, frameMsg.isKeyframe);
          break;
        }
        case "progress": {
          const progressMsg = message;
          callbacks.onProgress(
            progressMsg.keyframesExtracted,
            progressMsg.totalFrames,
            progressMsg.phase
          );
          break;
        }
        case "error":
          callbacks.onError(message.error);
          break;
      }
    };
    webCodecsWorker.onerror = (error) => {
      console.error("[WebCodecs Worker] Error:", error);
      webCodecsWorker = null;
    };
    webCodecsWorkerUrl = workerUrl;
    return webCodecsWorker;
  } catch (e) {
    console.warn("[useFrameExtraction] Failed to create WebCodecs worker:", e);
    return null;
  }
}
async function getVideo(url) {
  if (videoPool.has(url)) {
    return videoPool.get(url);
  }
  if (videoLoadPromises.has(url)) {
    return videoLoadPromises.get(url);
  }
  const loadPromise = new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.crossOrigin = "anonymous";
    video.muted = true;
    video.preload = "auto";
    video.playsInline = true;
    const onLoadedMetadata = () => {
      cleanup();
      videoPool.set(url, video);
      resolve(video);
    };
    const onError = () => {
      cleanup();
      reject(new Error(`Failed to load video: ${url}`));
    };
    const cleanup = () => {
      video.removeEventListener("loadedmetadata", onLoadedMetadata);
      video.removeEventListener("error", onError);
      videoLoadPromises.delete(url);
    };
    video.addEventListener("loadedmetadata", onLoadedMetadata);
    video.addEventListener("error", onError);
    video.src = url;
    video.load();
  });
  videoLoadPromises.set(url, loadPromise);
  return loadPromise;
}
async function extractFrame(video, timestamp, targetWidth, targetHeight) {
  return new Promise((resolve, reject) => {
    const seekTime = Math.max(0, Math.min(timestamp, video.duration - 0.1));
    const onSeeked = async () => {
      video.removeEventListener("seeked", onSeeked);
      video.removeEventListener("error", onError);
      try {
        const options = {
          resizeQuality: "medium"
        };
        if (targetWidth && targetHeight) {
          options.resizeWidth = targetWidth;
          options.resizeHeight = targetHeight;
        } else if (targetWidth) {
          options.resizeWidth = targetWidth;
          options.resizeHeight = Math.round(targetWidth / video.videoWidth * video.videoHeight);
        }
        const bitmap = await createImageBitmap(video, options);
        resolve(bitmap);
      } catch (e) {
        reject(e);
      }
    };
    const onError = () => {
      video.removeEventListener("seeked", onSeeked);
      video.removeEventListener("error", onError);
      reject(new Error(`Failed to seek to ${timestamp}`));
    };
    video.addEventListener("seeked", onSeeked);
    video.addEventListener("error", onError);
    if (supportsFastSeek) {
      video.fastSeek(seekTime);
    } else {
      video.currentTime = seekTime;
    }
  });
}
function useFrameExtraction(options) {
  const {
    sources,
    frameWidth = DEFAULT_SCRUB_FRAME_WIDTH,
    // Default to 320px for faster extraction
    frameHeight,
    extractionFps = 2,
    autoExtract = true,
    onProgress,
    onScrubReady,
    workerUrl
  } = options;
  const cacheRef = useRef(null);
  const extractedCountRef = useRef(0);
  const totalFramesRef = useRef(0);
  const scrubReadyCalledRef = useRef(false);
  const isExtractingRef = useRef(false);
  const abortControllerRef = useRef(null);
  const webCodecsRequestIdRef = useRef(null);
  const useWebCodecsRef = useRef(false);
  const [isScrubReady, setIsScrubReady] = useState(false);
  const [extractionProgress, setExtractionProgress] = useState(0);
  const [memoryUsage, setMemoryUsage] = useState({ entries: 0, estimatedMB: 0, maxMB: 200 });
  const findSourceForTime = useCallback(
    (timeMs) => {
      for (const source of sources) {
        const sourceEndTime = source.startTime + source.duration;
        if (timeMs >= source.startTime && timeMs < sourceEndTime) {
          const offsetInSource = (timeMs - source.startTime) / 1e3;
          const inPoint = source.inPoint ?? 0;
          const localTime = inPoint + offsetInSource;
          return { source, localTime };
        }
      }
      return null;
    },
    [sources]
  );
  const getFrameForTime = useCallback(
    (timeMs) => {
      if (!cacheRef.current) return null;
      const result = findSourceForTime(timeMs);
      if (!result) return null;
      const { source, localTime } = result;
      const exactFrame = cacheRef.current.get(source.url, localTime);
      if (exactFrame) return exactFrame;
      const closest = cacheRef.current.getClosestFrame(source.url, localTime, 1);
      return closest?.bitmap ?? null;
    },
    [findSourceForTime]
  );
  const extractFramesForVideo = useCallback(
    async (videoUrl, timestamps, signal) => {
      if (!cacheRef.current || timestamps.length === 0) return;
      try {
        const video = await getVideo(videoUrl);
        let targetWidth = frameWidth;
        let targetHeight = frameHeight;
        if (targetWidth && !targetHeight) {
          targetHeight = Math.round(targetWidth / video.videoWidth * video.videoHeight);
        } else if (targetHeight && !targetWidth) {
          targetWidth = Math.round(targetHeight / video.videoHeight * video.videoWidth);
        }
        for (const timestamp of timestamps) {
          if (signal?.aborted) return;
          if (cacheRef.current.has(videoUrl, timestamp)) continue;
          try {
            await new Promise((resolve) => {
              if ("requestIdleCallback" in window) {
                requestIdleCallback(() => resolve(), { timeout: 100 });
              } else {
                setTimeout(resolve, 0);
              }
            });
            if (signal?.aborted) return;
            const bitmap = await extractFrame(video, timestamp, targetWidth, targetHeight);
            cacheRef.current?.set(videoUrl, timestamp, bitmap);
            extractedCountRef.current += 1;
            if (totalFramesRef.current > 0) {
              const progress = extractedCountRef.current / totalFramesRef.current;
              setExtractionProgress(Math.min(1, progress));
              onProgress?.(progress);
            }
            if (extractedCountRef.current % 10 === 0 && cacheRef.current) {
              setMemoryUsage(cacheRef.current.getMemoryUsage());
            }
          } catch (frameError) {
            console.warn(`[FrameExtraction] Failed to extract frame at ${timestamp}:`, frameError);
          }
        }
      } catch (videoError) {
        console.error(`[FrameExtraction] Failed to load video ${videoUrl}:`, videoError);
      }
    },
    [frameWidth, frameHeight, onProgress]
  );
  const prioritizeTime = useCallback(
    (timeMs) => {
      const result = findSourceForTime(timeMs);
      if (!result) return;
      const { source, localTime } = result;
      if (cacheRef.current?.has(source.url, localTime)) return;
      if (useWebCodecsRef.current && webCodecsRequestIdRef.current && webCodecsWorker) {
        const request = {
          type: "prioritize",
          requestId: webCodecsRequestIdRef.current,
          timestamp: timeMs
        };
        webCodecsWorker.postMessage(request);
        return;
      }
      const interval = 1 / extractionFps;
      const timestamps = [];
      for (let offset = 0; offset <= 2; offset += interval) {
        timestamps.push(localTime + offset);
        if (offset > 0) timestamps.push(localTime - offset);
      }
      const inPoint = source.inPoint ?? 0;
      const outPoint = source.outPoint ?? source.duration / 1e3;
      const validTimestamps = [...new Set(timestamps)].filter((t) => t >= inPoint && t <= outPoint).sort((a, b) => Math.abs(a - localTime) - Math.abs(b - localTime));
      if (validTimestamps.length > 0) {
        extractFramesForVideo(source.url, validTimestamps).catch(() => {
        });
      }
    },
    [findSourceForTime, extractionFps, extractFramesForVideo]
  );
  const preloadAroundTime = useCallback(
    (timeMs, rangeMs = 5e3) => {
      const result = findSourceForTime(timeMs);
      if (!result || !cacheRef.current) return;
      const { source, localTime } = result;
      const interval = 1 / extractionFps;
      const rangeSeconds = rangeMs / 1e3;
      const timestamps = [];
      const inPoint = source.inPoint ?? 0;
      const outPoint = source.outPoint ?? source.duration / 1e3;
      for (let t = localTime - rangeSeconds; t <= localTime + rangeSeconds; t += interval) {
        if (t >= inPoint && t <= outPoint && !cacheRef.current.has(source.url, t)) {
          timestamps.push(t);
        }
      }
      if (timestamps.length > 0) {
        extractFramesForVideo(source.url, timestamps).catch(() => {
        });
      }
    },
    [findSourceForTime, extractionFps, extractFramesForVideo]
  );
  const clearCache = useCallback(() => {
    if (cacheRef.current) {
      cacheRef.current.clear();
      setMemoryUsage({ entries: 0, estimatedMB: 0, maxMB: 200 });
    }
  }, []);
  const calculateTotalFrames = useCallback(() => {
    let total = 0;
    const interval = 1 / extractionFps;
    for (const source of sources) {
      const inPoint = source.inPoint ?? 0;
      const outPoint = source.outPoint ?? source.duration / 1e3;
      const duration = outPoint - inPoint;
      total += Math.ceil(duration / interval);
    }
    return total;
  }, [sources, extractionFps]);
  const startWebCodecsExtraction = useCallback(() => {
    if (!workerUrl || sources.length === 0) return false;
    const worker = getWebCodecsWorker(workerUrl);
    if (!worker) return false;
    isExtractingRef.current = true;
    useWebCodecsRef.current = true;
    for (const source of sources) {
      const requestId = `webcodecs_${source.url}_${Date.now()}`;
      webCodecsRequestIdRef.current = requestId;
      webCodecsCallbacks.set(requestId, {
        onFrame: (timestamp, bitmap, isKeyframe) => {
          const timestampSeconds = timestamp / 1e3;
          cacheRef.current?.set(source.url, timestampSeconds, bitmap);
          extractedCountRef.current += 1;
          if (totalFramesRef.current > 0) {
            const progress = extractedCountRef.current / totalFramesRef.current;
            setExtractionProgress(Math.min(1, progress));
            onProgress?.(progress);
          }
          if (isKeyframe && !scrubReadyCalledRef.current) {
            scrubReadyCalledRef.current = true;
            setIsScrubReady(true);
            onScrubReady?.();
          }
          if (extractedCountRef.current % 10 === 0 && cacheRef.current) {
            setMemoryUsage(cacheRef.current.getMemoryUsage());
          }
        },
        onProgress: (keyframes, total, phase) => {
          if (phase === "complete") {
            isExtractingRef.current = false;
            webCodecsCallbacks.delete(requestId);
          }
        },
        onError: (error) => {
          console.warn("[useFrameExtraction] WebCodecs error:", error);
          useWebCodecsRef.current = false;
          webCodecsCallbacks.delete(requestId);
          startExtraction();
        }
      });
      const inPoint = source.inPoint ?? 0;
      const outPoint = source.outPoint ?? source.duration / 1e3;
      const duration = outPoint - inPoint;
      const request = {
        type: "start",
        requestId,
        videoUrl: source.url,
        frameWidth,
        frameInterval: 1 / extractionFps,
        duration
      };
      worker.postMessage(request);
    }
    return true;
  }, [workerUrl, sources, frameWidth, extractionFps, onProgress, onScrubReady]);
  const startExtraction = useCallback(async () => {
    if (isExtractingRef.current || sources.length === 0) return;
    isExtractingRef.current = true;
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;
    const interval = 1 / extractionFps;
    const initialDuration = 5;
    try {
      for (const source of sources) {
        if (signal.aborted) return;
        const inPoint = source.inPoint ?? 0;
        const outPoint = source.outPoint ?? source.duration / 1e3;
        const boundaryTimestamps = [inPoint, Math.max(inPoint, outPoint - 0.1)];
        await extractFramesForVideo(source.url, boundaryTimestamps, signal);
        const initialTimestamps = [];
        for (let t = inPoint; t < Math.min(inPoint + initialDuration, outPoint); t += interval) {
          initialTimestamps.push(t);
        }
        await extractFramesForVideo(source.url, initialTimestamps, signal);
      }
      if (!scrubReadyCalledRef.current) {
        scrubReadyCalledRef.current = true;
        setIsScrubReady(true);
        onScrubReady?.();
      }
      for (const source of sources) {
        if (signal.aborted) return;
        const inPoint = source.inPoint ?? 0;
        const outPoint = source.outPoint ?? source.duration / 1e3;
        const allTimestamps = [];
        for (let t = inPoint; t <= outPoint; t += interval) {
          if (!cacheRef.current?.has(source.url, t)) {
            allTimestamps.push(t);
          }
        }
        const batchSize = 10;
        for (let i = 0; i < allTimestamps.length; i += batchSize) {
          if (signal.aborted) return;
          const batch = allTimestamps.slice(i, i + batchSize);
          await extractFramesForVideo(source.url, batch, signal);
        }
      }
    } finally {
      isExtractingRef.current = false;
    }
  }, [sources, extractionFps, extractFramesForVideo, onScrubReady]);
  useEffect(() => {
    cacheRef.current = getGlobalFrameCache({ extractionFps });
    totalFramesRef.current = calculateTotalFrames();
    if (autoExtract && sources.length > 0) {
      const timer = setTimeout(() => {
        const webCodecsStarted = startWebCodecsExtraction();
        if (!webCodecsStarted) {
          startExtraction();
        }
      }, 100);
      return () => {
        clearTimeout(timer);
        abortControllerRef.current?.abort();
        if (webCodecsRequestIdRef.current && webCodecsWorker) {
          const stopRequest = {
            type: "stop",
            requestId: webCodecsRequestIdRef.current
          };
          webCodecsWorker.postMessage(stopRequest);
          webCodecsCallbacks.delete(webCodecsRequestIdRef.current);
        }
      };
    } else {
      setIsScrubReady(true);
    }
    return () => {
      abortControllerRef.current?.abort();
    };
  }, [sources, extractionFps, autoExtract, calculateTotalFrames, startExtraction, startWebCodecsExtraction]);
  useEffect(() => {
    const interval = setInterval(() => {
      if (cacheRef.current) {
        setMemoryUsage(cacheRef.current.getMemoryUsage());
      }
    }, 5e3);
    return () => clearInterval(interval);
  }, []);
  return {
    getFrameForTime,
    isScrubReady,
    extractionProgress,
    prioritizeTime,
    memoryUsage,
    preloadAroundTime,
    clearCache
  };
}
var SCRUB_THROTTLE_MS = 16;
function VideoPreview({
  sources,
  currentTime,
  playing = false,
  muted = true,
  isScrubbing = false,
  onTimeUpdate,
  onPlayingChange,
  onScrubReady,
  onExtractionProgress,
  workerUrl,
  className
}) {
  const videoRef = React44.useRef(null);
  const containerRef = React44.useRef(null);
  const hlsRef = React44.useRef(null);
  const scrubPreviewRef = React44.useRef(null);
  const loadedSourceRef = React44.useRef(null);
  const isTransitioningRef = React44.useRef(false);
  const scrubTimeRef = React44.useRef(currentTime);
  const lastScrubUpdateRef = React44.useRef(0);
  const scrubRafRef = React44.useRef(null);
  const isScrubbingRef = React44.useRef(isScrubbing);
  isScrubbingRef.current = isScrubbing;
  const [dimensions, setDimensions] = React44.useState({ width: 0, height: 0 });
  const {
    getFrameForTime: getExtractedFrame,
    isScrubReady,
    extractionProgress,
    prioritizeTime,
    preloadAroundTime
  } = useFrameExtraction({
    sources,
    frameWidth: 320,
    // Reduced from 1280 for faster extraction
    autoExtract: true,
    onProgress: onExtractionProgress,
    onScrubReady,
    workerUrl
  });
  const getFrameForTime = React44.useCallback(
    (timeMs) => {
      return getExtractedFrame(timeMs);
    },
    [getExtractedFrame]
  );
  const updateScrubPreviewFrame = React44.useCallback(
    (timeMs) => {
      if (!scrubPreviewRef.current) return;
      const frame = getFrameForTime(timeMs);
      if (frame) {
        scrubPreviewRef.current.setFrame(frame);
      }
    },
    [getFrameForTime]
  );
  const throttledPrioritize = React44.useCallback(
    (timeMs) => {
      const now = performance.now();
      if (now - lastScrubUpdateRef.current < SCRUB_THROTTLE_MS) {
        return;
      }
      lastScrubUpdateRef.current = now;
      prioritizeTime(timeMs);
    },
    [prioritizeTime]
  );
  const scheduleScubUpdate = React44.useCallback(() => {
    if (scrubRafRef.current !== null) {
      return;
    }
    scrubRafRef.current = requestAnimationFrame(() => {
      scrubRafRef.current = null;
      if (!isScrubbingRef.current) return;
      const timeMs = scrubTimeRef.current;
      updateScrubPreviewFrame(timeMs);
      throttledPrioritize(timeMs);
    });
  }, [updateScrubPreviewFrame, throttledPrioritize]);
  React44.useEffect(() => {
    scrubTimeRef.current = currentTime;
    if (isScrubbing) {
      scheduleScubUpdate();
    }
  }, [currentTime, isScrubbing, scheduleScubUpdate]);
  React44.useEffect(() => {
    return () => {
      if (scrubRafRef.current !== null) {
        cancelAnimationFrame(scrubRafRef.current);
        scrubRafRef.current = null;
      }
    };
  }, []);
  React44.useEffect(() => {
    if (scrubPreviewRef.current) {
      scrubPreviewRef.current.setActive(isScrubbing);
      if (isScrubbing) {
        updateScrubPreviewFrame(currentTime);
      }
    }
  }, [isScrubbing, currentTime, updateScrubPreviewFrame]);
  React44.useEffect(() => {
    if (!isScrubbing && !playing) {
      preloadAroundTime(currentTime);
    }
  }, [isScrubbing, playing, currentTime, preloadAroundTime]);
  React44.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const updateDimensions = () => {
      setDimensions({
        width: container.clientWidth,
        height: container.clientHeight
      });
    };
    updateDimensions();
    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, []);
  const sortedSources = React44.useMemo(() => {
    return [...sources].sort((a, b) => a.startTime - b.startTime);
  }, [sources]);
  const totalDuration = React44.useMemo(() => {
    return sortedSources.reduce((acc, s) => Math.max(acc, s.startTime + s.duration), 0);
  }, [sortedSources]);
  const { activeSource, activeSourceIndex } = React44.useMemo(() => {
    const index = sortedSources.findIndex(
      (s) => currentTime >= s.startTime && currentTime < s.startTime + s.duration
    );
    return {
      activeSource: index >= 0 ? sortedSources[index] : null,
      activeSourceIndex: index
    };
  }, [sortedSources, currentTime]);
  const nextSource = React44.useMemo(() => {
    if (activeSourceIndex >= 0 && activeSourceIndex < sortedSources.length - 1) {
      return sortedSources[activeSourceIndex + 1];
    }
    return null;
  }, [sortedSources, activeSourceIndex]);
  const findNextSourceFromPosition = React44.useCallback((position) => {
    return sortedSources.find((s) => s.startTime >= position) || null;
  }, [sortedSources]);
  const currentTimeRef = React44.useRef(currentTime);
  currentTimeRef.current = currentTime;
  const playingRef = React44.useRef(playing);
  playingRef.current = playing;
  React44.useEffect(() => {
    const video = videoRef.current;
    if (!video || !activeSource) {
      loadedSourceRef.current = null;
      return;
    }
    const sourceUrl = activeSource.url;
    if (!sourceUrl) return;
    const loaded = loadedSourceRef.current;
    if (loaded && loaded.url === sourceUrl && loaded.startTime === activeSource.startTime) {
      return;
    }
    isTransitioningRef.current = true;
    const loadingSource = activeSource;
    const onSourceReady = () => {
      loadedSourceRef.current = loadingSource;
      const currentTimeMs = currentTimeRef.current;
      const isPlaying = playingRef.current;
      const inPoint = loadingSource.inPoint ?? 0;
      const timeIntoSegment = Math.max(0, (currentTimeMs - loadingSource.startTime) / 1e3);
      const localTime = inPoint + timeIntoSegment;
      video.currentTime = localTime;
      isTransitioningRef.current = false;
      if (isPlaying) {
        video.play().catch(() => onPlayingChange?.(false));
      }
    };
    const isHls = sourceUrl.endsWith(".m3u8");
    if (isHls && Hls.isSupported()) {
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }
      const hls = new Hls();
      hls.loadSource(sourceUrl);
      hls.attachMedia(video);
      hlsRef.current = hls;
      hls.on(Events.MANIFEST_PARSED, onSourceReady);
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = sourceUrl;
      video.addEventListener("canplay", onSourceReady, { once: true });
    } else {
      video.src = sourceUrl;
      video.addEventListener("canplay", onSourceReady, { once: true });
    }
  }, [activeSource, activeSource?.url, activeSource?.startTime, onPlayingChange]);
  React44.useEffect(() => {
    const video = videoRef.current;
    if (!video || !activeSource || isTransitioningRef.current) return;
    const inPoint = activeSource.inPoint ?? 0;
    const timeIntoSegment = Math.max(0, (currentTime - activeSource.startTime) / 1e3);
    const localTime = inPoint + timeIntoSegment;
    if (Math.abs(video.currentTime - localTime) > 0.3) {
      video.currentTime = localTime;
    }
  }, [currentTime, activeSource]);
  React44.useEffect(() => {
    const video = videoRef.current;
    if (!video || isTransitioningRef.current) return;
    if (playing && activeSource) {
      video.play().catch(() => onPlayingChange?.(false));
    } else {
      video.pause();
    }
  }, [playing, activeSource, onPlayingChange]);
  React44.useEffect(() => {
    if (!playing || activeSource || isTransitioningRef.current) return;
    const nextFromPosition = findNextSourceFromPosition(currentTime);
    if (nextFromPosition) {
      onTimeUpdate?.(nextFromPosition.startTime);
    } else {
      onPlayingChange?.(false);
    }
  }, [playing, activeSource, currentTime, findNextSourceFromPosition, onTimeUpdate, onPlayingChange]);
  const handleTimeUpdate = React44.useCallback(() => {
    const video = videoRef.current;
    const loadedSource = loadedSourceRef.current;
    if (!video || !activeSource || !playing || isTransitioningRef.current) return;
    if (!loadedSource || loadedSource.url !== activeSource.url || loadedSource.startTime !== activeSource.startTime) return;
    const inPoint = loadedSource.inPoint ?? 0;
    const timeIntoSource = video.currentTime - inPoint;
    const absoluteTime = loadedSource.startTime + timeIntoSource * 1e3;
    const outPoint = loadedSource.outPoint;
    const reachedOutPoint = outPoint !== void 0 && video.currentTime >= outPoint - 0.1;
    const segmentEnd = loadedSource.startTime + loadedSource.duration;
    if (reachedOutPoint || absoluteTime >= segmentEnd - 100) {
      if (nextSource) {
        onTimeUpdate?.(nextSource.startTime);
      } else {
        onTimeUpdate?.(Math.min(absoluteTime, totalDuration));
      }
    } else {
      onTimeUpdate?.(absoluteTime);
    }
  }, [activeSource, playing, nextSource, totalDuration, onTimeUpdate]);
  const handleVideoEnded = React44.useCallback(() => {
    const loadedSource = loadedSourceRef.current;
    if (!loadedSource) {
      onPlayingChange?.(false);
      return;
    }
    const segmentEnd = loadedSource.startTime + loadedSource.duration;
    const loadedIndex = sortedSources.findIndex(
      (s) => s.url === loadedSource.url && s.startTime === loadedSource.startTime
    );
    const nextAfterLoaded = loadedIndex >= 0 && loadedIndex < sortedSources.length - 1 ? sortedSources[loadedIndex + 1] : null;
    if (nextAfterLoaded) {
      onTimeUpdate?.(nextAfterLoaded.startTime);
    } else {
      onTimeUpdate?.(segmentEnd);
      onPlayingChange?.(false);
    }
  }, [sortedSources, onTimeUpdate, onPlayingChange]);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ref: containerRef,
      className: cn("relative aspect-video overflow-hidden rounded-lg bg-black shadow-lg", className),
      children: [
        /* @__PURE__ */ jsx(
          "video",
          {
            ref: videoRef,
            className: cn(
              "h-full w-full [&::-webkit-media-controls]:hidden [&::-webkit-media-controls-enclosure]:hidden",
              // Fade out video when scrubbing (ScrubPreview shows cached frame)
              isScrubbing && "opacity-0"
            ),
            style: {
              transition: isScrubbing ? "none" : "opacity 150ms ease-in"
            },
            controls: false,
            muted,
            onTimeUpdate: handleTimeUpdate,
            onEnded: handleVideoEnded,
            playsInline: true
          }
        ),
        dimensions.width > 0 && /* @__PURE__ */ jsx(
          ScrubPreview,
          {
            ref: scrubPreviewRef,
            width: dimensions.width,
            height: dimensions.height
          }
        ),
        !activeSource && !playing && !isScrubbing && /* @__PURE__ */ jsx("div", { className: "absolute inset-0 flex items-center justify-center bg-black/40", children: /* @__PURE__ */ jsx("p", { className: "text-sm text-white/60", children: "No video at this position" }) }),
        !isScrubReady && extractionProgress < 1 && /* @__PURE__ */ jsxs("div", { className: "absolute bottom-2 right-2 flex items-center gap-2 rounded bg-black/60 px-2 py-1 text-xs text-white/80", children: [
          /* @__PURE__ */ jsx("div", { className: "h-1 w-16 overflow-hidden rounded-full bg-white/20", children: /* @__PURE__ */ jsx(
            "div",
            {
              className: "h-full bg-blue-500 transition-all",
              style: { width: `${extractionProgress * 100}%` }
            }
          ) }),
          /* @__PURE__ */ jsx("span", { children: "Loading frames..." })
        ] })
      ]
    }
  );
}
function formatDuration(durationMs) {
  if (!durationMs) return null;
  const totalSeconds = Math.round(durationMs / 1e3);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor(totalSeconds % 3600 / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  }
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}
function formatCreatedAt(createdAt) {
  if (!createdAt) return null;
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return createdAt;
  return date.toISOString().slice(0, 10);
}
function formatTags(tags) {
  if (!tags || tags.length === 0) return null;
  const visibleTags = tags.slice(0, 3);
  const remaining = tags.length - visibleTags.length;
  return remaining > 0 ? `${visibleTags.join(", ")} +${remaining}` : visibleTags.join(", ");
}
function DraggableAsset({
  asset,
  selected,
  draggable = true,
  onDoubleClick,
  onClick,
  onDragStart
}) {
  const { listeners, setNodeRef, isDragging } = useDraggable({
    id: `media-${asset.id}`,
    data: {
      type: "media-asset",
      asset
    },
    disabled: !draggable
  });
  React44.useEffect(() => {
    if (isDragging && onDragStart) {
      onDragStart(asset);
    }
  }, [isDragging, asset, onDragStart]);
  const style = isDragging ? { opacity: 0.3 } : {};
  const handleDoubleClick = React44.useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    onDoubleClick?.(asset);
  }, [asset, onDoubleClick]);
  const handleClick = React44.useCallback((_e) => {
    if (!isDragging) {
      onClick?.(asset);
    }
  }, [asset, onClick, isDragging]);
  const dragProps = draggable ? listeners : {};
  const durationLabel = formatDuration(asset.duration);
  const createdLabel = formatCreatedAt(asset.createdAt);
  const tagsLabel = formatTags(asset.tags);
  return /* @__PURE__ */ jsxs(Tooltip, { delayDuration: 200, children: [
    /* @__PURE__ */ jsx(TooltipTrigger, { asChild: true, children: /* @__PURE__ */ jsxs(
      "div",
      {
        ref: setNodeRef,
        style,
        ...dragProps,
        role: "button",
        tabIndex: 0,
        "aria-label": `${asset.name} - ${asset.type}`,
        "aria-pressed": selected,
        className: cn(
          "group relative aspect-video overflow-hidden rounded-md border bg-muted transition-all hover:ring-2 hover:ring-primary/50",
          draggable && "cursor-grab active:cursor-grabbing",
          !draggable && "cursor-pointer",
          selected && "ring-2 ring-primary border-primary"
        ),
        onClick: handleClick,
        onDoubleClick: handleDoubleClick,
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick?.(asset);
          }
        },
        children: [
          asset.thumbnailUrl ? /* @__PURE__ */ jsx(
            "img",
            {
              src: asset.thumbnailUrl,
              alt: asset.name,
              className: "h-full w-full object-cover transition-transform group-hover:scale-105 pointer-events-none"
            }
          ) : /* @__PURE__ */ jsx("div", { className: "flex h-full w-full items-center justify-center bg-muted pointer-events-none", children: /* @__PURE__ */ jsx("span", { className: "text-[10px] font-medium text-muted-foreground uppercase", children: asset.type }) }),
          /* @__PURE__ */ jsxs("div", { className: "absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-1.5 opacity-0 transition-opacity group-hover:opacity-100 pointer-events-none", children: [
            /* @__PURE__ */ jsx("p", { className: "truncate text-[10px] font-medium text-white", children: asset.name }),
            asset.duration && /* @__PURE__ */ jsxs("p", { className: "text-[8px] text-white/80", children: [
              Math.floor(asset.duration / 1e3),
              "s"
            ] })
          ] }),
          selected && /* @__PURE__ */ jsx("div", { className: "absolute top-1 right-1 h-2 w-2 rounded-full bg-primary pointer-events-none" })
        ]
      }
    ) }),
    /* @__PURE__ */ jsx(TooltipContent, { side: "right", align: "start", className: "max-w-xs", children: /* @__PURE__ */ jsxs("div", { className: "space-y-1", children: [
      /* @__PURE__ */ jsx("p", { className: "text-sm font-semibold", children: asset.name }),
      /* @__PURE__ */ jsxs("div", { className: "space-y-0.5 text-[11px] text-muted-foreground", children: [
        /* @__PURE__ */ jsxs("p", { children: [
          /* @__PURE__ */ jsx("span", { className: "font-medium text-foreground", children: "Type:" }),
          " ",
          asset.type
        ] }),
        asset.category && /* @__PURE__ */ jsxs("p", { children: [
          /* @__PURE__ */ jsx("span", { className: "font-medium text-foreground", children: "Category:" }),
          " ",
          asset.category
        ] }),
        durationLabel && /* @__PURE__ */ jsxs("p", { children: [
          /* @__PURE__ */ jsx("span", { className: "font-medium text-foreground", children: "Duration:" }),
          " ",
          durationLabel
        ] }),
        createdLabel && /* @__PURE__ */ jsxs("p", { children: [
          /* @__PURE__ */ jsx("span", { className: "font-medium text-foreground", children: "Created:" }),
          " ",
          createdLabel
        ] }),
        tagsLabel && /* @__PURE__ */ jsxs("p", { children: [
          /* @__PURE__ */ jsx("span", { className: "font-medium text-foreground", children: "Tags:" }),
          " ",
          tagsLabel
        ] })
      ] })
    ] }) })
  ] });
}
function MediaGrid({
  assets,
  loading,
  onAssetSelect,
  onAssetDoubleClick,
  onDragStart,
  selectedIds = [],
  draggable = true,
  className
}) {
  const [search, setSearch] = React44.useState("");
  const filteredAssets = assets.filter(
    (a) => a.name.toLowerCase().includes(search.toLowerCase())
  );
  return /* @__PURE__ */ jsx(TooltipProvider, { children: /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-4", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "relative", children: [
      /* @__PURE__ */ jsx(Search, { className: "absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" }),
      /* @__PURE__ */ jsx(
        Input,
        {
          placeholder: "Search assets...",
          className: "pl-8",
          value: search,
          onChange: (e) => setSearch(e.target.value)
        }
      )
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-2 gap-3 overflow-y-auto pr-2 sm:grid-cols-3 md:grid-cols-2 lg:grid-cols-3", children: [
      loading ? Array.from({ length: 6 }).map((_, i) => /* @__PURE__ */ jsx(Skeleton, { className: "aspect-video w-full rounded-md" }, i)) : filteredAssets.map((asset) => /* @__PURE__ */ jsx(
        DraggableAsset,
        {
          asset,
          selected: selectedIds.includes(asset.id),
          draggable,
          onClick: onAssetSelect,
          onDoubleClick: onAssetDoubleClick,
          onDragStart
        },
        asset.id
      )),
      !loading && filteredAssets.length === 0 && /* @__PURE__ */ jsx("div", { className: "col-span-full py-8 text-center", children: /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "No assets found" }) })
    ] })
  ] }) });
}
var DropdownMenu = DropdownMenuPrimitive2.Root;
var DropdownMenuTrigger = DropdownMenuPrimitive2.Trigger;
var DropdownMenuGroup = DropdownMenuPrimitive2.Group;
var DropdownMenuPortal = DropdownMenuPrimitive2.Portal;
var DropdownMenuSub = DropdownMenuPrimitive2.Sub;
var DropdownMenuRadioGroup = DropdownMenuPrimitive2.RadioGroup;
var DropdownMenuSubTrigger = React44.forwardRef(({ className, inset, children, ...props }, ref) => /* @__PURE__ */ jsxs(
  DropdownMenuPrimitive2.SubTrigger,
  {
    ref,
    className: cn(
      "flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none",
      "focus:bg-accent data-[state=open]:bg-accent",
      inset && "pl-8",
      className
    ),
    ...props,
    children: [
      children,
      /* @__PURE__ */ jsx(ChevronRight, { className: "ml-auto h-4 w-4" })
    ]
  }
));
DropdownMenuSubTrigger.displayName = DropdownMenuPrimitive2.SubTrigger.displayName;
var DropdownMenuSubContent = React44.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DropdownMenuPrimitive2.SubContent,
  {
    ref,
    className: cn(
      "z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-lg",
      "data-[state=open]:animate-in data-[state=closed]:animate-out",
      "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
      "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2",
      "data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    ),
    ...props
  }
));
DropdownMenuSubContent.displayName = DropdownMenuPrimitive2.SubContent.displayName;
var DropdownMenuContent = React44.forwardRef(({ className, sideOffset = 4, ...props }, ref) => /* @__PURE__ */ jsx(DropdownMenuPrimitive2.Portal, { children: /* @__PURE__ */ jsx(
  DropdownMenuPrimitive2.Content,
  {
    ref,
    sideOffset,
    className: cn(
      "z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md",
      "data-[state=open]:animate-in data-[state=closed]:animate-out",
      "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
      "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2",
      "data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    ),
    ...props
  }
) }));
DropdownMenuContent.displayName = DropdownMenuPrimitive2.Content.displayName;
var DropdownMenuItem = React44.forwardRef(({ className, inset, destructive, ...props }, ref) => /* @__PURE__ */ jsx(
  DropdownMenuPrimitive2.Item,
  {
    ref,
    className: cn(
      "relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors",
      "focus:bg-accent focus:text-accent-foreground",
      "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      destructive && "text-destructive focus:bg-destructive/10 focus:text-destructive",
      inset && "pl-8",
      className
    ),
    ...props
  }
));
DropdownMenuItem.displayName = DropdownMenuPrimitive2.Item.displayName;
var DropdownMenuCheckboxItem = React44.forwardRef(({ className, children, checked, ...props }, ref) => /* @__PURE__ */ jsxs(
  DropdownMenuPrimitive2.CheckboxItem,
  {
    ref,
    className: cn(
      "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors",
      "focus:bg-accent focus:text-accent-foreground",
      "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    ),
    checked,
    ...props,
    children: [
      /* @__PURE__ */ jsx("span", { className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center", children: /* @__PURE__ */ jsx(DropdownMenuPrimitive2.ItemIndicator, { children: /* @__PURE__ */ jsx(Check, { className: "h-4 w-4" }) }) }),
      children
    ]
  }
));
DropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive2.CheckboxItem.displayName;
var DropdownMenuRadioItem = React44.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs(
  DropdownMenuPrimitive2.RadioItem,
  {
    ref,
    className: cn(
      "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors",
      "focus:bg-accent focus:text-accent-foreground",
      "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    ),
    ...props,
    children: [
      /* @__PURE__ */ jsx("span", { className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center", children: /* @__PURE__ */ jsx(DropdownMenuPrimitive2.ItemIndicator, { children: /* @__PURE__ */ jsx(Circle, { className: "h-2 w-2 fill-current" }) }) }),
      children
    ]
  }
));
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive2.RadioItem.displayName;
var DropdownMenuLabel = React44.forwardRef(({ className, inset, ...props }, ref) => /* @__PURE__ */ jsx(
  DropdownMenuPrimitive2.Label,
  {
    ref,
    className: cn(
      "px-2 py-1.5 text-sm font-semibold",
      inset && "pl-8",
      className
    ),
    ...props
  }
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive2.Label.displayName;
var DropdownMenuSeparator = React44.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DropdownMenuPrimitive2.Separator,
  {
    ref,
    className: cn("-mx-1 my-1 h-px bg-muted", className),
    ...props
  }
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive2.Separator.displayName;
var DropdownMenuShortcut = ({ className, ...props }) => /* @__PURE__ */ jsx(
  "span",
  {
    className: cn("ml-auto text-xs tracking-widest opacity-60", className),
    ...props
  }
);
DropdownMenuShortcut.displayName = "DropdownMenuShortcut";

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger, AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay, AlertDialogPortal, AlertDialogTitle, AlertDialogTrigger, AuthLayout, AuthTemplate, Badge, Breadcrumb, BreadcrumbEllipsis, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator, Button, Card, CardBody, CardContent, CardDescription, CardFooter, CardHeader, Card2 as CardOrganism, CardFooter2 as CardOrganismFooter, CardHeader2 as CardOrganismHeader, CardTitle, Checkbox, Combobox, ContextMenu, ContextMenuCheckboxItem, ContextMenuContent, ContextMenuGroup, ContextMenuItem, ContextMenuLabel, ContextMenuPortal, ContextMenuRadioGroup, ContextMenuRadioItem, ContextMenuSeparator, ContextMenuShortcut, ContextMenuSub, ContextMenuSubContent, ContextMenuSubTrigger, ContextMenuTrigger, DashboardHeader, DashboardLayout, Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger, DragDropContext, DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuPortal, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuShortcut, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger, Field, FilePreview, Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage, Form2 as FormOrganism, FrameCache, HighlightedText, Input, InputOTP, Label, LoginForm, MFAForm, MFASetup, MediaGrid, MobileSidebar, OtpTemplate, Pagination, PaginationButton, PaginationEllipsis, PaginationNavButton, Popover, PopoverContent, PopoverTrigger, Progress, QRCode, RadioGroup, RadioGroupItem, RecentSearches, ScrubPreview, Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectScrollDownButton, SelectScrollUpButton, SelectSeparator, SelectTrigger, SelectValue, Sidebar, SidebarNavGroup, SidebarNavItem, SidebarTrigger, SignUpForm, SignupTemplate, Skeleton, SkeletonCircle, SkeletonText, Slider, Spinner, Switch, Tabs, TabsContent, TabsList, TabsTrigger, ThemeContext, ThemeProvider, ThemeToggle, TimeRuler, Timeline, TimelineItem, TimelineTrack, Toast, ToastAction, ToastClose, Toast2 as ToastOrganism, ToastDescription as ToastOrganismDescription, ToastProvider, ToastTitle, ToastViewport, Toggle, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, UploadDropzone, UploadProgress, UserMenu, VideoPreview, WorkspaceSwitcher, acceptedFileTypeMimeMap, badgeVariants, buttonBaseStyles, buttonVariants, cn, disabledInputStyles, disabledStyles, disabledWithCursorStyles, errorAlwaysStyles, errorBorderStyles, errorContainerStyles, errorStyles, fileInputStyles, focusAlwaysRingCompactStyles, focusAlwaysRingStyles, focusRingCompactStyles, focusRingStyles, fontFamily, fontSize, fontWeight, formInputBaseStyles, formatFileSize, getFileIcon, getGlobalFrameCache, inputStateVariants, inputVariants, interactiveBaseStyles, interactiveMutedStyles, interactiveStyles, labelVariants, letterSpacing, lightThemeColors, lineHeight, navItemVariants, peerDisabledStyles, radius, radiusPixels, resetGlobalFrameCache, semanticColors, sidebarVariants, spacing, spacingPresets, spinnerVariants, successStyles, switchVariants, tabsListVariants, tabsTriggerVariants, themeToggleVariants, toastVariants, toggleBaseStyles, toggleVariants, useClickOutside, useCopyToClipboard, useDebounce, useFormField, useFrameExtraction, useKeyboardShortcut, useLocalStorage, useMediaQuery, useTheme, yakBrandColors };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map